import {
  Fragment,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type FormEvent,
  type DragEvent,
} from 'react';
import {
  DndContext,
  DragOverlay,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { horizontalListSortingStrategy, SortableContext } from '@dnd-kit/sortable';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Eye,
  Maximize2,
  Minimize2,
  Redo2,
  Undo2,
  X,
} from 'lucide-react';
import {
  ApiError,
  boardPath,
  parsePastedCardTitles,
  type AccessibilityPreferences,
  type AutomationButtonRunReceipt,
  type BoardContents,
  type BoardFaceLinkCardSurface,
  type BoardFaceList,
  type BoardTemplate,
  type TemplateCategoryPatch,
  type TemplateVisibilityPatch,
  type BoardViewKind,
  type CardMirror,
  type CustomFieldDefinition,
  type Reconciliation,
  type ListCardSortBody,
  type TimelineGroupBy,
  type TimelineLane,
} from '@molt/shared';
import type { BoardCardOpenIntent, BoardCardReturnState } from '@/lib/board-card-return';
import { boardBackgroundStyle, isChosenBoardBackground } from '@/lib/board-backgrounds';
import {
  useCreateBoardFromTemplate,
  useUpdateTemplateCategory,
  useUpdateTemplateVisibility,
} from '@/lib/board-templates';
import { cn } from '@/lib/cn';
import { revealFocusedElement, wheelToHorizontalDelta } from '@/lib/board-focus';
import { useReducedMotionPreference } from '@/lib/accessibility-preferences';
import {
  BOARD_AUTO_SCROLL,
  boardCollisionDetection,
  cardNeighboursForDrop,
  isCardDrag,
  resolveDropListId,
} from '@/lib/card-drag';
import {
  applyOptimisticCardMove,
  isOptimisticCardId,
  previewCardMoveToBoard,
  useArchiveCard,
  useAddCardMember,
  useCreateCard,
  useMoveCard,
  useMoveCardToBoard,
  useRemoveCardMember,
  useRenameCard,
  useSetCardCompleted,
  useUpdateCardDueDate,
  useUpdateCardStartDate,
  type MoveCardVariables,
} from '@/lib/cards';
import {
  useBulkArchiveCards,
  useBulkCopyCards,
  useBulkMoveCards,
} from '@/lib/card-bulk-actions';
import { useMergeCards } from '@/lib/card-share';
import { useConvertLinkCard, useDeleteCardMirror } from '@/lib/card-mirrors';
import {
  bulkDropNeighbours,
  orderSelectedCardIds,
  selectCardByGesture,
  selectedCardsInBoardOrder,
  type CardSelectionAnchor,
} from '@/lib/card-selection';
import {
  boardCalendarQueryOptions,
  boardCalendarResizePatch,
  boardCalendarReschedulePatch,
  buildBoardCalendarGrid,
  dueDateForCalendarDate,
  type BoardCalendarMode,
} from '@/lib/board-calendar';
import {
  boardTimelineMovePatch,
  boardTimelineQueryOptions,
  boardTimelineResizePatch,
  boardTimelineSchedulePatch,
  buildBoardTimelineScale,
  type BoardTimelineZoom,
} from '@/lib/board-timeline';
import { boardTableQueryOptions, tableMoveNeighbours } from '@/lib/board-table';
import { boardCustomFieldsQueryOptions } from '@/lib/custom-fields';
import {
  boardDashboardQueryOptions,
  useCreateDashboardTile,
  useDeleteDashboardTile,
  useUpdateDashboardTileChartType,
  useUpdateDashboardTileFacet,
} from '@/lib/board-dashboard';
import { boardMapQueryOptions } from '@/lib/board-map';
import { boardViewCardPath } from '@/lib/board-face';
import {
  useArchiveAllListCards,
  useCreateBulkCards,
  useMoveAllListCards,
  useSortListCards,
} from '@/lib/list-operations';
import {
  hasReconciliationConsequences,
  useMoveDestinations,
} from '@/lib/cross-board-move';
import { useCopyCard, useCopyList } from '@/lib/card-copy';
import { cardDisplayTitle } from '@/lib/card-title';
import { dragAnnouncements, dragInstructions, useMoltSensors } from '@/lib/dnd';
import {
  weekStartForLocale,
  type LocaleFormattingTranslate,
} from '@/lib/locale-formatting';
import { neighboursForMove } from '@/lib/list-drag';
import { useLocalePreferencesValue } from '@/lib/locale-preferences';
import {
  isOptimisticListId,
  previewListMoveToBoard,
  useMoveList,
  useMoveListToBoard,
} from '@/lib/lists';
import { Button } from '@/components/Button';
import { Dialog } from '@/components/Dialog';
import { FilterChip } from '@/components/FilterChip';
import { IconButton } from '@/components/IconButton';
import { Notice } from '@/components/Notice';
import { TextField } from '@/components/TextField';
import { TooltipProvider } from '@/components/Tooltip';
import { labelColourDisplayName } from '@/components/palette';
import { announce } from '@/lib/announce';
import { useBoardStar, useToggleBoardStar } from '@/lib/board-stars';
import { useApplyCardLabel, useRemoveCardLabel } from '@/lib/labels';
import { listUndoSourceKey, useUndoRedo } from '@/lib/undo';
import {
  useBoardAutomationButtons,
  useRunBoardAutomationButton,
} from '@/lib/automations';
import { AutomationButtonStrip } from '@/components/automation/AutomationButtonStrip';
import type { BoardLiveStatus } from '@/lib/live-board';
import {
  EMPTY_BOARD_FILTER,
  boardFilterCacheKey,
  boardFilterChips,
  boardFilterLabelFormatter,
  isBoardFilterActive,
  removeBoardFilterChip,
  toggleMyCardsFilter,
  type BoardFilterSelection,
} from '@/lib/board-filter';
import {
  boardCapabilities,
  ownBoardRole,
  useBoardMembers,
  useRenameBoard,
  useReopenBoard,
} from '@/lib/boards';
import { isSuperadminSession, useSession } from '@/lib/session';
import { queryKeys } from '@/lib/query-keys';
import {
  OFFLINE_DESCRIPTION_DRAFTS_CHANGED_EVENT,
  loadOfflineDescriptionDrafts,
  loadOfflineMoveDrafts,
  offlineDescriptionToOperation,
  offlineMoveToOperation,
  pendingOfflineDescriptionCardIds,
  pendingOfflineCardIds,
  saveOfflineDescriptionDrafts,
  saveOfflineMoveDrafts,
  syncOfflineOperations,
  type OfflineDescriptionDraft,
  type OfflineMoveDraft,
} from '@/lib/offline-sync';
import { watchStateQueryOptions } from '@/lib/watch';
import { workspacesQueryOptions } from '@/lib/workspaces';
import {
  calendarPowerUpHeaderEntry,
  isCalendarPowerUpHeaderEntry,
  powerUpSurfaceSummary,
  useBoardPowerUpSurfaces,
  type PowerUpSurfaceRuntimeEntry,
} from '@/lib/power-ups';
import {
  isEditableShortcutTarget,
  platformShortcutContext,
  shouldSuppressSingleKeyShortcuts,
  type KeyboardShortcutContext,
} from '@/lib/keyboard-shortcuts';
import { BoardClosedNotice, BoardEmpty } from './BoardStates';
import { PlatformAdminBoardNotice } from './PlatformAdminBoardNotice';
import { BoardFilterPanel } from './BoardFilterPanel';
import { BoardMenuPanel, type BoardMenuSection } from './BoardMenuPanel';
import { BoardTemplateBanner } from './BoardTemplateBanner';
import { BoardTitle } from './BoardTitle';
import {
  BoardCalendarView,
  type BoardCalendarResizeIntent,
  type BoardCalendarRescheduleIntent,
} from './BoardCalendarView';
import {
  BoardTimelineView,
  boardTimelineUnscheduledCardsFromContents,
  type BoardTimelineLaneIntent,
  type BoardTimelineMoveIntent,
  type BoardTimelineScheduleIntent,
  type BoardTimelineResizeIntent,
} from './BoardTimelineView';
import { BoardTableView, type BoardTableMoveIntent } from './BoardTableView';
import { BoardDashboardView } from './BoardDashboardView';
import {
  BoardMapView,
  type BoardMapCreateIntent,
  type BoardMapOpenCardIntent,
} from './BoardMapView';
import { BoardViewShell } from './BoardViewShell';
import { BoardViewSwitcher } from './BoardViewSwitcher';
import { MobileParityNotice } from './MobileParityNotice';
import { OfflineBoardNotice } from './OfflineBoardNotice';
import { BoardStarButton } from '../boards/BoardStarButton';
import {
  BoardShortcutPreferencesProvider,
  CardTile,
  type CardSelectionInput,
} from './CardTile';
import { CrossBoardMoveDialog } from './CrossBoardMoveDialog';
import { CardCopyDialog } from './CardCopyDialog';
import {
  CreateFromTemplateDialog,
  type CreateFromTemplateDialogSubmit,
} from './CreateFromTemplateDialog';
import {
  BulkCardActionsDialog,
  type BulkCardActionMode,
  type BulkCardActionSubmit,
} from './BulkCardActionsDialog';
import { VISIBILITY_LABEL_KEYS } from './BoardVisibilityControl';
import { TEMPLATE_CATEGORY_LABEL_KEYS } from './TemplateVisibilityControls';
import { ListCopyDialog } from './ListCopyDialog';
import { ListComposer } from './ListComposer';
import { ListOperationsDialog } from './ListOperationsDialog';
import { PasteCardsDialog } from './PasteCardsDialog';
import { SortableListColumn } from './SortableListColumn';
import {
  boardPinnedViewsQueryOptions,
  boardViewAccounting,
  boardViewLabel,
  displayPreferencesQueryOptions,
  dragPinnedView,
  isBoardViewAvailableOnMobile,
  loadLocalBoardViewChrome,
  movePinnedView as reorderPinnedViews,
  normalisePinnedViews,
  saveLocalBoardViewChrome,
  useSetBoardPinnedViews,
  useSetFocusMode,
} from '@/lib/view-state';
import { onboardingTipsQueryOptions, useMarkWelcomeTipSeen } from '@/lib/onboarding';
import { setShellChromeSuppressed } from '@/lib/shell-chrome';
import { useInterfaceLanguage } from '@/lib/interface-language';

/**
 * The board face — `FE-13.1`, `FE-13.3`, `FE-13.4` (`docs/design/c1/C1-BRIEF.md`)
 * and, from chunk `C2`, `FE-11.1`'s composers and `FE-11.2`'s drag
 * (`docs/design/c2/C2-BRIEF.md` §6.5).
 *
 * The composition, and the scroll architecture. This file owns exactly five
 * things: the fixed header, the horizontally scrolling strip, the focus and
 * wheel wiring that makes the strip usable from a keyboard and a trackpad, the
 * `DndContext` every list drag happens inside, and **which composer is open**.
 * It renders a list through `SortableListColumn` and a state through
 * `BoardStates`.
 *
 * ---------------------------------------------------------------------------
 * THE SCROLL ARCHITECTURE, WHICH IS THE HARD PART OF `FE-13.3`
 * ---------------------------------------------------------------------------
 * `FR-VIEW-010` AC1 wants three scroll behaviours at once, and they compose
 * only in one arrangement:
 *
 *   1. the page does not scroll — the shell's `full-bleed` layout
 *      (`lib/shell-layout.ts`) already guarantees this with `h-screen` +
 *      `overflow-hidden` on the root;
 *   2. the board header stays put while the strip moves — which needs no
 *      `position: sticky` at all, because the header is a *sibling* of the
 *      scroll container rather than its first child. Sticky would be the
 *      answer if the header scrolled with the content; it does not, so the
 *      cheaper and more robust spelling is simply "outside the scroller";
 *   3. each column scrolls its own cards vertically — which is `ListColumn`'s
 *      half and is only reachable if this file hands it a bounded height.
 *
 * ⚠ **`min-h-0` on every flex ancestor of a scroll container.** A flex child's
 * default `min-height: auto` refuses to shrink below its content, so without it
 * a 200-card column pushes the strip past the viewport edge and the failure
 * presents as a CSS bug rather than as the flexbox rule it is. It appears three
 * times below — on this component's root, on the strip, and on each `<li>`
 * (now `SortableListColumn`'s) — and none of the three is removable.
 * `lib/shell-layout.ts`'s comment carries the same warning for the two above
 * it.
 *
 * ⚠ **`items-start`, not the default `stretch`.** Stretch would inflate a
 * two-card panel to the full height of the strip, which is a tall empty
 * translucent rectangle beside a full one. `items-start` lets a short column be
 * short; `max-h-full` on the `<li>` is what caps a tall one so its cards scroll
 * instead of its panel growing.
 *
 * ---------------------------------------------------------------------------
 * WHAT `ListColumn` HAS TO DO FOR THE VERTICAL HALF TO WORK
 * ---------------------------------------------------------------------------
 * This file bounds the column and cannot scroll it: the `<li>` is `max-h-full
 * min-h-0`, so `ListColumn`'s own `<section>` must be `flex min-h-0 flex-col
 * max-h-full` and its card `<ol>` must be `min-h-0 overflow-y-auto`. If it is
 * not, the column overflows and the *horizontal* scroller grows a vertical
 * scrollbar — which breaks "the strip is the only thing on the page that
 * scrolls horizontally" from the other direction.
 *
 * ---------------------------------------------------------------------------
 * THE BOARD SURFACE, AND WHEN `data-board-text` APPEARS
 * ---------------------------------------------------------------------------
 * `FE-42.3` paints chosen local palette and curated stock-photo backgrounds.
 * The untouched row is still legally `{ type: 'colour', colour: null }`, with
 * the schema default `textTreatment: 'light'` sitting beside it; honouring that
 * would paint light text on Paper's light board. So the fallback keeps
 * `bg-board`, sets no `data-board-text`, and lets `--color-on-board` follow the
 * theme. Only a chosen background gets `data-board-text` and the title halo.
 *
 * ---------------------------------------------------------------------------
 * `C2` — THE DRAG (`FE-11.2`, `FR-BOARD-011`)
 * ---------------------------------------------------------------------------
 * ⚠ **The client never computes a rank** (C2 brief §3.1). `PATCH
 * /lists/:id/position` takes the two lists *flanking the gap the dragged list
 * landed in*, and that pair **is** the request body — there is no `position`
 * key on any request body in this product, and nothing in `apps/web` imports
 * `rankBetween`, `ranksBetween`, `firstRank` or `compareRanks`. The server
 * resolves both neighbours' ranks inside one transaction, which is the only way
 * two members dragging the same list converge.
 *
 * Three consequences worth naming, because each of them is a silent bug:
 *
 *   − **The neighbour pair is computed against the order at drop time**, and
 *     against the order *before* the move, because `neighboursForMove` removes
 *     the active id itself. Handing it a pre-reordered array is an off-by-one
 *     that reorders *almost* correctly, which reads as a race rather than as
 *     arithmetic.
 *   − **A no-op sends nothing.** Dropped where it started, or dropped with no
 *     `over` at all (outside the strip — `FR-BOARD-011` AC2), and the request
 *     is never made. Escape is `onDragCancel`, and for a LIST there is still
 *     nothing to do in it — dnd-kit returns the column for you if you do not
 *     fight it. ⚠ Corrected in `C3`: the handler now EXISTS, because a card
 *     drag leaves a preview behind that dnd-kit knows nothing about. The list
 *     path inside it is still empty.
 *   − **Placeholder ids never reach the wire**, as neighbours or as the subject
 *     — `excludeIds`, plus `SortableListColumn`'s own `disabled` gate.
 *
 * `accessibility={{ screenReaderInstructions, announcements }}` is a **named
 * deliverable of SC-7 and `FE-04.4`**, not "keyboard works". `describeDragItem`
 * below is what makes it worth having: dnd-kit announces the id it is given,
 * and an announcement of `01JBOARDLIST…` is compliant and useless.
 *
 * ⚠ **`DndContext` renders a live region of its own** — `role="status"
 * aria-live="assertive"`, from `@dnd-kit/accessibility` — which is a second
 * region in a product whose `lib/announce.ts` says there is exactly one and
 * that it is never assertive. The brief requires the `accessibility` prop, so
 * it is here; the conflict is reported to the parent rather than resolved in
 * this file.
 *
 * ---------------------------------------------------------------------------
 * `C2` — WHICH COMPOSER IS OPEN (`FE-11.1`, `FR-BOARD-009`)
 * ---------------------------------------------------------------------------
 * There are up to three kinds of composer on this surface — the one at the
 * right end, the one `BoardEmpty` offers on a board with no lists, and one per
 * gap between two adjacent lists — and **only one of them may ever be open**.
 * That is why `open`/`onOpenChange` are controlled and why the state below is a
 * single value rather than a boolean per composer: mutual exclusivity is then
 * structural instead of remembered.
 *
 * ⚠ The end composer and `BoardEmpty`'s are the *same* `kind`, and they are
 * mutually exclusive by construction: C1 gates the trailing `<li>` on
 * `lists.length > 0` precisely so that two controls named `Add a list` never
 * coexist, and every role query in five spec files depends on that staying
 * true.
 *
 * ---------------------------------------------------------------------------
 * `C3` — THE CARD DRAG (`FE-12.3`, `FR-BOARD-025`), IN THE *SAME* CONTEXT
 * ---------------------------------------------------------------------------
 * `C3-BRIEF.md` §6.7, as corrected by §12.9 and §12.12. **There is exactly one
 * `DndContext` on this surface and C3 adds no second one** (§7.6): a card
 * dragged out of list A and over list B has to see both, which is dnd-kit v6's
 * own multiple-containers pattern, and it is also what keeps
 * `@dnd-kit/accessibility`'s per-context live region at one rather than two.
 * Every handler below therefore **branches first on `isCardDrag(active)` and
 * returns**, leaving C2's list path underneath byte-for-byte unrefactored.
 *
 * Three props are handed to that one context and none of them is swapped on
 * drag-type state, which is the rule §6.2 rule 3 and §12.8 each state from a
 * different direction:
 *
 *   − `collisionDetection={boardCollisionDetection}` — ONE function, branching
 *     internally. Swapping the prop mid-drag re-runs dnd-kit's measurement
 *     against a different algorithm and picks silently wrong targets.
 *   − `autoScroll={BOARD_AUTO_SCROLL}` — one config for both drags, verified
 *     against dnd-kit 6.3.1's source in `card-drag.ts`'s own docstring rather
 *     than assumed. `STRIP_AUTO_SCROLL` is gone from this file: its
 *     `threshold.y` is `0`, and a card needs both axes.
 *   − `accessibility.announcements` — ONE resolver (`describeDragItem`), one
 *     pass over `contents.lists`, answering for a list *and* a card.
 *
 * ⚠ **`onDragOver` EXISTS AND IS NOT OPTIONAL** (§12.12). Without one, a card
 * dragged over *another* list opens no gap there — verified in
 * `@dnd-kit/sortable`'s source, not assumed: `SortableContext` computes
 * `disableTransforms = overIndex !== -1 && activeIndex === -1`, which is
 * exactly the destination list's situation, so its own strategy declines to
 * displace anything. The gap `FR-BOARD-025`'s behaviour sentence requires can
 * only come from this file moving the card into the destination array itself.
 *
 * ---------------------------------------------------------------------------
 * ⚠ THE TRAP THAT SITS RIGHT NEXT TO IT — §12.9 item 1
 * ---------------------------------------------------------------------------
 * **Move the preview, not the source of truth.** `cardNeighboursForDrop`'s
 * own docstring makes it a PRECONDITION that it is called against the board
 * *as it was before the drag*, with `activeId` still listed under its SOURCE
 * list. Hand it an array `onDragOver` has already spliced and it takes the
 * same-list branch instead of the cross-list one and lands the card one slot
 * off, with nothing red anywhere.
 *
 * The mechanism here is **two derivations from one source, and the source is
 * never touched**:
 *
 *   − `cardPreview` is a tiny value — which card, which destination, which
 *     index — and `previewLists` is `contents.lists` with that one splice
 *     applied. **`previewLists` is what renders.**
 *   − `contents.lists` is the prop, and it is never mutated, reordered or
 *     shadowed. **`contents.lists` is what `cardNeighboursForDrop` is given**,
 *     at `onDragEnd`, every time.
 *
 * So the precondition is satisfied *by construction* rather than by a snapshot
 * ref taken at `onDragStart` and remembered — there is nothing to remember,
 * because the pre-drag arrangement is still sitting in the prop.
 *
 * ⚠ **The second half of the same trap, reached from the other side.** Once
 * the preview has moved the card into list B, the card's own `<li>` is under
 * the pointer — so `pointerWithin` resolves to it and dnd-kit reports
 * `over.id === active.id`. Fed straight to `cardNeighboursForDrop` that is its
 * documented "dropped back on itself" no-op and **the cross-list move is
 * silently dropped** (`FR-BOARD-025` AC1, failing green). `handleCardDrop`
 * translates that case back into the pre-drag world instead: the card sits
 * immediately above whatever follows it in the preview, so *that* id is the
 * `overId`, and with nothing after it the destination list's own id is — which
 * `cardNeighboursForDrop` already reads as "the bottom".
 *
 * ⚠ **`onDragCancel` is now handled, and C2's argument for not handling it no
 * longer applies.** C2 could leave Escape entirely to dnd-kit because it held
 * no state of its own; this file now holds a preview, and without a cancel
 * handler the gap stays open in the destination list after Escape
 * (`FR-BOARD-025` AC2). dnd-kit still returns the card — clearing the preview
 * is all this adds.
 *
 * ⚠ **The preview is cleared by the DATA, not by the drop.** On a drop that
 * sends, `setCardPreview(null)` in the handler would commit a frame in which
 * the card is back in its source list — React flushes a discrete event's state
 * synchronously, while TanStack Query's `onMutate` (and so the optimistic
 * cache write) runs a microtask later. The effect below drops the preview when
 * `contents.lists` itself changes, which is exactly when the cache has taken
 * over the same fact. The no-send paths clear it immediately, because for them
 * no cache write is coming.
 *
 * ---------------------------------------------------------------------------
 * `C3` — SINGLE-OPEN, EXTENDED FROM LISTS TO CARDS (§6.7 item 7)
 * ---------------------------------------------------------------------------
 * `OpenComposer` grows a third member rather than a second state variable, for
 * the same reason it had two: **mutual exclusivity is structural instead of
 * remembered.** One composer is open anywhere on the board — a list composer
 * at the end, a list composer in a gap, or a card composer at the top or the
 * bottom of one column. `quickEditCardId` is separate because quick edit is
 * not a composer, and the two close each other explicitly.
 *
 * ✅ **The seam this file owed the parent, closed at integration** — see
 * `cardColumnPropsFor` below. The state and its handlers were complete and
 * correct here from the start; the four props could not reach `ListColumn`,
 * because `SortableListColumn` sits between the two files and declared none
 * of them. Both files' suites passed and **no card composer and no quick-edit
 * panel was reachable in the composed application** — the fifth seam of that
 * exact shape in this one chunk (`C3-BRIEF.md` §12.19). The parent added the
 * four pass-through props to `SortableListColumn` and wired them here.
 *
 * ---------------------------------------------------------------------------
 * `D3` — THE BOARD'S LIFECYCLE ARRIVES ON ITS FACE (`FE-08.1`, `FE-08.2`)
 * ---------------------------------------------------------------------------
 * Three things land here and nothing else does: the bare `<h1>` becomes
 * `BoardTitle` (renameable in place, `FR-BOARD-003`), the header gains the
 * **board menu trigger**, and `BoardClosedNotice` gains `FR-EXP-012` AC2's
 * Reopen. Everything destructive — close, permanent delete, the confirmations
 * — is `BoardMenuPanel`'s and is deliberately not visible from this file.
 *
 * ⚠ **THE PANEL'S LAYOUT CONTRACT, WHICH ONLY A SCREENSHOT CAN CHECK.**
 * `BoardMenuPanel` takes no `className` and supplies `h-full max-h-full w-80
 * shrink-0` with **no positioning of its own**. Mounted as a sibling inside
 * this component's *column* flex it lands **under** the strip instead of
 * beside the board, and every unit test still passes. So the root below is now
 * a **row** flex holding two children — the board's own column, and the panel
 * — which is `FR-BOARD-005` AC1's literal reading ("the panel opens beside the
 * board and the board face remains scrollable"): the board narrows, nothing
 * overlays it, and no z-index is involved.
 *
 * ⚠ **`min-w-0` on that inner column is load-bearing**, the same flexbox rule
 * as the `min-h-0` note above and in the other axis: a flex child's automatic
 * minimum size refuses to shrink below its content, so without it the board
 * column will not narrow and the panel is pushed off the right edge of the
 * viewport. Nothing turns red; the 1440×900 pass is what sees it.
 *
 * ⚠ **The trigger renders only for a signed-in viewer** (`D3-BRIEF.md` §2.5),
 * and the test is `profile !== null` — the *inverse* of this codebase's usual
 * "test `isSignedOut`, never `!profile`" rule, deliberately. That rule exists
 * so a component does not render the signed-**out** branch for one frame while
 * the session loads; here the risk runs the other way. The panel probes
 * `GET /boards/:id/members`, which answers **401 for an anonymous caller even
 * on a public board**, and `main.tsx` redirects on any non-session 401 — so a
 * trigger rendered optimistically during the session load would let an
 * anonymous visitor bounce themselves to `/sign-in` with a query they never
 * asked for. `profile !== null` is false while pending, so the trigger simply
 * appears when the session does.
 *
 * ⚠ **The trigger is `size="sm"` and that is not cosmetic.** `size-7` is 28px
 * against the `text-xl` heading's 28px line box, so the header stays the 52px
 * `density-budget.md` measures (the board title bar at 53–105). `size="md"` is
 * 36px and grows the header by 8px, spent out of the strip's height — and
 * three pixels of chrome in the wrong place is the difference between 24 cards
 * on screen and 20.
 *
 * ⚠ **Two permanent tab stops are added here, not one.** `D3-BRIEF.md` §2.1
 * budgets one (the trigger) against `FR-EXP-022`'s 80, but §7's own focus order
 * — "skip link → board title (when editable) → board menu trigger → lists" —
 * names the title as a stop too, and `InlineEdit`'s idle affordance is a real
 * `<button>` where the old `<h1>` was not focusable at all. On an editable
 * board the trail is therefore **75 of 80**, not 74. Reported rather than
 * papered over: the title cannot be made non-focusable without losing
 * `FR-BOARD-003`'s "selecting the board title turns it into an editable field".
 *
 * ⚠ **One channel per event** (`D3-BRIEF.md` §5.7), which decides two things
 * that look like omissions:
 *
 *   − **No `error` is handed to `BoardTitle`.** `InlineEdit`'s error line is a
 *     `role="alert"`, and `lib/boards.ts` already announces its own rollback
 *     for every failed mutation — rendering it here as well is the same
 *     sentence twice. `BoardMenuPanel` declined the identical prop on
 *     `BoardDangerConfirm` for the same reason. The consequence, reported
 *     chunk-wide: a failed rename gives a sighted mouse user no visible
 *     feedback, only the corrected title snapping back.
 *   − **No `announce()` for a close or a reopen.** `BoardClosedNotice` is
 *     `role="status"`; its appearing (or disappearing) is that event's one
 *     channel. A rename has no live region of its own, so `announce()` is its
 *     only channel, and it fires on **success**, not on submit — announcing an
 *     optimistic rename that then rolled back would put two contradictory
 *     sentences in the same region.
 */
/**
 * The stable empty definitions list. A fresh `[]` on every render would make
 * `filterChips`'s `useMemo` recompute on every render and re-render the whole
 * chip strip — a `??` fallback is an identity, not a value, wherever a
 * dependency array can see it.
 */
const NO_CUSTOM_FIELDS: readonly CustomFieldDefinition[] = [];

export interface BoardFaceProps {
  contents: BoardContents;
  canEdit: boolean;
  template?: BoardTemplate | null | undefined;
  templateLoading?: boolean | undefined;
  liveStatus?: BoardLiveStatus | undefined;
  offlineCachedAt?: string | undefined;
  /**
   * `FE-08.3` — called after `BoardMenuPanel` deletes this board, so the host
   * can leave an address that no longer resolves.
   *
   * ⚠ **The route is the required caller** (`b.$shortLink.{-$slug}.tsx`,
   * `D3-BRIEF.md` §6.9: navigate to `/` with `replace: true`, because the
   * history entry points at a board that is gone). Optional here only so that
   * this component stays renderable without a router — an unwired `onDeleted`
   * leaves the member standing on a 404 after a successful delete, which is
   * the seam shape §5.1 exists to make loud rather than silent.
   */
  onDeleted?: (() => void) | undefined;
  /** Called after the viewer leaves this board, so the route can navigate away. */
  onLeft?: (() => void) | undefined;
  /** Route-owned `/c/` navigation so the board can carry scroll return state. */
  onCardOpen?: ((intent: BoardCardOpenIntent) => void) | undefined;
  /** `FR-BOARD-023` AC2 return state from a card back to this board. */
  cardReturn?: BoardCardReturnState | undefined;
  filter?: BoardFilterSelection | undefined;
  onFilterChange?: ((filter: BoardFilterSelection) => void) | undefined;
  activeView?: BoardViewKind | undefined;
  viewPreferenceBusy?: boolean | undefined;
  onViewChange?: ((view: BoardViewKind) => void) | undefined;
  initialBoardMenuSection?: BoardMenuSection | undefined;
  collapsedListIds?: ReadonlySet<string> | undefined;
  onListCollapseChange?: ((listId: string, collapsed: boolean) => void) | undefined;
  accessibilityPreferences?: AccessibilityPreferences | null | undefined;
  onOpenShortcuts?:
    | ((options: {
        context: KeyboardShortcutContext;
        returnFocus: HTMLElement | null;
      }) => void)
    | undefined;
  onSingleKeyShortcutsDisabledChange?: ((disabled: boolean) => void) | undefined;
  onReducedMotionOverrideChange?: ((enabled: boolean) => void) | undefined;
  accessibilityPreferencesBusy?: boolean | undefined;
}

/**
 * Which composer is open, as one value.
 *
 * `'end'` covers both the trailing composer and `BoardEmpty`'s, which never
 * render at the same time. `'gap'` names the list the new one goes *after* —
 * the same id `onInsertListAfter` carries, so the double-click zone and
 * `ListHeader`'s `Add a list to the right` menu item set literally the same
 * state. One code path, one composer instance, `FR-EXP-022` satisfied.
 */
type OpenComposer =
  | { readonly kind: 'end' }
  | { readonly kind: 'gap'; readonly afterListId: string }
  /**
   * `C3` (`FE-12.1`). A card composer at the top or the bottom of one column.
   * A third member of the SAME union, deliberately: a card composer and a list
   * composer are then mutually exclusive for free, which is `C3-BRIEF.md`
   * §6.7's "only one composer is open anywhere on the board".
   */
  | {
      readonly kind: 'card';
      readonly listId: string;
      readonly placement: 'bottom' | 'top';
    }
  | null;

/**
 * The whole of the mid-drag preview: one card, one destination, one index into
 * that destination's PRE-DRAG `cards` array.
 *
 * Deliberately a description of a splice rather than a copy of the board.
 * `contents.lists` stays the single source of truth (see the header, §12.9),
 * and this is only ever enough to re-derive what the member should be looking
 * at while the pointer is down.
 */
interface CardPreview {
  readonly cardId: string;
  readonly toListId: string;
  readonly index: number;
}

interface BulkCardActionTarget {
  readonly mode: BulkCardActionMode;
  readonly cardId: string;
  readonly listId: string;
}

function useIsMobileBoardViewport(): boolean {
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const query = window.matchMedia('(max-width: 767px)');
    setMobile(query.matches);
    const handleChange = () => setMobile(query.matches);
    query.addEventListener('change', handleChange);
    return () => query.removeEventListener('change', handleChange);
  }, []);

  return mobile;
}

function browserIsOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

function createOfflineOperationId(): string {
  const random =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `offline-${random}`;
}

interface CardPlacement {
  readonly title: string;
  readonly listId: string;
  readonly listTitle: string;
  readonly index: number;
}

interface PasteCardsTarget {
  readonly text: string;
  readonly listId: string;
  readonly listTitle: string;
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
  readonly error: string | null;
}

/**
 * `contents.lists` with the previewed card moved — the ONLY place the two
 * arrangements diverge, and it returns `lists` itself (same reference) whenever
 * there is nothing to preview, so a same-list drag costs no re-render.
 *
 * ⚠ **`openCardCount` and `cardCount` move with the card**, mirroring
 * `applyOptimisticCardMove` (`lib/cards.ts`) exactly. `ListColumn`'s header
 * renders `openCardCount`, never `cards.length` — brief §5.2 — so a preview
 * that splices the array and leaves the counts alone puts a column header in
 * open disagreement with the cards under it for the whole duration of the
 * drag. That is the same defect §5.2 names for the cache updaters, reached
 * through the preview instead, and no assertion about the array can see it.
 */
function applyCardPreview(
  lists: readonly BoardFaceList[],
  preview: CardPreview | null,
): readonly BoardFaceList[] {
  if (preview === null) return lists;

  const source = lists.find((entry) =>
    entry.cards.some((card) => card.id === preview.cardId),
  );
  // A preview into the card's own list is not a preview: within one list
  // `SortableContext`'s strategy already opens the gap by transform, and
  // splicing the array underneath it would fight the animation it is running.
  if (source === undefined || source.id === preview.toListId) return lists;

  const moving = source.cards.find((card) => card.id === preview.cardId);
  if (moving === undefined) return lists;

  return lists.map((entry) => {
    if (entry.id === source.id) {
      return {
        ...entry,
        cards: entry.cards.filter((card) => card.id !== preview.cardId),
        cardCount: entry.cardCount - 1,
        openCardCount: entry.openCardCount - 1,
      };
    }
    if (entry.id === preview.toListId) {
      const cards = [...entry.cards];
      cards.splice(Math.min(Math.max(preview.index, 0), cards.length), 0, moving);
      return {
        ...entry,
        cards,
        cardCount: entry.cardCount + 1,
        openCardCount: entry.openCardCount + 1,
      };
    }
    return entry;
  });
}

function cardPlacements(
  t: LocaleFormattingTranslate,
  lists: readonly BoardFaceList[],
): ReadonlyMap<string, CardPlacement> {
  const placements = new Map<string, CardPlacement>();
  for (const list of lists) {
    list.cards.forEach((card, index) => {
      placements.set(card.id, {
        title: cardDisplayTitle(t, card.title),
        listId: list.id,
        listTitle: list.title,
        index,
      });
    });
  }
  return placements;
}

/*
 * ⚠ FE-80.1 — THE CONSTANT THIS BLOCK USED TO INTRODUCE IS GONE, and the
 * argument it records is not. A module constant cannot call `t`, so the refusal
 * sentence on a **Reopen** the caller may not use is now
 * `boardFace.reopenRefused`, resolved at its one use site in the component
 * below. The reasoning below is why it is this file's sentence at all, and it
 * survives the move unchanged.
 *
 * ⚠ Local rather than `boardCapabilities`'s `closeDisabledReason`, and that is
 * agent A's own instruction rather than a divergence — `lib/boards.ts`:
 * *"`canClose` governs the toggle in general (close **or** reopen — both are
 * `requireBoardAdmin` server-side); which direction a caller offers is the
 * composing component's business, not this function's."* The permission is one
 * fact and the two sentences describing it are not: a disabled control named
 * *Reopen board* whose reason reads *"Only a board admin can **close** this
 * board"* is wrong text on screen, and `FR-EXP-012`'s edge case asks for a
 * reason, not for a string. The *checking* sentence is still agent A's, taken
 * from `closeDisabledReason` below rather than spelled a second time here.
 */

/** The board menu trigger's glyph. Decorative — `IconButton`'s `label` names it. */
function BoardMenuGlyph() {
  return (
    <svg viewBox="0 0 16 16" className="size-4" aria-hidden="true" focusable="false">
      <circle cx="3" cy="8" r="1.5" fill="currentColor" />
      <circle cx="8" cy="8" r="1.5" fill="currentColor" />
      <circle cx="13" cy="8" r="1.5" fill="currentColor" />
    </svg>
  );
}

interface CalendarCreateDialogProps {
  readonly open: boolean;
  readonly dateKey: string | null;
  readonly lists: readonly BoardFaceList[];
  readonly busy: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onSubmit: (input: {
    readonly listId: string;
    readonly title: string;
    readonly dateKey: string;
  }) => Promise<void>;
}

function CalendarCreateDialog({
  open,
  dateKey,
  lists,
  busy,
  onOpenChange,
  onSubmit,
}: CalendarCreateDialogProps) {
  const { t } = useInterfaceLanguage();
  const [title, setTitle] = useState('');
  const [listId, setListId] = useState(lists[0]?.id ?? '');
  /* ⚠ A KEY, not a sentence, on `ProfilePanel`'s precedent: this state outlives
   * the render that set it, so holding the English of the moment would leave a
   * stale sentence on screen the first time the catalogue query resolves under
   * it. `null` means "no error"; every other value is a catalogue key.
   *
   * ⚠ TWO pieces of state where there was one, and the split is the point: a
   * server message is DATA and must survive verbatim, while the two validation
   * sentences are chrome and must be re-resolved on every render. Folding a
   * server string into `errorKey` would have `t` look it up as a key and echo
   * it back unchanged — which renders correctly and hides the category error. */
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle('');
    setListId(lists[0]?.id ?? '');
    setErrorKey(null);
    setSubmitError(null);
  }, [lists, open]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (dateKey === null) return;
    const trimmed = title.trim();
    if (trimmed === '') {
      setSubmitError(null);
      setErrorKey('boardFace.calendarCreate.titleRequired');
      return;
    }
    if (listId === '') {
      setSubmitError(null);
      setErrorKey('boardFace.calendarCreate.listRequired');
      return;
    }

    try {
      await onSubmit({ listId, title: trimmed, dateKey });
    } catch (caught) {
      setErrorKey(null);
      setSubmitError(
        caught instanceof Error ? caught.message : t('boardFace.calendarCreate.failed'),
      );
    }
  }

  const formError = submitError ?? (errorKey === null ? null : t(errorKey));

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      title={t('boardFace.calendarCreate.title')}
      description={
        dateKey === null
          ? undefined
          : t('boardFace.calendarCreate.description', {
              date: calendarDateLabel(dateKey),
            })
      }
      footer={
        <>
          <Button type="button" variant="secondary" onClick={() => onOpenChange(false)}>
            {t('boardFace.calendarCreate.cancel')}
          </Button>
          <Button type="submit" form="calendar-card-create-form" busy={busy}>
            {t('boardFace.calendarCreate.submit')}
          </Button>
        </>
      }
    >
      <form
        id="calendar-card-create-form"
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <TextField
          label={t('boardFace.calendarCreate.titleLabel')}
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
          autoComplete="off"
          {...(formError === null ? {} : { error: formError })}
        />
        <label className="flex flex-col gap-1 text-sm font-medium text-ink">
          {t('boardFace.calendarCreate.listLabel')}
          <select
            value={listId}
            onChange={(event) => setListId(event.currentTarget.value)}
            className="h-9 rounded-card border border-border-strong bg-surface-raised px-3 text-card text-ink focus:border-accent"
          >
            {lists.map((list) => (
              <option key={list.id} value={list.id}>
                {list.title}
              </option>
            ))}
          </select>
        </label>
      </form>
    </Dialog>
  );
}

function calendarDateLabel(dateKey: string): string {
  return new Intl.DateTimeFormat(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(`${dateKey}T00:00:00`));
}

function timelineLaneListId(lane: TimelineLane): string | null {
  return lane.kind === 'list' ? lane.list.id : null;
}

const REMOTE_MOVE_ANNOUNCEMENT_WINDOW_MS = 750;

/**
 * ⚠ `t` FIRST and REQUIRED, matching `cardDisplayTitle` and
 * `customFieldTypeLabel`. A module function cannot reach the context, and a
 * defaulted translator is the shape that lets one caller fall through to a
 * default that looks correct.
 */
function pastedCardsErrorMessage(t: LocaleFormattingTranslate, error: unknown): string {
  if (error instanceof ApiError && error.details?.capacityLimit?.[0] !== undefined) {
    return t('boardFace.paste.capacityLimit', {
      limit: error.details.capacityLimit[0],
    });
  }
  return error instanceof Error ? error.message : t('boardFace.paste.failed');
}

export function BoardFace({
  contents,
  canEdit,
  template = null,
  templateLoading,
  liveStatus,
  offlineCachedAt,
  onDeleted,
  onLeft,
  onCardOpen,
  cardReturn,
  filter = EMPTY_BOARD_FILTER,
  onFilterChange,
  activeView = 'board',
  viewPreferenceBusy,
  onViewChange,
  initialBoardMenuSection,
  collapsedListIds,
  onListCollapseChange,
  accessibilityPreferences,
  onOpenShortcuts,
}: BoardFaceProps) {
  const { t } = useInterfaceLanguage();
  const session = useSession();
  const { profile } = session;
  /** SA-9 — see `BoardMenuPanel`'s spelling of the same fact. */
  const isSuper = isSuperadminSession(session);
  const { language } = useLocalePreferencesValue();
  /**
   * `FR-NOTIF-032` -- one derived week start for the fetched range AND both
   * drawn grids, so an `en-US` member never fetches a Sunday-first week and
   * draws a Monday-first one.
   */
  const calendarWeekStartsOn = weekStartForLocale(language);
  const offlineQueueUserId = profile?.id;
  const offlineQueueStorageKey =
    offlineQueueUserId === undefined
      ? null
      : `${offlineQueueUserId}:${contents.board.shortLink}`;
  const [offlineMoves, setOfflineMoves] = useState<readonly OfflineMoveDraft[]>(() =>
    loadOfflineMoveDrafts(offlineQueueUserId, contents.board.shortLink),
  );
  const [offlineMovesLoadedKey, setOfflineMovesLoadedKey] = useState<string | null>(
    offlineQueueStorageKey,
  );
  const [offlineDescriptions, setOfflineDescriptions] = useState<
    readonly OfflineDescriptionDraft[]
  >(() => loadOfflineDescriptionDrafts(offlineQueueUserId, contents.board.shortLink));
  const [rejectedOfflineDescriptions, setRejectedOfflineDescriptions] = useState<
    readonly OfflineDescriptionDraft[]
  >([]);
  const [offlineDescriptionsLoadedKey, setOfflineDescriptionsLoadedKey] = useState<
    string | null
  >(offlineQueueStorageKey);
  const offlineMovesReady =
    offlineQueueStorageKey !== null && offlineMovesLoadedKey === offlineQueueStorageKey;
  const offlineDescriptionsReady =
    offlineQueueStorageKey !== null &&
    offlineDescriptionsLoadedKey === offlineQueueStorageKey;
  const activeOfflineMoves = offlineMovesReady ? offlineMoves : [];
  const activeOfflineDescriptions = offlineDescriptionsReady ? offlineDescriptions : [];
  const offlineContents = useMemo(
    () =>
      activeOfflineMoves.reduce<BoardContents>(
        (current, move) => applyOptimisticCardMove(current, move) ?? current,
        contents,
      ),
    [activeOfflineMoves, contents],
  );
  const { board, lists } = offlineContents;
  /** The path for a card this face builds itself — `boardViewCardPath`'s docstring. */
  const viewCardPath = (card: {
    readonly shortLink: string;
    readonly cardNumber: number;
    readonly title: string;
  }): string => boardViewCardPath(board, card);
  const queryClient = useQueryClient();
  const stripRef = useRef<HTMLOListElement>(null);
  const syncingOfflineMovesRef = useRef(false);
  const hasLists = lists.length > 0;
  const templateBoard = template?.isTemplate === true ? template : null;
  const isTemplate = templateBoard !== null;
  const workingCanEdit = canEdit && !isTemplate;

  /** `FE-08.2`. The trigger is the panel's `returnFocusRef` — `FR-EXP-022` AC3. */
  const [menuOpen, setMenuOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState<
    | { mode: 'card'; card: BoardFaceList['cards'][number]; listId: string }
    | { mode: 'list'; list: BoardFaceList }
    | null
  >(null);
  const [moveResult, setMoveResult] = useState<{
    reconciliation: Reconciliation;
    cardsMoved?: number;
  } | null>(null);
  const [copyTarget, setCopyTarget] = useState<{
    card: BoardFaceList['cards'][number];
    listId: string;
  } | null>(null);
  const [copyResult, setCopyResult] = useState<Reconciliation | null>(null);
  const [copyListTarget, setCopyListTarget] = useState<BoardFaceList | null>(null);
  const [copyListResult, setCopyListResult] = useState<{
    reconciliation: Reconciliation;
    cardsCopied: number;
  } | null>(null);
  const [bulkCardActionTarget, setBulkCardActionTarget] =
    useState<BulkCardActionTarget | null>(null);
  const [listOperationsTarget, setListOperationsTarget] = useState<BoardFaceList | null>(
    null,
  );
  const [pasteCardsTarget, setPasteCardsTarget] = useState<PasteCardsTarget | null>(null);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [templateCreateResult, setTemplateCreateResult] = useState<{
    cardsCopied: number;
    path: string;
  } | null>(null);
  const [calendarMode, setCalendarMode] = useState<BoardCalendarMode>('month');
  const [calendarAnchor] = useState(() => new Date());
  const [calendarCreateDate, setCalendarCreateDate] = useState<string | null>(null);
  const [calendarPowerUpOpen, setCalendarPowerUpOpen] = useState(false);
  const [timelineZoom, setTimelineZoom] = useState<BoardTimelineZoom>('month');
  const [timelineAnchor, setTimelineAnchor] = useState(() => new Date());
  const [timelineGroupBy, setTimelineGroupBy] = useState<TimelineGroupBy>('none');
  const [boardAutomationRunReceipt, setBoardAutomationRunReceipt] =
    useState<AutomationButtonRunReceipt | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);

  /*
   * ⚠ `profile !== null`, not `!isSignedOut` — see the header. False while the
   * session is pending, which is the direction that cannot bounce an anonymous
   * visitor to `/sign-in` through a members probe they never asked for.
   */
  const moveDestinations = useMoveDestinations(
    moveTarget !== null ||
      copyTarget !== null ||
      copyListTarget !== null ||
      bulkCardActionTarget !== null,
    profile?.id,
  );
  const signedIn = profile !== null;
  const mobileBoardViewport = useIsMobileBoardViewport();
  const showingMobileParityNotice =
    mobileBoardViewport && !isBoardViewAvailableOnMobile(activeView);
  const effectiveActiveView = activeView;
  const offlinePendingCardIds = useMemo(() => {
    const pending = new Set(pendingOfflineCardIds(activeOfflineMoves));
    for (const cardId of pendingOfflineDescriptionCardIds(activeOfflineDescriptions)) {
      pending.add(cardId);
    }
    return pending;
  }, [activeOfflineDescriptions, activeOfflineMoves]);
  const syncOfflineDrafts = useCallback(async () => {
    if (
      syncingOfflineMovesRef.current ||
      !offlineMovesReady ||
      !offlineDescriptionsReady ||
      activeOfflineMoves.length + activeOfflineDescriptions.length === 0
    ) {
      return;
    }

    syncingOfflineMovesRef.current = true;
    const queuedMoves = activeOfflineMoves;
    const queuedDescriptions = activeOfflineDescriptions;
    try {
      const response = await syncOfflineOperations([
        ...queuedMoves.map(offlineMoveToOperation),
        ...queuedDescriptions.map(offlineDescriptionToOperation),
      ]);
      const settledOperationIds = new Set(
        response.results
          .filter((result) => result.status === 'applied' || result.idempotent === true)
          .map((result) => result.clientOperationId),
      );
      const rejectedDeletedDescriptions = queuedDescriptions.filter((draft) =>
        response.results.some(
          (result) =>
            result.clientOperationId === draft.clientOperationId &&
            result.operationKind === 'card.edit_description' &&
            result.status === 'rejected' &&
            result.error?.code === 'not_found',
        ),
      );
      if (settledOperationIds.size === 0 && rejectedDeletedDescriptions.length === 0) {
        return;
      }
      if (rejectedDeletedDescriptions.length > 0) {
        setRejectedOfflineDescriptions((current) => {
          const byOperation = new Map(
            current.map((draft) => [draft.clientOperationId, draft]),
          );
          for (const draft of rejectedDeletedDescriptions) {
            byOperation.set(draft.clientOperationId, draft);
          }
          return [...byOperation.values()];
        });
      }
      const resolvedDescriptionOperationIds = new Set([
        ...settledOperationIds,
        ...rejectedDeletedDescriptions.map((draft) => draft.clientOperationId),
      ]);
      setOfflineMoves((current) =>
        current.filter((move) => !settledOperationIds.has(move.clientOperationId)),
      );
      setOfflineDescriptions((current) =>
        current.filter(
          (draft) => !resolvedDescriptionOperationIds.has(draft.clientOperationId),
        ),
      );
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(board.id),
      });
      announce(
        t(
          rejectedDeletedDescriptions.length > 0
            ? 'boardFace.offline.syncedWithRejections'
            : 'boardFace.offline.synced',
        ),
      );
    } catch (error) {
      /* ⚠ The arms take DIFFERENT slots, so they cannot be one key:
       * `interfaceMessage` THROWS on a slot it is given no value for, which
       * makes a name/slot disagreement a render-time crash rather than a wrong
       * string. Both arms are reached by fixtures. */
      announce(
        error instanceof Error
          ? t('boardFace.offline.syncFailedWithReason', { reason: error.message })
          : t('boardFace.offline.syncFailed'),
      );
    } finally {
      syncingOfflineMovesRef.current = false;
    }
  }, [
    activeOfflineDescriptions,
    activeOfflineMoves,
    board.id,
    offlineDescriptionsReady,
    offlineMovesReady,
    queryClient,
  ]);
  const [pinnedViews, setPinnedViews] = useState<readonly BoardViewKind[]>([]);
  const [draggingPinnedView, setDraggingPinnedView] = useState<BoardViewKind | null>(
    null,
  );
  const draggingPinnedViewRef = useRef<BoardViewKind | null>(null);
  /*
   * `FR-VIEW-004` AC2, the durable half — `FE-55.1`.
   *
   * ⚠ Gated on `signedIn` inside `boardPinnedViewsQueryOptions`, exactly as
   * `displayPreferencesQueryOptions` below it is. An anonymous board reader
   * has no member state to ask for, and an ungated query 401s on every
   * anonymous board open.
   */
  const durablePinnedViews = useQuery(boardPinnedViewsQueryOptions(board.id, signedIn));
  const setPinnedViewsPreference = useSetBoardPinnedViews(board.id);
  const adoptedPinnedViewsForRef = useRef<string | null>(null);
  const [focusModeEnabled, setFocusModeEnabled] = useState(false);
  const displayPreferences = useQuery(displayPreferencesQueryOptions(signedIn));
  const setFocusModePreference = useSetFocusMode();
  const adoptedFocusModeForRef = useRef<string | null>(null);
  /**
   * `FR-EXP-033` AC2 — the one-time first-run tip.
   *
   * `welcomeBoardId` is the whole reason `boardSchema` did not have to be
   * widened: the board face compares its own id against the caller's welcome
   * board rather than reading a flag off the board it was handed.
   */
  const onboardingTips = useQuery(onboardingTipsQueryOptions(signedIn));
  const markWelcomeTipSeen = useMarkWelcomeTipSeen();
  const [welcomeTipConfirmed, setWelcomeTipConfirmed] = useState(false);
  const shownPinnedViews = pinnedViews.filter(
    (view) => !mobileBoardViewport || isBoardViewAvailableOnMobile(view),
  );
  const showSecondaryBoardChrome = !focusModeEnabled;

  const rename = useRenameBoard(board.id);
  const reopen = useReopenBoard(board.id);
  const createCard = useCreateCard(board.id);
  const deleteCardMirror = useDeleteCardMirror(board.id, t);
  const convertLinkCard = useConvertLinkCard(board.id, t);
  const updateCardStartDate = useUpdateCardStartDate();
  const updateCardDueDate = useUpdateCardDueDate();
  const archiveCard = useArchiveCard(board.id);
  const starState = useBoardStar(board.id, signedIn);
  const toggleBoardStar = useToggleBoardStar();
  const boardWatch = useQuery(
    watchStateQueryOptions({ targetType: 'board', targetId: board.id }, signedIn),
  );
  const templateWorkspaces = useQuery({
    ...workspacesQueryOptions(),
    enabled: signedIn && templateDialogOpen,
  });
  const createFromTemplate = useCreateBoardFromTemplate();
  const updateTemplateVisibility = useUpdateTemplateVisibility(board.id);
  const updateTemplateCategory = useUpdateTemplateCategory(board.id);
  const undoRedo = useUndoRedo(board.id, t);
  const boardAutomationButtons = useBoardAutomationButtons(
    board.id,
    signedIn && workingCanEdit && !board.closed,
  );
  const runBoardAutomationButton = useRunBoardAutomationButton();
  const boardPowerUpSurfaces = useBoardPowerUpSurfaces(board.id, signedIn);
  const powerUpSurfaces = useMemo(
    () => powerUpSurfaceSummary(boardPowerUpSurfaces.data),
    [boardPowerUpSurfaces.data],
  );
  const calendarPowerUpEntry = useMemo(
    () => calendarPowerUpHeaderEntry(powerUpSurfaces),
    [powerUpSurfaces],
  );
  const genericBoardHeaderPowerUpButtons = useMemo(
    () =>
      powerUpSurfaces.boardHeaderButtons.filter(
        (entry) => !isCalendarPowerUpHeaderEntry(entry),
      ),
    [powerUpSurfaces.boardHeaderButtons],
  );

  useEffect(() => {
    if (initialBoardMenuSection !== undefined) setMenuOpen(true);
  }, [initialBoardMenuSection]);

  useEffect(() => {
    if (calendarPowerUpEntry === null) setCalendarPowerUpOpen(false);
  }, [calendarPowerUpEntry]);

  useEffect(() => {
    setOfflineMoves(loadOfflineMoveDrafts(offlineQueueUserId, contents.board.shortLink));
    setOfflineMovesLoadedKey(offlineQueueStorageKey);
  }, [contents.board.shortLink, offlineQueueStorageKey, offlineQueueUserId]);

  useEffect(() => {
    setRejectedOfflineDescriptions([]);
  }, [offlineQueueStorageKey]);

  useEffect(() => {
    function reloadDescriptions() {
      setOfflineDescriptions(
        loadOfflineDescriptionDrafts(offlineQueueUserId, contents.board.shortLink),
      );
      setOfflineDescriptionsLoadedKey(offlineQueueStorageKey);
    }

    reloadDescriptions();
    window.addEventListener(OFFLINE_DESCRIPTION_DRAFTS_CHANGED_EVENT, reloadDescriptions);
    return () =>
      window.removeEventListener(
        OFFLINE_DESCRIPTION_DRAFTS_CHANGED_EVENT,
        reloadDescriptions,
      );
  }, [contents.board.shortLink, offlineQueueStorageKey, offlineQueueUserId]);

  useEffect(() => {
    if (
      offlineQueueStorageKey === null ||
      offlineMovesLoadedKey !== offlineQueueStorageKey
    ) {
      return;
    }
    saveOfflineMoveDrafts(offlineQueueUserId, board.shortLink, offlineMoves);
  }, [
    board.shortLink,
    offlineMoves,
    offlineMovesLoadedKey,
    offlineQueueStorageKey,
    offlineQueueUserId,
  ]);

  useEffect(() => {
    if (
      offlineQueueStorageKey === null ||
      offlineDescriptionsLoadedKey !== offlineQueueStorageKey
    ) {
      return;
    }
    saveOfflineDescriptionDrafts(
      offlineQueueUserId,
      board.shortLink,
      offlineDescriptions,
      { notify: false },
    );
  }, [
    board.shortLink,
    offlineDescriptions,
    offlineDescriptionsLoadedKey,
    offlineQueueStorageKey,
    offlineQueueUserId,
  ]);

  useEffect(() => {
    if (
      !offlineMovesReady ||
      !offlineDescriptionsReady ||
      activeOfflineMoves.length + activeOfflineDescriptions.length === 0
    ) {
      return undefined;
    }
    if (!browserIsOffline()) {
      void syncOfflineDrafts();
    }

    function handleOnline() {
      void syncOfflineDrafts();
    }

    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, [
    activeOfflineDescriptions.length,
    activeOfflineMoves.length,
    offlineDescriptionsReady,
    offlineMovesReady,
    syncOfflineDrafts,
  ]);

  /*
   * ---------------------------------------------------------------------------
   * ⚠ THE SEAM `BoardMenuPanel` COULD NOT CLOSE, AND WHY THIS FILE OBSERVES
   *    THE MEMBER LIST AT ALL
   * ---------------------------------------------------------------------------
   * `BoardClosedNoticeProps.reopenDisabledReason` needs the caller's board
   * role, and the panel's own members query is gated on the **panel being
   * open** — a board face that never opens the menu has no role to read, so
   * the Reopen control would have had nothing to gate on.
   *
   * The answer is a second `enabled` on the same query key. React Query dedupes
   * it against the panel's, and — since `D4` — against the board route's third
   * observer of the same key, so this is one request rather than three.
   *
   * ⚠ **`D3` wrote that `enabled` as `board.closed && signedIn`, and the line
   * below no longer says that.** The next paragraph widened it, and the
   * property `D3-BRIEF.md` §6.7 was protecting — an open board fetches no
   * member list until its menu is opened — is knowingly given up rather than
   * overlooked. Do not restore the `closed &&` conjunct.
   *
   * ⚠ `rolesKnown` is the members query **and** the session, not the members
   * query alone. `ownBoardRole(members, undefined)` returns `null` — the same
   * value it returns for "you hold no row on this board" — so a members-only
   * gate tells a board admin *"Only a board admin can…"* for the width of the
   * session load: a wrong sentence rather than a slow one. Same reasoning, and
   * the same spelling, as `BoardMenuPanel`'s.
   *
   * ⚠ **In this file the session half is already implied** — the Reopen control
   * renders only when `signedIn`, so `profile !== null` holds wherever the
   * reason is read, and the *reachable* checking state is a members query still
   * in flight. It is kept anyway, and is not redundant code to delete: the
   * derivation has to be correct independently of the caller's render gate, and
   * it is `BoardMenuPanel` — whose trigger is not gated on the members query —
   * where the same conjunct actually bites.
   */
  /*
   * ⚠ **`D4` widened this gate from `board.closed && signedIn` to `signedIn`,
   * and the widening is load-bearing.** `FE-09.2` gates every affordance on the
   * board face by the viewer's role, and an **open** board is the only board
   * that renders those affordances — left as `closed &&`, an open board would
   * never fetch the member list, so `rolesKnown` would never become true on the
   * one surface the item exists to gate. The predicate would be correct and
   * starved. `D4-BRIEF.md` §5.5.
   *
   * The cost is one request per open board for a signed-in viewer, deduped
   * against the route's and the panel's observers of the same key.
   */
  const members = useBoardMembers(board.id, signedIn);
  /**
   * `FR-SEARCH-031` — the definitions the filter panel draws one facet per.
   *
   * ⚠ **Not gated on `signedIn`, deliberately.** `GET /boards/:id/custom-fields`
   * resolves its caller with `currentUser(request)?.id ?? null` and answers
   * **404, never 401**, outside the read scope — verified in
   * `apps/server/src/routes/custom-fields.ts` — so this query cannot trip
   * `main.tsx`'s global 401 → `/sign-in` redirect and eject an anonymous
   * visitor off a public board, which is `FE-61.2`'s bug and the reason a
   * `signedIn &&` conjunct is the reflex here. `FR-SEARCH-001` grants that
   * visitor the filter, so gating would also strip a shared filtered link's
   * custom-field facets for exactly the audience it was shared with.
   *
   * ⚠ Whether the route answers 200 (rather than 404) to an anonymous caller
   * on a PUBLIC board is not asserted anywhere and is not claimed here. If it
   * 404s, this query simply never settles, `pruneBoardFilterFacets` imposes
   * no pruning, the server arm still filters correctly, and the panel offers
   * no custom-field facets to that visitor — degraded, never wrong.
   *
   * The key is the route's own, so this observer and the route's prune effect
   * share one fetch.
   */
  const boardCustomFields = useQuery(boardCustomFieldsQueryOptions(board.id));
  const customFieldDefinitions = boardCustomFields.data ?? NO_CUSTOM_FIELDS;
  const rolesKnown = members.isSuccess && profile !== null;
  const ownRole = ownBoardRole(members.data, profile?.id);
  const capabilities = boardCapabilities({
    role: ownRole,
    closed: board.closed,
    rolesKnown,
    isSuper,
    t,
  });
  // SA-9: both routes sit on `requireBoardAdminRole`, whose `isAdmin` folds in
  // the superadmin flag.
  const canConfigureDashboard =
    rolesKnown && (ownRole === 'admin' || isSuper) && !board.closed;
  /*
   * SA-9's banner. Keyed by board id rather than a bare boolean so that a face
   * reused across a board-to-board navigation does not carry one board's
   * dismissal onto the next; and `useState`, never `localStorage`, because the
   * requirement is "dismissible-per-visit".
   *
   * ⚠ `rolesKnown` is not optional here: `ownBoardRole` answers `null` for "not
   * loaded yet" as well as for "no row", so without it the banner would flash
   * on a superadmin's own boards for the width of the members request.
   */
  const [platformAdminNoticeDismissedFor, setPlatformAdminNoticeDismissedFor] = useState<
    string | null
  >(null);
  const showPlatformAdminNotice =
    isSuper &&
    rolesKnown &&
    ownRole === null &&
    platformAdminNoticeDismissedFor !== board.id;
  const canManageTemplate =
    rolesKnown && (ownRole === 'admin' || isSuper) && !board.closed;
  const templateManagementDisabledReason = canManageTemplate
    ? null
    : !rolesKnown
      ? t('boardFace.template.checkingAccess')
      : board.closed
        ? t('boardFace.template.closedReason')
        : t('boardFace.template.adminReason');

  const reopenDisabledReason = capabilities.canClose
    ? undefined
    : rolesKnown
      ? t('boardFace.reopenRefused')
      : // Agent A's "Checking what you can do on this board…", not a second
        // spelling of it. Non-null whenever `canClose` is false; the `??` is
        // the type widening and not a branch.
        (capabilities.closeDisabledReason ?? undefined);

  const [openComposer, setOpenComposer] = useState<OpenComposer>(null);
  /** Which one card, anywhere on the board, is in quick edit — `FE-12.2`. */
  const [quickEditCardId, setQuickEditCardId] = useState<string | null>(null);
  const [selectedCardIds, setSelectedCardIds] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [selectionAnchor, setSelectionAnchor] = useState<CardSelectionAnchor | null>(
    null,
  );
  const [selectionNotice, setSelectionNotice] = useState<string | null>(null);
  const [showLabelNames, setShowLabelNames] = useState(false);
  const [highlightedCardId, setHighlightedCardId] = useState<string | null>(
    cardReturn?.cardId ?? null,
  );
  /** The card under the pointer right now, for the `DragOverlay` alone. */
  const [activeCardId, setActiveCardId] = useState<string | null>(null);
  const [cardPreview, setCardPreview] = useState<CardPreview | null>(null);
  const previousCardPlacementsRef = useRef<ReadonlyMap<string, CardPlacement> | null>(
    null,
  );
  const previousFilterAnnouncementRef = useRef<{
    filterKey: string;
    visibleCardCount: number;
  } | null>(null);
  const locallyMovingCardIdsRef = useRef<Set<string>>(new Set());
  const remoteMoveAnnouncementQueueRef = useRef<string[]>([]);
  const remoteMoveAnnouncementTimerRef = useRef<number | null>(null);
  const filterActive = isBoardFilterActive(filter);
  const filterKey = useMemo(() => boardFilterCacheKey(filter), [filter]);
  const visibleCardCount = useMemo(
    () => lists.reduce((total, list) => total + list.cards.length, 0),
    [lists],
  );
  const orderedSelectedCardIds = useMemo(
    () => orderSelectedCardIds(lists, selectedCardIds),
    [lists, selectedCardIds],
  );
  const selectedCards = useMemo(
    () => selectedCardsInBoardOrder(lists, selectedCardIds),
    [lists, selectedCardIds],
  );
  const boardFilterFormatter = useMemo(
    () =>
      boardFilterLabelFormatter(t, {
        labelColour: (colour) => labelColourDisplayName(t, colour),
      }),
    [t],
  );
  const filterChips = useMemo(
    () =>
      boardFilterChips(
        filter,
        members.data ?? [],
        offlineContents.labels,
        customFieldDefinitions,
        boardFilterFormatter,
      ),
    [
      boardFilterFormatter,
      customFieldDefinitions,
      filter,
      members.data,
      offlineContents.labels,
    ],
  );
  const boardViewText = useCallback(
    (view: BoardViewKind) => boardViewLabel(t, view),
    [t],
  );
  const liveMessage =
    liveStatus === undefined || liveStatus === 'disabled'
      ? null
      : t(`boardFace.live.${liveStatus}`);
  const viewAccounting = useMemo(
    () => ({
      board: boardViewAccounting('board', contents),
      table: boardViewAccounting('table', contents),
      calendar: boardViewAccounting('calendar', contents),
      timeline: boardViewAccounting('timeline', contents),
      dashboard: boardViewAccounting('dashboard', contents),
      map: boardViewAccounting('map', contents),
    }),
    [contents],
  );

  useEffect(() => {
    /*
     * ⚠ `FR-EXP-033` AC2 — reset here, with the rest of the per-board chrome.
     * The route renders `BoardFace` unkeyed, so moving between boards changes
     * `board.id` without remounting; that is the whole reason this effect
     * exists rather than the state being fresh. `welcomeTipConfirmed` alone
     * satisfies `showWelcomeTip` once the tip is spent, so leaving it set
     * congratulates a member a second time, on a later visit, for a drag they
     * did earlier — the confirmation outliving the interaction it confirms.
     */
    setWelcomeTipConfirmed(false);
    if (profile === null) {
      setPinnedViews([]);
      setFocusModeEnabled(false);
      adoptedFocusModeForRef.current = null;
      adoptedPinnedViewsForRef.current = null;
      return;
    }
    const chrome = loadLocalBoardViewChrome(profile.id, board.id);
    setPinnedViews(chrome.pinnedViews);
    setFocusModeEnabled(chrome.focusMode);
    adoptedFocusModeForRef.current = null;
    adoptedPinnedViewsForRef.current = null;
  }, [board.id, profile?.id]);

  /**
   * `FR-VIEW-011` AC2 — **the server owns focus mode; `localStorage` only
   * paints the first frame.**
   *
   * The effect above still seeds from the local record, so a member who left
   * focus mode on does not watch the chrome appear and then vanish. This one
   * adopts the durable answer the moment `GET /me/display-preferences`
   * resolves. Where they disagree the server wins, which is how focus mode
   * follows a member to a second device — and, because the column is
   * user-scoped with no `board_id`, to every other board too. That is the
   * contract; see `lib/view-state.ts`.
   *
   * ⚠ **Once per `(board.id, profile.id)`, tracked by a ref — not "whenever
   * the query answer differs from the state".** Two writers to one piece of
   * state is how a late resolution stamps a stale value over a click the
   * member has already made: toggle, then a slow `GET` lands, then the chrome
   * comes back on its own with nothing red anywhere. The ref makes adoption a
   * one-shot hydration rather than a standing reconciliation, so every toggle
   * after it belongs to the member.
   */
  useEffect(() => {
    if (profile === null) return;
    const preferences = displayPreferences.data;
    if (preferences === undefined) return;

    const scope = `${profile.id}:${board.id}`;
    if (adoptedFocusModeForRef.current === scope) return;
    adoptedFocusModeForRef.current = scope;

    /*
     * ⚠ **Assigned unconditionally, never guarded on
     * `preferences.focusModeEnabled === focusModeEnabled`.** That guard looks
     * free and is a trap: on the mount commit this effect runs beside the hint
     * effect above, and it closes over the `focusModeEnabled` of the render it
     * belongs to — the `useState` initial `false`, not the value the hint
     * effect has just set. A server answer of `false` over a local hint of
     * `true` therefore compared equal, returned early with the ref already
     * marked adopted, and left the chrome hidden for a member whose durable
     * answer says it should be shown. React bails out of an identical
     * `setState` on its own, so the guard bought nothing and cost that.
     */
    setFocusModeEnabled(preferences.focusModeEnabled);

    /*
     * ⚠ **The record is RE-READ, never closed over.** The local record carries
     * the member's pinned views beside their focus mode, so writing it back
     * means writing both — and `pinnedViews` here would be the `pinnedViews`
     * of the render this effect belongs to. On a warm cache
     * (`displayPreferences.data` already present on the first render, which is
     * every board open after the first in a session) this effect runs in the
     * same commit as the hint effect above, so that value is the `useState`
     * initial `[]` rather than the pins the hint effect has just set — and the
     * write empties the member's pinned views with nothing red anywhere.
     *
     * Re-reading is also what keeps the omission of `pinnedViews` from the
     * dependency list honest: this effect must fire when the query resolves
     * and never when a pin is moved, and it now reads nothing that a pin move
     * changes.
     */
    const chrome = loadLocalBoardViewChrome(profile.id, board.id);
    saveLocalBoardViewChrome(profile.id, board.id, {
      pinnedViews: chrome.pinnedViews,
      focusMode: preferences.focusModeEnabled,
    });
  }, [board.id, profile?.id, displayPreferences.data]);

  /**
   * `FR-VIEW-004` AC2 — **the server owns the pinned order; `localStorage`
   * only paints the first frame.** `FE-55.1`.
   *
   * `GET`/`PUT /boards/:id/pinned-views` shipped with `BE-55.3` and nothing in
   * `apps/web/src` called them: pins were written to
   * `molt:board-view-chrome:<userId>:<boardId>` and read back from it, so
   * *"when they reload, the new order persists for that member"* held for one
   * browser on one machine. This effect is where the durable answer arrives.
   *
   * The shape is deliberately the mirror of the focus-mode adoption above it,
   * for the same three reasons, and the two differences are both written out:
   *
   * ⚠ **One-shot, keyed on `(board.id, profile.id)` by a ref** — never "adopt
   * whenever the query answer differs from the state". Two writers to one
   * piece of state is how a late `GET` stamps a stale order over a drag the
   * member has already made: the strip reorders itself on its own, seconds
   * after they let go, with nothing red anywhere. `setPinnedViewsForCurrentMember`
   * marks the ref for the same reason from the other side — an explicit
   * reorder made while the first `GET` is still in flight is the member's
   * intent, and it outranks the answer that was already stale when it was
   * asked for.
   *
   * ⚠ **`focusMode` is RE-READ from the record, never closed over** — the
   * exact mirror of the trap the effect above documents, in the direction that
   * effect's test cannot see. The local record carries both facets, so writing
   * it back means writing both, and `focusModeEnabled` here would be the
   * `focusModeEnabled` of the render this effect belongs to. On a warm cache
   * (both queries resolved before the first render, which is every board open
   * after the first in a session) this effect commits beside the hint effect,
   * so that value is the `useState` initial `false` rather than the durable
   * `true` — and the write-back silently turns focus mode off for a member
   * whose durable answer says on. `BoardFace.test.tsx`'s *"adopting focus mode
   * does not empty the member's pinned views"* stays green through it, because
   * it only asserts the other direction. Both write-backs re-read; neither
   * closes over state.
   *
   * ⚠ **The one-time seed, and it is the only case where local outranks the
   * server.** On the day this lands every member who has ever pinned a view
   * has a non-empty local record and an empty server set, because nothing has
   * ever written the route. Server-wins-always would unpin every member in the
   * product at once, against `FR-VIEW-004` AC1's promise that the views a
   * member pinned *"appear when they open the board"* — so an empty server set
   * beside a non-empty local hint `PUT`s the hint once, ref-gated, and the
   * server is authoritative from then on. Every other disagreement, including
   * two non-empty sets that differ, is won by the server: that is the
   * cross-device answer AC2 asks for. The bounded cost is a second device with
   * a stale local record re-pinning once, which is the *less* surprising
   * outcome — pins were never cross-device before this landed, so that record
   * is exactly what that device's member is already looking at.
   */
  useEffect(() => {
    if (profile === null) return;
    const durable = durablePinnedViews.data;
    if (durable === undefined) return;

    const scope = `${profile.id}:${board.id}`;
    if (adoptedPinnedViewsForRef.current === scope) return;
    adoptedPinnedViewsForRef.current = scope;

    const chrome = loadLocalBoardViewChrome(profile.id, board.id);
    const server = normalisePinnedViews(durable.views);

    if (server.length === 0 && chrome.pinnedViews.length > 0) {
      /*
       * Seed. The hint effect has already put these on screen, so the
       * assignment is a no-op React bails out of — it is written
       * unconditionally anyway, because "the hint effect has already run" is
       * an ordering claim about a sibling effect rather than something this
       * one can see.
       */
      setPinnedViews(chrome.pinnedViews);
      setPinnedViewsPreference.mutate(chrome.pinnedViews);
      return;
    }

    /*
     * ⚠ Assigned unconditionally, never guarded on "the server answer already
     * equals the state". That guard is the same trap the focus-mode effect
     * argues at length: on a warm cache this effect closes over the render's
     * `pinnedViews`, which is the `useState` initial `[]` rather than the pins
     * the hint effect has just set, so an empty server answer would compare
     * equal, return with the ref marked adopted, and leave a stale local order
     * on screen for the rest of the session.
     */
    setPinnedViews(server);
    saveLocalBoardViewChrome(profile.id, board.id, {
      pinnedViews: server,
      focusMode: chrome.focusMode,
    });
  }, [board.id, profile?.id, durablePinnedViews.data]);

  useEffect(() => {
    const source = `board-focus:${board.id}`;
    setShellChromeSuppressed(source, focusModeEnabled);
    return () => setShellChromeSuppressed(source, false);
  }, [board.id, focusModeEnabled]);

  function setPinnedViewsForCurrentMember(views: readonly BoardViewKind[]): void {
    const next = normalisePinnedViews(views);
    setPinnedViews(next);
    if (profile !== null) {
      saveLocalBoardViewChrome(profile.id, board.id, {
        pinnedViews: next,
        focusMode: focusModeEnabled,
      });
      /*
       * `FR-VIEW-004` AC2, the durable half. The local write above stays — it
       * is what paints the next board open before the query answers — but it
       * is no longer the authority, and this is what carries the order to the
       * member's other devices.
       *
       * ⚠ Guarded on a session, not on `canEdit`: a pinned view is per-member
       * display chrome, so a read-only member and an observer are as entitled
       * to it as an admin. `board_pinned_views`'s primary key leads with
       * `user_id`, so no member can write another's row and no other member
       * sees this one — which is also why the route emits no event and the
       * strip needs no socket.
       *
       * ⚠ The adoption ref is marked here, not only in the hydration effect.
       * A member who reorders while the first `GET` is still in flight has
       * expressed an intent that is newer than the answer being fetched;
       * without this line that answer lands afterwards and reorders the strip
       * under them.
       *
       * ⚠ Serialisation lives in `useSetBoardPinnedViews`, not here. The body
       * is the WHOLE order, so two reorders fired a beat apart would otherwise
       * interleave and let the slower round trip decide the durable answer.
       * The mutation carries a `scope`, so the second is paused until the
       * first settles and the last request is the last intent. The strip does
       * not wait on any of it: `setPinnedViews(next)` above is synchronous and
       * the render never reads the query cache.
       *
       * ⚠ **The failure path, which the hook hands to this file in writing**
       * (*"recovery policy belongs to the board face, which owns that state;
       * this hook must not invent one"*): there is none, deliberately. The
       * mutation has no optimistic write and therefore nothing to roll back,
       * and the two lines above have already put the member's order in
       * `useState` and in the local record. A failed `PUT` leaves the strip
       * showing what the member did, the local record agreeing with it, and
       * the server one order behind — and the next open of this board paints
       * that record as the hint and then adopts the server's answer over it.
       * The strip self-corrects on the next open rather than snapping back
       * under a member who is still looking at it. What is NOT offered is a
       * rollback: undoing a reorder the member can see, because a request they
       * cannot see failed, is the worse of the two surprises.
       */
      adoptedPinnedViewsForRef.current = `${profile.id}:${board.id}`;
      setPinnedViewsPreference.mutate(next);
    }
  }

  function movePinnedView(view: BoardViewKind, direction: -1 | 1): void {
    const next = reorderPinnedViews(
      pinnedViews,
      view,
      direction === -1 ? 'left' : 'right',
    );
    if (next.join('\0') === pinnedViews.join('\0')) return;
    setPinnedViewsForCurrentMember(next);
    announce(
      t('boardFace.pinned.reordered', {
        views: next.map(boardViewText).join(', '),
      }),
    );
  }

  function handlePinnedViewDragStart(
    event: DragEvent<HTMLDivElement>,
    view: BoardViewKind,
  ): void {
    draggingPinnedViewRef.current = view;
    setDraggingPinnedView(view);
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', view);
  }

  function handlePinnedViewDragOver(event: DragEvent<HTMLDivElement>): void {
    if (draggingPinnedViewRef.current === null && draggingPinnedView === null) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }

  function handlePinnedViewDrop(
    event: DragEvent<HTMLDivElement>,
    targetView: BoardViewKind,
  ): void {
    event.preventDefault();
    const dragged =
      draggingPinnedViewRef.current ??
      draggingPinnedView ??
      boardViewKindFromDragData(event.dataTransfer.getData('text/plain'));
    draggingPinnedViewRef.current = null;
    setDraggingPinnedView(null);
    if (dragged === null) return;
    const { left, width } = event.currentTarget.getBoundingClientRect();
    const position = event.clientX < left + width / 2 ? 'before' : 'after';
    const next = dragPinnedView(pinnedViews, dragged, targetView, position);
    if (next.join('\0') === pinnedViews.join('\0')) return;
    setPinnedViewsForCurrentMember(next);
    announce(
      t('boardFace.pinned.reordered', {
        views: next.map(boardViewText).join(', '),
      }),
    );
  }

  function boardViewKindFromDragData(value: string): BoardViewKind | null {
    return normalisePinnedViews([value])[0] ?? null;
  }

  function setFocusModeForCurrentMember(enabled: boolean): void {
    if (enabled) {
      setMenuOpen(false);
      setFilterOpen(false);
    }
    setFocusModeEnabled(enabled);
    if (profile !== null) {
      saveLocalBoardViewChrome(profile.id, board.id, {
        pinnedViews,
        focusMode: enabled,
      });
      /*
       * `FR-VIEW-011` AC2, the durable half. The local write above stays so
       * the next board open paints the right frame before the query answers;
       * this is what makes the state survive a different browser, and it is
       * optimistic (`useSetFocusMode`) so the chrome goes on the click rather
       * than a round trip later.
       *
       * ⚠ Guarded on a session, not on `canEdit`: focus mode is a per-member
       * display state, so a read-only member and an observer are as entitled
       * to it as an admin. It changes no board data and no other member sees
       * it.
       */
      setFocusModePreference.mutate(enabled);
    }
    announce(t(enabled ? 'boardFace.focus.on' : 'boardFace.focus.off'));
  }

  /**
   * `FR-EXP-033` AC2 — *"a confirmation of the interaction is shown and the
   * tip does not repeat"*.
   *
   * Three conditions, and all three are narrow on purpose:
   *
   *  - **this board is the caller's own welcome board** — an id comparison,
   *    so no other board in the product can ever render this;
   *  - **the account has not spent its one tip** — `has_seen_welcome_tip` is
   *    a single user-scoped boolean, so one-time means once per account, ever;
   *  - **secondary chrome is showing** — a member who entered focus mode
   *    asked for the chrome to go, and the tip is chrome. It returns when they
   *    leave focus mode, because the tip is spent by the drag, not by hiding.
   *
   * `welcomeTipConfirmed` keeps the panel on screen after the acknowledgement
   * has optimistically flipped `welcomeTipSeen`, so the confirmation is
   * actually readable instead of vanishing with the tip that produced it.
   */
  const welcomeBoardId = onboardingTips.data?.welcomeBoardId ?? null;
  const isWelcomeBoard = welcomeBoardId !== null && welcomeBoardId === board.id;
  const showWelcomeTip =
    signedIn &&
    isWelcomeBoard &&
    showSecondaryBoardChrome &&
    (welcomeTipConfirmed || onboardingTips.data?.welcomeTipSeen === false);

  /**
   * Spending the tip. Called from the two cross-list card drops below, on
   * success rather than on drop, so the confirmation states something that
   * actually happened.
   *
   * ⚠ The acknowledgement is one-way and there is no verb to undo it — see
   * `useMarkWelcomeTipSeen`, which deliberately does not roll back on error.
   * A tip that reappeared after the member had already dragged the card is the
   * one outcome `FR-EXP-033` AC2 forbids.
   */
  function spendWelcomeTip(fromListId: string, toListId: string): void {
    if (fromListId === toListId) return;
    if (!isWelcomeBoard) return;
    if (onboardingTips.data?.welcomeTipSeen !== false) return;
    setWelcomeTipConfirmed(true);
    announce(t('boardFace.welcomeTip.done'));
    markWelcomeTipSeen.mutate();
  }

  function collapseAllLists(): void {
    if (onListCollapseChange === undefined) return;
    for (const list of lists) {
      if (collapsedListIds?.has(list.id) === true) continue;
      onListCollapseChange(list.id, true);
    }
    announce(t('boardFace.lists.allCollapsed'));
  }

  function expandAllLists(): void {
    if (onListCollapseChange === undefined) return;
    for (const list of lists) {
      if (collapsedListIds?.has(list.id) !== true) continue;
      onListCollapseChange(list.id, false);
    }
    announce(t('boardFace.lists.allExpanded'));
  }

  const calendarGrid = useMemo(
    () =>
      buildBoardCalendarGrid({
        mode: calendarMode,
        anchorDate: calendarAnchor,
        weekStartsOn: calendarWeekStartsOn,
      }),
    [calendarAnchor, calendarMode, calendarWeekStartsOn],
  );
  const calendarInput = useMemo(
    () => ({
      from: calendarGrid.from,
      to: calendarGrid.to,
      filter,
    }),
    [calendarGrid.from, calendarGrid.to, filter],
  );
  const boardCalendar = useQuery({
    ...boardCalendarQueryOptions(board.id, calendarInput),
    enabled: effectiveActiveView === 'calendar' && !showingMobileParityNotice,
  });
  const calendarPowerUpGrid = useMemo(
    () =>
      buildBoardCalendarGrid({
        mode: 'month',
        anchorDate: calendarAnchor,
        weekStartsOn: calendarWeekStartsOn,
      }),
    [calendarAnchor, calendarWeekStartsOn],
  );
  const calendarPowerUpInput = useMemo(
    () => ({
      from: calendarPowerUpGrid.from,
      to: calendarPowerUpGrid.to,
    }),
    [calendarPowerUpGrid.from, calendarPowerUpGrid.to],
  );
  const calendarPowerUpCalendar = useQuery({
    ...boardCalendarQueryOptions(board.id, calendarPowerUpInput),
    enabled: calendarPowerUpOpen && calendarPowerUpEntry !== null,
  });
  const timelineScale = useMemo(
    () =>
      buildBoardTimelineScale({
        zoom: timelineZoom,
        anchorDate: timelineAnchor,
      }),
    [timelineAnchor, timelineZoom],
  );
  const timelineInput = useMemo(
    () => ({
      from: timelineScale.from,
      to: timelineScale.to,
      filter,
      groupBy: timelineGroupBy,
    }),
    [filter, timelineGroupBy, timelineScale.from, timelineScale.to],
  );
  const boardTimeline = useQuery({
    ...boardTimelineQueryOptions(board.id, timelineInput),
    enabled: effectiveActiveView === 'timeline' && !showingMobileParityNotice,
  });
  const timelineUnscheduledCards = useMemo(
    () => boardTimelineUnscheduledCardsFromContents(contents),
    [contents],
  );
  const boardTable = useQuery({
    ...boardTableQueryOptions(board.id, filter),
    enabled: effectiveActiveView === 'table' && !showingMobileParityNotice,
  });
  /* FR-VIEW-012 (FE-56.2) — the Table view's custom-field COLUMNS. Deliberately
     the same `boardCustomFieldsQueryOptions` the board menu's Custom Fields
     panel uses rather than a second projection, so the two share a cache entry
     and a definition renamed in the panel renames the column. The table payload
     carries only the per-card values; see `BoardTableView.tsx`'s header for why
     the split, and why this must never be handed to the Workspace Table. */
  const boardTableCustomFields = useQuery({
    ...boardCustomFieldsQueryOptions(board.id),
    enabled: effectiveActiveView === 'table' && !showingMobileParityNotice,
  });
  const boardDashboard = useQuery({
    ...boardDashboardQueryOptions(board.id, filter),
    enabled: effectiveActiveView === 'dashboard' && !showingMobileParityNotice,
  });
  const boardMap = useQuery({
    ...boardMapQueryOptions(board.id, filter),
    enabled: effectiveActiveView === 'map' && !showingMobileParityNotice,
  });

  const moveList = useMoveList(board.id);
  const moveCard = useMoveCard(board.id);
  const moveCardToBoard = useMoveCardToBoard(board.id);
  const copyCard = useCopyCard(board.id, t);
  const copyList = useCopyList(board.id, t);
  const moveListToBoard = useMoveListToBoard(board.id);
  const sortListCards = useSortListCards(board.id);
  const archiveAllListCards = useArchiveAllListCards(board.id);
  const moveAllListCards = useMoveAllListCards(board.id);
  const createBulkCards = useCreateBulkCards(board.id);
  const bulkArchiveCards = useBulkArchiveCards();
  const bulkMoveCards = useBulkMoveCards();
  const bulkCopyCards = useBulkCopyCards();
  const mergeCards = useMergeCards(board.id);
  const setCardCompleted = useSetCardCompleted();
  const renameCard = useRenameCard(board.id);
  const applyCardLabel = useApplyCardLabel();
  const removeCardLabel = useRemoveCardLabel();
  const addCardMember = useAddCardMember();
  const removeCardMember = useRemoveCardMember();
  const createDashboardTile = useCreateDashboardTile(board.id);
  const updateDashboardTileFacet = useUpdateDashboardTileFacet(board.id);
  const updateDashboardTileChartType = useUpdateDashboardTileChartType(board.id);
  const deleteDashboardTile = useDeleteDashboardTile(board.id);
  const sensors = useMoltSensors();
  const reducedMotion = useReducedMotionPreference(accessibilityPreferences);
  const shortcutPreferenceState = useMemo(
    () => ({
      singleKeyShortcutsDisabled:
        accessibilityPreferences?.singleKeyShortcutsDisabled ?? false,
      reducedMotion,
    }),
    [accessibilityPreferences?.singleKeyShortcutsDisabled, reducedMotion],
  );
  const boardShortcutContext = useMemo<KeyboardShortcutContext>(
    () => ({
      canEdit: workingCanEdit,
      canUseCardTarget: workingCanEdit && lists.some((list) => list.cards.length > 0),
      canUseWatch: workingCanEdit,
      canUseDates: workingCanEdit,
      canUseLabels: workingCanEdit,
      canUseMembers: workingCanEdit,
      singleKeyShortcutsDisabled: shortcutPreferenceState.singleKeyShortcutsDisabled,
      platform: platformShortcutContext(),
    }),
    [lists, shortcutPreferenceState.singleKeyShortcutsDisabled, workingCanEdit],
  );

  /**
   * What renders. ⚠ Never what `cardNeighboursForDrop` is given — see the
   * header's §12.9 note; that function gets `lists`, the untouched prop.
   */
  const previewLists = useMemo(
    () => applyCardPreview(lists, cardPreview),
    [lists, cardPreview],
  );

  const resolveShortcutListId = useCallback(
    (target: EventTarget | null): string | null => {
      if (!(target instanceof HTMLElement)) return null;

      const cardId = target.closest<HTMLElement>('[data-card-id]')?.dataset.cardId;
      if (cardId !== undefined) {
        return (
          lists.find((entry) => entry.cards.some((card) => card.id === cardId))?.id ??
          null
        );
      }

      const cardStack = target.closest<HTMLElement>('[data-board-card-stack-list-id]');
      const stackListId = cardStack?.dataset.boardCardStackListId;
      if (stackListId !== undefined && stackListId !== '') {
        return lists.some((entry) => entry.id === stackListId) ? stackListId : null;
      }

      const section = target.closest<HTMLElement>('section[data-board-list-id]');
      const sectionListId = section?.dataset.boardListId;
      if (sectionListId !== undefined && sectionListId !== '') {
        return lists.some((entry) => entry.id === sectionListId) ? sectionListId : null;
      }

      const labelledSection = target.closest<HTMLElement>('section[aria-labelledby]');
      const headingId = labelledSection?.getAttribute('aria-labelledby')?.split(/\s+/)[0];
      const heading =
        headingId === undefined
          ? null
          : labelledSection?.ownerDocument.getElementById(headingId);
      const headingTitle = heading?.getAttribute('title') ?? heading?.textContent?.trim();
      if (headingTitle === undefined || headingTitle === '') return null;

      const matches = lists.filter((entry) => entry.title === headingTitle);
      return matches.length === 1 ? (matches[0]?.id ?? null) : null;
    },
    [lists],
  );

  const handleListCollapseChange = useCallback(
    (listId: string, collapsed: boolean) => {
      setOpenComposer((current) =>
        current?.kind === 'card' && current.listId === listId ? null : current,
      );
      setQuickEditCardId((current) => {
        if (current === null) return current;
        const list = lists.find((entry) => entry.id === listId);
        return list?.cards.some((card) => card.id === current) === true ? null : current;
      });
      onListCollapseChange?.(listId, collapsed);
    },
    [lists, onListCollapseChange],
  );

  useLayoutEffect(() => {
    if (cardReturn === undefined) return undefined;

    setHighlightedCardId(cardReturn.cardId);
    const strip = stripRef.current;
    if (strip !== null) {
      strip.scrollLeft = cardReturn.stripScrollLeft;
      const highlighted = strip.querySelector<HTMLElement>(
        '[data-board-card-highlighted="true"]',
      );
      const cardStack = highlighted?.closest<HTMLElement>(
        '[data-board-card-stack-list-id]',
      );
      if (cardStack !== undefined && cardStack !== null) {
        cardStack.scrollTop = cardReturn.listScrollTop;
      }
      highlighted?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
    }

    const timeout = window.setTimeout(() => {
      setHighlightedCardId((current) => (current === cardReturn.cardId ? null : current));
    }, 3000);

    return () => window.clearTimeout(timeout);
  }, [cardReturn]);

  function handleCardOpen(intent: BoardCardOpenIntent) {
    onCardOpen?.({
      ...intent,
      stripScrollLeft: stripRef.current?.scrollLeft ?? 0,
    });
  }

  const clearCardSelection = useCallback((): void => {
    setSelectedCardIds(new Set());
    setSelectionAnchor(null);
    setSelectionNotice(null);
    setBulkCardActionTarget(null);
  }, []);

  function handleCardSelectionGesture(
    input: CardSelectionInput,
    card: BoardFaceList['cards'][number],
  ): void {
    const result = selectCardByGesture(t, lists, selectedCardIds, selectionAnchor, {
      cardId: card.id,
      listId: card.listId,
      shiftKey: input.shiftKey,
      metaKey: input.metaKey,
      ctrlKey: input.ctrlKey,
    });
    setSelectedCardIds(result.selectedCardIds);
    setSelectionAnchor(result.anchor);
    setSelectionNotice(result.notice);
    if (result.notice !== null) announce(result.notice);
    setQuickEditCardId(null);
  }

  function handleOpenBulkCardActions(
    card: BoardFaceList['cards'][number],
    listId: string,
    mode: BulkCardActionMode = 'archive',
  ): void {
    if (!selectedCardIds.has(card.id)) {
      setSelectedCardIds(new Set([card.id]));
      setSelectionAnchor({ cardId: card.id, listId });
    }
    setBulkCardActionTarget({ cardId: card.id, listId, mode });
    setQuickEditCardId(null);
  }

  function handleBulkCardActionSubmit(input: BulkCardActionSubmit): void {
    const cardIds = orderedSelectedCardIds;
    if (cardIds.length === 0) return;

    if (input.mode === 'archive') {
      bulkArchiveCards.mutate(
        { boardId: board.id, cardIds },
        {
          onSuccess: () => {
            clearCardSelection();
          },
        },
      );
      return;
    }

    if (input.mode === 'move') {
      bulkMoveCards.mutate(
        {
          boardId: board.id,
          destinationBoardId: input.boardId,
          cardIds,
          listId: input.listId,
          previousCardId: input.previousCardId,
          nextCardId: input.nextCardId,
          destinationListTitle: input.destinationListTitle,
        },
        {
          onSuccess: () => {
            clearCardSelection();
          },
        },
      );
      return;
    }

    if (input.mode === 'merge') {
      if (
        input.targetCardId === undefined ||
        input.sourceCardId === undefined ||
        input.targetTitle === undefined ||
        input.sourceTitle === undefined
      ) {
        return;
      }
      mergeCards.mutate(
        {
          targetCardId: input.targetCardId,
          sourceCardId: input.sourceCardId,
          targetTitle: input.targetTitle,
          sourceTitle: input.sourceTitle,
        },
        {
          onSuccess: (result) => {
            clearCardSelection();
            setBulkCardActionTarget(null);
            announce(
              t('boardFace.card.merged', {
                target: cardDisplayTitle(t, input.targetTitle ?? ''),
                source: cardDisplayTitle(t, input.sourceTitle ?? ''),
                result: result.card.title,
              }),
            );
            void undoRedo.recordSource({
              boardId: board.id,
              cardId: result.card.id,
              activityEntryId: result.activityEntryId,
              scope: { kind: 'card', cardId: result.card.id },
              eventTypes: ['card.merged'],
              label: t('undo.source.cardsMerged', {
                target: cardDisplayTitle(t, input.targetTitle ?? ''),
                source: cardDisplayTitle(t, input.sourceTitle ?? ''),
              }),
            });
          },
        },
      );
      return;
    }

    bulkCopyCards.mutate(
      {
        boardId: board.id,
        destinationBoardId: input.boardId,
        cardIds,
        listId: input.listId,
        previousCardId: input.previousCardId,
        nextCardId: input.nextCardId,
        keepChecklists: input.keepChecklists,
        keepLabels: input.keepLabels,
        keepMembers: input.keepMembers,
        keepAttachments: input.keepAttachments,
        keepDates: input.keepDates,
        destinationListTitle: input.destinationListTitle,
      },
      {
        onSuccess: () => {
          clearCardSelection();
        },
      },
    );
  }

  const handleOpenShortcuts = useCallback((): void => {
    onOpenShortcuts?.({
      context: boardShortcutContext,
      returnFocus:
        document.activeElement instanceof HTMLElement ? document.activeElement : null,
    });
  }, [boardShortcutContext, onOpenShortcuts]);

  /*
   * The preview is retired by the DATA rather than by the drop (header, §12.9).
   * A no-op `setCardPreview(null)` when it is already null bails out inside
   * React without a re-render, so this costs nothing on mount or on any render
   * where nothing was being previewed.
   */
  useEffect(() => {
    setCardPreview(null);
  }, [lists]);

  useEffect(() => {
    const previous = previousCardPlacementsRef.current;
    const next = cardPlacements(t, lists);
    previousCardPlacementsRef.current = next;
    if (previous === null) return;

    const changedLists: string[] = [];
    const changedPositions: string[] = [];
    for (const [cardId, placement] of next) {
      const before = previous.get(cardId);
      if (before === undefined) continue;
      if (before.listId === placement.listId && before.index === placement.index) {
        continue;
      }
      if (locallyMovingCardIdsRef.current.has(cardId)) {
        continue;
      }
      const message = t('boardFace.remote.cardMoved', {
        title: placement.title,
        list: placement.listTitle,
        position: placement.index + 1,
      });
      if (before.listId !== placement.listId) {
        changedLists.push(message);
      } else {
        changedPositions.push(message);
      }
    }

    const remoteMoves = changedLists.length > 0 ? changedLists : changedPositions;
    if (remoteMoves.length > 0) {
      remoteMoveAnnouncementQueueRef.current.push(...remoteMoves);
      if (remoteMoveAnnouncementTimerRef.current !== null) {
        window.clearTimeout(remoteMoveAnnouncementTimerRef.current);
      }
      remoteMoveAnnouncementTimerRef.current = window.setTimeout(() => {
        const queuedMoves = remoteMoveAnnouncementQueueRef.current;
        remoteMoveAnnouncementQueueRef.current = [];
        remoteMoveAnnouncementTimerRef.current = null;
        if (queuedMoves.length === 1) {
          announce(queuedMoves[0]!);
        } else if (queuedMoves.length > 1) {
          announce(t('boardFace.remote.cardsMoved', { count: queuedMoves.length }));
        }
      }, REMOTE_MOVE_ANNOUNCEMENT_WINDOW_MS);
    }
    // ⚠ `t` is in the dependency list for the reason fe-plan residual 11 keeps
    // asking about: without it this effect closes over the catalogue that was
    // current when `lists` last changed, and the first announcement after a
    // language switch is the previous language's.
  }, [lists, t]);

  useEffect(
    () => () => {
      if (remoteMoveAnnouncementTimerRef.current !== null) {
        window.clearTimeout(remoteMoveAnnouncementTimerRef.current);
      }
    },
    [],
  );

  useEffect(() => {
    const previous = previousFilterAnnouncementRef.current;
    const current = { filterKey, visibleCardCount };
    previousFilterAnnouncementRef.current = current;
    if (previous === null) {
      if (filterActive) {
        announce(
          t('boardFace.filter.showing', { count: visibleCardCount }, visibleCardCount),
        );
      }
      return;
    }

    const filterChanged = previous.filterKey !== filterKey;
    const activeResultCountChanged =
      filterActive && previous.visibleCardCount !== visibleCardCount;
    if (!filterChanged && !activeResultCountChanged) return;

    if (!filterActive) {
      if (filterChanged) announce(t('boardFace.filter.cleared'));
      return;
    }

    announce(
      t('boardFace.filter.showing', { count: visibleCardCount }, visibleCardCount),
    );
  }, [filterActive, filterKey, t, visibleCardCount]);

  /*
   * `FR-VIEW-010`'s behaviour text: shift+wheel moves the strip sideways.
   *
   * A native listener rather than React's `onWheel`, and the reason is not
   * style: React attaches its delegated `wheel` listener **passively**, so
   * `event.preventDefault()` inside an `onWheel` handler does nothing (and
   * warns in Chrome). Without a real `preventDefault` the browser scrolls the
   * strip natively *as well as* this handler moving it, which double-scrolls.
   *
   * ⚠ The guard on `delta === 0` is what stops a plain vertical wheel over a
   * card column from scrolling the whole board sideways: `wheelToHorizontalDelta`
   * (agent F) answers 0 for that gesture, this handler then declines to
   * `preventDefault`, and the event falls through to the column's own vertical
   * scroller untouched. Returning early *before* preventing is the whole
   * contract between the two files.
   */
  useEffect(() => {
    const strip = stripRef.current;
    if (strip === null) return;

    // An arrow expression, not a `function` declaration: a declaration is
    // hoisted above the null guard, so TypeScript refuses to narrow `strip`
    // inside it and the body would need a second, redundant check.
    const onWheel = (event: WheelEvent) => {
      // A gesture the browser already reads as horizontal is left to the
      // browser: it carries rubber-band and inertia that reimplementing
      // `scrollLeft` throws away. Firefox reports shift+wheel this way and
      // Chrome reports it as `deltaY` + `shiftKey`, so between this early
      // return and the conversion below both spellings land in one place.
      if (event.deltaX !== 0) return;

      const delta = wheelToHorizontalDelta(event);
      if (delta === 0) return;
      event.preventDefault();
      // `scrollLeft`, not `scrollBy({behavior})`: a wheel gesture is already
      // the member's own continuous motion, and smoothing it lags the pointer.
      // Reduced-motion is `lib/board-focus.ts`'s concern, for the jumps this
      // file does not make.
      strip.scrollLeft += delta;
    };

    strip.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      strip.removeEventListener('wheel', onWheel);
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape' && selectedCardIds.size > 0) {
        event.preventDefault();
        clearCardSelection();
        return;
      }
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isEditableShortcutTarget(event.target)) return;
      if (event.key === '?') {
        if (onOpenShortcuts === undefined) return;
        event.preventDefault();
        handleOpenShortcuts();
        return;
      }
      if (event.key.toLowerCase() === 'z') {
        if (
          !event.shiftKey &&
          shouldSuppressSingleKeyShortcuts({
            singleKeyShortcutsDisabled:
              shortcutPreferenceState.singleKeyShortcutsDisabled,
            target: event.target,
          })
        ) {
          return;
        }
        event.preventDefault();
        if (event.shiftKey) {
          undoRedo.redo();
        } else {
          undoRedo.undo();
        }
        return;
      }
      if (
        shouldSuppressSingleKeyShortcuts({
          singleKeyShortcutsDisabled: shortcutPreferenceState.singleKeyShortcutsDisabled,
          target: event.target,
        })
      ) {
        return;
      }
      if (event.key.toLowerCase() === 'f') {
        event.preventDefault();
        setFilterOpen(true);
        return;
      }
      if (event.key.toLowerCase() === 'x') {
        if (!filterActive) return;
        event.preventDefault();
        onFilterChange?.(EMPTY_BOARD_FILTER);
        return;
      }
      if (event.key.toLowerCase() === 'q') {
        if (profile === null || onFilterChange === undefined) return;
        event.preventDefault();
        onFilterChange(toggleMyCardsFilter(filter, profile.id));
        return;
      }
      if (event.key === ']') {
        event.preventDefault();
        setMenuOpen(true);
        return;
      }
      if (event.key === '\\') {
        if (onListCollapseChange === undefined) return;
        const listId = resolveShortcutListId(event.target);
        if (listId === null) return;
        event.preventDefault();
        handleListCollapseChange(listId, !(collapsedListIds?.has(listId) ?? false));
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [
    collapsedListIds,
    clearCardSelection,
    selectedCardIds,
    filter,
    filterActive,
    handleListCollapseChange,
    onFilterChange,
    onListCollapseChange,
    handleOpenShortcuts,
    profile,
    resolveShortcutListId,
    shortcutPreferenceState.singleKeyShortcutsDisabled,
    undoRedo,
  ]);

  /*
   * `FR-EXP-022`'s edge case: "a focused card inside a scrolled column is
   * scrolled into view". React's `onFocus` is `focusin` — it bubbles — so one
   * handler on the strip covers every card, every column header and every
   * affordance inside it, and nothing below has to know about it.
   *
   * The reveal itself (both axes, and whether it animates) is
   * `lib/board-focus.ts`'s, agent F's file. This one supplies the element and
   * makes no decision.
   */
  function handleFocusIn(event: FocusEvent<HTMLElement>) {
    revealFocusedElement(event.target, { reducedMotion });
  }

  /*
   * ── The drag ──────────────────────────────────────────────────────────
   */

  /**
   * The id → name resolver `dragAnnouncements` requires. Its fallback is
   * deliberately a phrase rather than the id: an id that has fallen out of the
   * payload mid-drag is a bug, and reading it aloud helps nobody.
   *
   * ⚠ **One resolver for both drags** (§6.7): dnd-kit's announcements are a
   * single `Announcements` object on a single `DndContext`, so this has to
   * answer for a list name *and* a card title. One pass over `contents.lists`
   * does both — a list matches by its own id, a card by an id inside it.
   *
   * Two consequences worth writing down:
   *
   *   − the fallback is `'an item'`, not C2's `'a list'`. There is now no
   *     honest way to call an unresolvable id a list, and announcing the wrong
   *     noun is worse than announcing a vague one.
   *   − a card whose title is legally empty on the wire is announced as
   *     `UNTITLED_CARD_NAME` — the same string `CardTile` renders and the same
   *     string its accessible name carries, so what is heard during the drag
   *     matches what is heard before and after it.
   */
  const describeDragItem = useCallback(
    (id: string): string => {
      for (const entry of lists) {
        if (entry.id === id) return entry.title;
        const card = entry.cards.find((candidate) => candidate.id === id);
        if (card !== undefined) {
          return cardDisplayTitle(t, card.title);
        }
      }
      return t('boardFace.drag.item');
    },
    [lists, t],
  );

  const announcements = useMemo(
    () => dragAnnouncements(t, describeDragItem),
    [describeDragItem, t],
  );

  /**
   * A drag begins: close whatever composer was open.
   *
   * An open gap composer is a real 292px `<li>` sitting between two columns,
   * and leaving it there during a drag puts a non-sortable column inside the
   * strip the member is rearranging. Safe to do here rather than racing
   * dnd-kit's measurement: `onDragStart` is invoked inside dnd-kit's own
   * `unstable_batchedUpdates` alongside its `DragStart` dispatch, so this
   * state change commits in the same render as `dragging` becoming true, and
   * the droppable rects (`MeasuringStrategy.WhileDragging`) are measured in
   * effects that run after that commit. Verified in
   * `node_modules/@dnd-kit/core/dist/core.esm.js`, not recalled.
   */
  function handleDragStart(event: DragStartEvent) {
    setOpenComposer(null);
    // `C3` §6.7 item 7: a drag closes BOTH. An open quick-edit panel is a
    // taller `<li>` in the middle of the list being rearranged, and its own
    // text field would otherwise still hold focus while the board moves under
    // it.
    setQuickEditCardId(null);
    // Belt-and-braces against a preview that somehow outlived its own drop:
    // a new drag always starts from the arrangement in the payload.
    setCardPreview(null);
    setActiveCardId(isCardDrag(event.active) ? String(event.active.id) : null);
  }

  /**
   * ⚠ `C3` §12.12 — WITHOUT THIS, THE CROSS-LIST GAP NEVER OPENS.
   *
   * The card-only half of dnd-kit v6's multiple-containers pattern: while the
   * pointer is over a *different* list, this file moves the card into that
   * list's array so `SortableContext` there has a real `activeIndex` and its
   * strategy can displace the cards below it. Within one list nothing happens
   * here at all — `verticalListSortingStrategy` already opens that gap by
   * transform, and a splice underneath it would fight the animation.
   *
   * ⚠ It writes `cardPreview` and NOTHING ELSE. `contents.lists` is untouched,
   * which is what keeps `handleCardDrop`'s call to `cardNeighboursForDrop`
   * legal (the header's §12.9 note).
   *
   * ⚠ And it READS `previewLists`, not `lists`, to place the gap — see the
   * long note at the index computation. §12.9 forbids the preview reaching
   * `cardNeighboursForDrop`; it does not forbid this function measuring
   * against what is on screen, and measuring against anything else makes the
   * gap a fixed point at the column-entry slot.
   */
  function handleDragOver(event: DragOverEvent) {
    const { active, over } = event;
    // ⚠ The list drag falls straight through: C2 shipped no `onDragOver` and
    // must keep behaving as though there still is none.
    if (!isCardDrag(active)) return;
    if (over === null) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    // Over its own previewed slot. `resolveDropListId` would answer with the
    // destination it is already in, and recomputing an index against an array
    // that contains the card would be off by one — there is simply nothing to
    // change.
    if (overId === activeId) return;

    // ⚠ `lists`, not `previewLists`: the source list is a fact about the board
    // before this drag, and the preview is what is being computed.
    const sourceListId =
      lists.find((entry) => entry.cards.some((card) => card.id === activeId))?.id ?? null;
    if (sourceListId === null) return;

    // `previewLists` for the LOOKUP, because "which list is this id in" must
    // reflect what the member is currently looking at.
    const toListId = resolveDropListId(overId, previewLists);
    if (toListId === null) return;

    if (toListId === sourceListId) {
      // Back home. Dropping the preview puts the card back in its own list,
      // where the sortable strategy takes over again.
      setCardPreview(null);
      return;
    }

    /*
     * ⚠ THE INDEX IS MEASURED AGAINST THE ARRANGEMENT THE MEMBER IS LOOKING AT
     * — `previewLists` — and then expressed as an index into the destination's
     * PRE-DRAG array, which is the same array minus the card being dragged.
     * `CardPreview.index` is a splice index into the pre-drag array (that is
     * what `applyCardPreview` does with it); it is *derived* here from the
     * previewed one, because `overId` was resolved by dnd-kit against the
     * previewed DOM and means nothing in the pre-drag one.
     *
     * ⚠ Looking `overId` up in the PRE-DRAG array instead — which is what this
     * did until integration — is not "one slot off", it is a **fixed point**:
     * once the gap opens at slot `i`, every destination card below it is
     * pushed down by a card height, so the card now sitting under the pointer
     * is the one that was at `i`, which maps back to `i`, which re-opens the
     * gap at `i`. The drop position then depends on where the pointer CROSSED
     * INTO the column rather than on where it ended up, and the member watches
     * the gap refuse to follow their pointer for a whole card's travel. Both
     * `FR-BOARD-025` AC1 tests failed on exactly that, in both input modes.
     *
     * Reading it off the previewed array makes the direction test disappear
     * rather than adding one: `withoutActive` is `previewed` minus the active
     * card, so for a card already previewed here
     * `withoutActive.indexOf(overId)` is `overIdx` when the member is moving
     * UP past it and `overIdx - 1` when moving DOWN — and "insert after" adds
     * one to the second. Both collapse to `overIdx`.
     */
    const previewedDestination = previewLists.find((entry) => entry.id === toListId);
    if (previewedDestination === undefined) return;
    const previewedIds = previewedDestination.cards.map((card) => card.id);
    const withoutActive = previewedIds.filter((id) => id !== activeId);
    const previewedOverIndex = previewedIds.indexOf(overId);

    let index: number;
    if (previewedOverIndex === -1) {
      // Not a card in this list — the column droppable itself, which
      // `card-drag` resolves for an empty list and for the space past the
      // last card. Both mean the bottom.
      index = withoutActive.length;
    } else if (previewedIds.includes(activeId)) {
      // Already previewed here; see the note above for why this is the whole
      // rule with no direction modifier.
      index = previewedOverIndex;
    } else {
      /*
       * The card is entering this list for the first time, so there is no
       * previewed slot to compare against and the geometry has to answer
       * instead: above the card under the pointer, unless the dragged card's
       * own rect has already cleared its bottom. dnd-kit's own
       * multiple-containers recipe uses this exact test (`translated.top >
       * over.top + over.height`); it is used only on this one frame, after
       * which the branch above takes over.
       */
      const translated = active.rect.current.translated;
      const below =
        translated !== null && translated.top > over.rect.top + over.rect.height;
      index = withoutActive.indexOf(overId) + (below ? 1 : 0);
    }

    // dnd-kit fires `onDragOver` on every pointer move once `over` is settled;
    // writing the same value back would re-render the whole strip each time.
    setCardPreview((current) =>
      current !== null &&
      current.cardId === activeId &&
      current.toListId === toListId &&
      current.index === index
        ? current
        : { cardId: activeId, toListId, index },
    );
  }

  /**
   * Escape, and any other cancellation — `FR-BOARD-025` AC2.
   *
   * ⚠ C2 deliberately passed no `onDragCancel` and said so; that argument
   * stopped holding the moment this file started holding a preview. dnd-kit
   * still returns the card on its own — what it cannot do is close a gap this
   * file opened in another list.
   */
  function handleDragCancel() {
    setCardPreview(null);
    setActiveCardId(null);
  }

  function shouldQueueOfflineCardMove(): boolean {
    return mobileBoardViewport && browserIsOffline();
  }

  function queueOfflineCardMove(variables: MoveCardVariables, title: string): void {
    setOfflineMoves((current) => [
      ...current,
      {
        clientOperationId: createOfflineOperationId(),
        cardId: variables.cardId,
        fromListId: variables.fromListId,
        toListId: variables.toListId,
        previousCardId: variables.previousCardId,
        nextCardId: variables.nextCardId,
        queuedAt: new Date().toISOString(),
      },
    ]);
    announce(t('boardFace.offline.queuedMove', { title }));
  }

  /**
   * The card drop — `FR-BOARD-025` AC1, `FR-BOARD-024`.
   *
   * ⚠ Everything here is computed against `lists`, the untouched prop. See the
   * header: that is the whole of how `onDragOver`'s preview is kept from
   * corrupting this function's source of truth.
   */
  function handleCardDrop(active: DragEndEvent['active'], over: DragEndEvent['over']) {
    const preview = cardPreview;
    setActiveCardId(null);

    /** No request is going out, so nothing else will retire the preview. */
    function abandon() {
      setCardPreview(null);
    }

    // Dropped over nothing at all. dnd-kit returns the card; the gap closes.
    if (over === null) {
      abandon();
      return;
    }

    const activeId = String(active.id);
    const sourceList = lists.find((entry) =>
      entry.cards.some((card) => card.id === activeId),
    );
    if (sourceList === undefined) {
      abandon();
      return;
    }

    let overId = String(over.id);
    let toListId: string | null;

    if (overId === activeId) {
      /*
       * ⚠ The second half of §12.9's trap. The preview has put the card into
       * list B, so the card's own `<li>` is the thing under the pointer and
       * dnd-kit reports `over.id === active.id`. Handed straight on, that is
       * `cardNeighboursForDrop`'s documented "dropped back on itself" no-op
       * and the cross-list move vanishes with nothing red.
       *
       * Translate it back into the pre-drag world instead: in the preview the
       * card sits immediately ABOVE whatever follows it, so that id is the
       * `overId` the pre-drag array wants. With nothing after it, the
       * destination list's own id is — which `cardNeighboursForDrop` already
       * reads as "the bottom", including for an empty list.
       */
      if (preview?.cardId !== activeId) {
        // No preview: this really is a same-list drop on the card's own slot.
        abandon();
        return;
      }
      toListId = preview.toListId;
      if (toListId === sourceList.id) {
        abandon();
        return;
      }
      const destination = previewLists.find((entry) => entry.id === toListId);
      const at = destination?.cards.findIndex((card) => card.id === activeId) ?? -1;
      const below = at === -1 ? undefined : destination?.cards[at + 1]?.id;
      overId = below ?? toListId;
    } else {
      // ⚠ `lists`, not `previewLists`. Only the active card's membership can
      // differ between the two, and `overId` is not the active card here — so
      // the two agree, and using the prop keeps the pre-drag rule uniform.
      toListId = resolveDropListId(overId, lists);
    }

    if (toListId === null) {
      abandon();
      return;
    }

    if (selectedCardIds.has(activeId) && orderedSelectedCardIds.length > 1) {
      const destination = lists.find((entry) => entry.id === toListId);
      if (destination === undefined) {
        abandon();
        return;
      }

      const neighbours = bulkDropNeighbours(destination.cards, selectedCardIds, overId);
      bulkMoveCards.mutate(
        {
          boardId: board.id,
          destinationBoardId: board.id,
          cardIds: orderedSelectedCardIds,
          listId: toListId,
          previousCardId: neighbours.previousCardId,
          nextCardId: neighbours.nextCardId,
          destinationListTitle: destination.title,
        },
        {
          onSuccess: () => {
            clearCardSelection();
          },
          onSettled: abandon,
        },
      );
      return;
    }

    if (toListId !== sourceList.id && collapsedListIds?.has(toListId) === true) {
      const destination = lists.find((entry) => entry.id === toListId);
      const nextCardId =
        destination?.cards.find((card) => !isOptimisticCardId(card.id))?.id ?? null;
      const rawTitle = sourceList.cards
        .find((card) => card.id === activeId)
        ?.title.trim();
      const title = cardDisplayTitle(t, rawTitle ?? '');

      onListCollapseChange?.(toListId, false);
      const variables: MoveCardVariables = {
        cardId: activeId,
        fromListId: sourceList.id,
        toListId,
        previousCardId: null,
        nextCardId,
      };
      if (shouldQueueOfflineCardMove()) {
        queueOfflineCardMove(variables, title);
        abandon();
        return;
      }

      locallyMovingCardIdsRef.current.add(activeId);
      moveCard.mutate(variables, {
        onSuccess: () => {
          announce(
            t('boardFace.card.movedToList', {
              title,
              list: destination?.title ?? t('boardFace.card.destinationFallback'),
            }),
          );
          // `FR-EXP-033` AC2 — the drag the welcome tip asked for.
          spendWelcomeTip(sourceList.id, toListId);
          void undoRedo.recordSource({
            boardId: board.id,
            cardId: activeId,
            scope: { kind: 'card', cardId: activeId },
            eventTypes: ['card.moved_between_lists'],
            label: t('undo.source.cardMoved', { title }),
          });
        },
        onSettled: () => {
          locallyMovingCardIdsRef.current.delete(activeId);
        },
      });
      return;
    }

    const neighbours = cardNeighboursForDrop({
      // ⚠ THE PRE-DRAG ARRANGEMENT, with `activeId` still under its source
      // list. `card-drag.ts` documents this as a precondition and names this
      // file as the one that has to honour it.
      lists,
      activeId,
      overId,
      toListId,
    });

    /*
     * ⚠ TWO NULLS, OPPOSITE HANDLING (brief §3.2 / §3.4), and this is the
     * likeliest silent bug in the chunk:
     *
     *   − `null` from this function is a NO-OP. Send nothing at all; the
     *     server's byte-identical response is the backstop, not the mechanism.
     *   − `{ previousCardId: null, nextCardId: null }` is the BOTTOM of the
     *     destination list, which is how a card lands in an EMPTY one. It is a
     *     legal body and it IS sent.
     *
     * (For a LIST that same pair is a 422 and is dropped a few lines below.
     * The two routes read it differently; cards are the ones where both-null
     * means something.)
     */
    if (neighbours === null) {
      abandon();
      return;
    }

    // The preview is deliberately NOT cleared here — the optimistic cache
    // write takes over the same fact one microtask later, and the effect above
    // retires the preview when it lands. See the header.
    const destination = lists.find((entry) => entry.id === toListId);
    const rawTitle = sourceList.cards.find((card) => card.id === activeId)?.title.trim();
    const title = cardDisplayTitle(t, rawTitle ?? '');
    const variables: MoveCardVariables = {
      cardId: activeId,
      fromListId: sourceList.id,
      toListId,
      previousCardId: neighbours.previousCardId,
      nextCardId: neighbours.nextCardId,
    };
    if (shouldQueueOfflineCardMove()) {
      queueOfflineCardMove(variables, title);
      abandon();
      return;
    }

    locallyMovingCardIdsRef.current.add(activeId);
    moveCard.mutate(variables, {
      onSuccess: () => {
        announce(
          toListId === sourceList.id
            ? t('boardFace.card.movedInList', { title, list: sourceList.title })
            : t('boardFace.card.movedToList', {
                title,
                list: destination?.title ?? t('boardFace.card.destinationFallback'),
              }),
        );
        // `FR-EXP-033` AC2. A no-op unless the card actually changed list, and
        // unless this is the caller's own unspent welcome board.
        spendWelcomeTip(sourceList.id, toListId);
        void undoRedo.recordSource({
          boardId: board.id,
          cardId: activeId,
          scope: { kind: 'card', cardId: activeId },
          eventTypes:
            toListId === sourceList.id
              ? ['card.position_changed']
              : ['card.moved_between_lists'],
          label: t('undo.source.cardMoved', { title }),
        });
      },
      onSettled: () => {
        locallyMovingCardIdsRef.current.delete(activeId);
      },
    });
  }

  /**
   * The drop — `FR-BOARD-011` AC1 for a list, `FR-BOARD-025` AC1 for a card.
   *
   * ⚠ The card branch returns before the list body, which is C2's, unchanged.
   */
  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (isCardDrag(active)) {
      handleCardDrop(active, over);
      return;
    }

    // Dropped outside the strip — `FR-BOARD-011` AC2's second half. Nothing is
    // sent and dnd-kit returns the list.
    if (over === null) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    // Dropped where it started. `neighboursForMove` answers null for this too;
    // returning here says so at the call site rather than relying on it.
    if (activeId === overId) return;

    const orderedIds = lists.map((list) => list.id);
    const neighbours = neighboursForMove(orderedIds, activeId, overId, {
      // ⚠ Brief §5.2: a placeholder may never be RETURNED as a neighbour. This
      // is the caller's half of that gate — `list-drag.ts` cannot know which
      // ids are optimistic.
      excludeIds: orderedIds.filter(isOptimisticListId),
    });
    if (neighbours === null) return;

    /*
     * ⚠ Both-null is a 422 on the position route (brief §3.2: a drag always
     * lands somewhere, so the pair is refused as ambiguous — unlike `POST
     * /lists`, where both-null means "append at the far right"). It cannot
     * arise from two real lists, but a strip that is mostly placeholders can
     * exclude its way to it, and a 422 shown as a failed drag would be this
     * file's bug rather than the server's.
     */
    if (neighbours.previousId === null && neighbours.nextId === null) return;

    /*
     * FE-80.1 display-name fallbacks — ⚠ a GUARD, not a `?? 'List'`.
     *
     * This was `lists.find(…)?.title ?? 'List'`, and the English word reached
     * BOTH `boardFace.list.moved` and `undo.source.listMoved` — an untranslated
     * noun inside two otherwise-routed sentences. It was never routed, because
     * the arm it defends CANNOT FIRE: `neighboursForMove` (`lib/list-drag.ts`,
     * the `!orderedIds.includes(activeId)` refusal) has already returned null
     * for an id that is not in `orderedIds`, and `orderedIds` is
     * `lists.map(…)` from THIS binding, in THIS synchronous handler, seventeen
     * lines up. A key whose only consumer is unreachable is dead weight and,
     * worse, admits no assertion that could ever have been red — the register's
     * R4/R6/R12 ruling. So the impossible case says so and returns.
     *
     * The neighbouring blank-title case is refused a layer down and needs no
     * fallback either: `listTitleSchema` is `z.string().trim().min(1)` on both
     * create and rename (`FR-BOARD-009`'s edge case, `FR-BOARD-010` AC3), so a
     * whitespace-only list title cannot be stored.
     */
    const activeList = lists.find((list) => list.id === activeId);
    if (activeList === undefined) return;
    const listTitle = activeList.title;
    moveList.mutate(
      {
        listId: activeId,
        previousListId: neighbours.previousId,
        nextListId: neighbours.nextId,
      },
      {
        onSuccess: () => {
          announce(t('boardFace.list.moved', { title: listTitle }));
          void undoRedo.recordSource({
            boardId: board.id,
            scope: { kind: 'board', boardId: board.id },
            eventTypes: ['list.position_changed'],
            sourceKey: listUndoSourceKey(activeId),
            label: t('undo.source.listMoved', { title: listTitle }),
          });
        },
      },
    );
  }

  /*
   * ── The composers ─────────────────────────────────────────────────────
   */

  /**
   * `FR-BOARD-009` AC3's one entry point. The gap's double-click zone
   * (`SortableListColumn`) and `ListHeader`'s `Add a list to the right` menu
   * item — the keyboard equivalent `FR-EXP-022` requires — both call this.
   *
   * "After the last list" is the far-right end, which the trailing composer
   * already is and already sits immediately right of that column. Routing it
   * there rather than opening a second composer beside it keeps one affordance
   * on screen for one insertion point — the presentational class of thing §7.4
   * says a role query cannot see.
   */
  const handleInsertListAfter = useCallback(
    (listId: string) => {
      const isLast = lists.at(-1)?.id === listId;
      setOpenComposer(isLast ? { kind: 'end' } : { kind: 'gap', afterListId: listId });
    },
    [lists],
  );

  const handleEndOpenChange = useCallback((open: boolean) => {
    setOpenComposer(open ? { kind: 'end' } : null);
  }, []);

  const handleGapOpenChange = useCallback((open: boolean) => {
    // A gap composer is only ever mounted while it is the open one, so the
    // only transition it can report is "closed".
    if (!open) setOpenComposer(null);
  }, []);

  /*
   * ── The card composers and quick edit (`FE-12.1`, `FE-12.2`, §6.7 item 7) ─
   */

  /**
   * One column reports which of its two composers it wants open.
   *
   * ⚠ The functional update is not decoration: `ListColumn` reports for its
   * own column only, and a bare `setOpenComposer(null)` would let a column
   * closing a composer it no longer owns close somebody else's. Closing is
   * therefore conditional on the open composer actually being this column's.
   */
  const handleCardComposerChange = useCallback(
    (listId: string, placement: 'bottom' | 'top' | null) => {
      setOpenComposer((current) => {
        if (placement !== null) return { kind: 'card', listId, placement };
        return current?.kind === 'card' && current.listId === listId ? null : current;
      });
      // Opening either closes the other.
      if (placement !== null) setQuickEditCardId(null);
    },
    [],
  );

  const handleQuickEditChange = useCallback((cardId: string | null) => {
    setQuickEditCardId(cardId);
    if (cardId !== null) setOpenComposer(null);
  }, []);

  const handleArchiveCardFront = useCallback(
    (card: BoardFaceList['cards'][number], listId: string) => {
      const title = cardDisplayTitle(t, card.title);
      archiveCard.mutate(
        { cardId: card.id, listId, title },
        {
          onSuccess: () => {
            announce(t('boardFace.card.archived', { title }));
            void undoRedo.recordSource({
              boardId: board.id,
              cardId: card.id,
              scope: { kind: 'card', cardId: card.id },
              eventTypes: ['card.archived'],
              label: t('undo.source.cardArchived', { title }),
            });
          },
        },
      );
    },
    [archiveCard, board.id, t, undoRedo],
  );

  const handleRemoveMirror = useCallback(
    (mirror: CardMirror) => {
      const title = cardDisplayTitle(t, mirror.title);
      deleteCardMirror.mutate({ mirrorId: mirror.id, title });
    },
    [deleteCardMirror, t],
  );

  const handleConvertLinkCard = useCallback(
    (card: BoardFaceLinkCardSurface) => {
      const title = cardDisplayTitle(t, card.title);
      convertLinkCard.mutate({ cardId: card.id, title });
    },
    [convertLinkCard, t],
  );

  const handleToggleCardCompleted = useCallback(
    (card: BoardFaceList['cards'][number]) => {
      setCardCompleted.mutate(
        { cardId: card.id, boardId: board.id, completed: !card.completed },
        {
          onSuccess: (detail) => {
            announce(
              t(
                detail.completed
                  ? 'boardFace.card.markedComplete'
                  : 'boardFace.card.markedIncomplete',
              ),
            );
          },
        },
      );
    },
    [board.id, setCardCompleted, t],
  );

  const refreshCalendar = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.boards.calendar(board.id) });
  }, [board.id, queryClient]);

  const refreshTimeline = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.boards.timeline(board.id) });
  }, [board.id, queryClient]);

  const refreshTable = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.boards.table(board.id) });
  }, [board.id, queryClient]);

  const refreshMap = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.boards.map(board.id) });
    void queryClient.invalidateQueries({ queryKey: queryKeys.boards.contents(board.id) });
  }, [board.id, queryClient]);

  const refreshTableCard = useCallback(
    (cardId: string) => {
      refreshTable();
      void queryClient.invalidateQueries({ queryKey: queryKeys.cards.detail(cardId) });
    },
    [queryClient, refreshTable],
  );

  const handleTableRenameCard = useCallback(
    (cardId: string, title: string) => {
      const listId = boardTable.data?.rows.find((row) => row.card.id === cardId)?.list.id;
      if (listId === undefined) return;
      renameCard.mutate(
        { cardId, listId, title },
        {
          onSuccess: () => {
            announce(t('boardFace.card.renamed', { title }));
            refreshTableCard(cardId);
          },
        },
      );
    },
    [boardTable.data, refreshTableCard, renameCard],
  );

  const handleTableMoveCard = useCallback(
    (intent: BoardTableMoveIntent) => {
      const neighbours =
        boardTable.data === undefined
          ? { previousCardId: null, nextCardId: null }
          : tableMoveNeighbours(
              boardTable.data.rows,
              intent.cardId,
              intent.toListId,
              intent.beforeCardId,
            );
      moveCard.mutate(
        {
          cardId: intent.cardId,
          fromListId:
            intent.fromListId !== ''
              ? intent.fromListId
              : (boardTable.data?.rows.find((row) => row.card.id === intent.cardId)?.list
                  .id ?? intent.toListId),
          toListId: intent.toListId,
          previousCardId: neighbours.previousCardId,
          nextCardId: neighbours.nextCardId,
        },
        {
          onSuccess: () => {
            announce(t('boardFace.table.cardMoved'));
            refreshTableCard(intent.cardId);
          },
        },
      );
    },
    [boardTable.data, moveCard, refreshTableCard],
  );

  const handleTableCreateCard = useCallback(
    async (input: { title: string; listId: string }) => {
      await createCard.mutateAsync({
        listId: input.listId,
        title: input.title,
        previousCardId: null,
        nextCardId: null,
      });
      announce(t('boardFace.table.cardCreated', { title: input.title }));
      refreshTable();
    },
    [createCard, refreshTable, t],
  );

  const handleTableSetDueDate = useCallback(
    (cardId: string, dueAt: string | null) => {
      updateCardDueDate.mutate(
        { cardId, boardId: board.id, dueAt },
        {
          onSuccess: () => {
            announce(
              t(
                dueAt === null
                  ? 'boardFace.dueDate.removed'
                  : 'boardFace.dueDate.updated',
              ),
            );
            refreshTableCard(cardId);
          },
        },
      );
    },
    [board.id, refreshTableCard, t, updateCardDueDate],
  );

  const handleTableAddLabel = useCallback(
    (cardId: string, labelId: string) => {
      applyCardLabel.mutate(
        { cardId, boardId: board.id, labelId },
        { onSuccess: () => refreshTableCard(cardId) },
      );
    },
    [applyCardLabel, board.id, refreshTableCard],
  );

  const handleTableRemoveLabel = useCallback(
    (cardId: string, labelId: string) => {
      removeCardLabel.mutate(
        { cardId, boardId: board.id, labelId },
        { onSuccess: () => refreshTableCard(cardId) },
      );
    },
    [board.id, refreshTableCard, removeCardLabel],
  );

  const handleTableAddMember = useCallback(
    (cardId: string, userId: string) => {
      addCardMember.mutate(
        { cardId, boardId: board.id, userId },
        { onSuccess: () => refreshTableCard(cardId) },
      );
    },
    [addCardMember, board.id, refreshTableCard],
  );

  const handleTableRemoveMember = useCallback(
    (cardId: string, userId: string) => {
      removeCardMember.mutate(
        { cardId, boardId: board.id, userId },
        { onSuccess: () => refreshTableCard(cardId) },
      );
    },
    [board.id, refreshTableCard, removeCardMember],
  );

  const handleCalendarCreate = useCallback(
    async (input: { listId: string; title: string; dateKey: string }) => {
      const card = await createCard.mutateAsync({
        listId: input.listId,
        title: input.title,
        previousCardId: null,
        nextCardId: null,
      });
      await updateCardDueDate.mutateAsync({
        cardId: card.id,
        boardId: board.id,
        dueAt: dueDateForCalendarDate(input.dateKey, t),
      });
      announce(t('boardFace.calendar.cardCreated', { title: input.title }));
      refreshCalendar();
      setCalendarCreateDate(null);
    },
    [board.id, createCard, refreshCalendar, t, updateCardDueDate],
  );

  const handleCalendarReschedule = useCallback(
    async (intent: BoardCalendarRescheduleIntent) => {
      const patch = boardCalendarReschedulePatch(intent.entry, intent.toDateKey, t);
      if (patch === null) return;
      if (patch.startAt !== intent.entry.startAt) {
        await updateCardStartDate.mutateAsync({
          cardId: intent.cardId,
          boardId: board.id,
          startAt: patch.startAt,
        });
      }
      if (patch.dueAt !== intent.entry.dueAt) {
        await updateCardDueDate.mutateAsync({
          cardId: intent.cardId,
          boardId: board.id,
          dueAt: patch.dueAt,
          // BE-44.7: a calendar drag moves the date, never the kind — an
          // all-day card stays all-day on its new day.
          dueAllDay: patch.dueAllDay,
        });
      }
      announce(
        t('boardFace.calendar.rescheduled', {
          title: cardDisplayTitle(t, intent.entry.card.title),
        }),
      );
      refreshCalendar();
    },
    [board.id, refreshCalendar, t, updateCardDueDate, updateCardStartDate],
  );

  const handleCalendarResize = useCallback(
    async (intent: BoardCalendarResizeIntent) => {
      const patch = boardCalendarResizePatch(
        intent.entry,
        intent.edge,
        intent.toDateKey,
        t,
      );
      if (patch === null) {
        announce(t('boardFace.calendar.rangeRefused'));
        return;
      }
      if (patch.startAt !== intent.entry.startAt) {
        await updateCardStartDate.mutateAsync({
          cardId: intent.cardId,
          boardId: board.id,
          startAt: patch.startAt,
        });
      }
      if (patch.dueAt !== intent.entry.dueAt) {
        await updateCardDueDate.mutateAsync({
          cardId: intent.cardId,
          boardId: board.id,
          dueAt: patch.dueAt,
          // BE-44.7: a calendar resize moves the date, never the kind.
          dueAllDay: patch.dueAllDay,
        });
      }
      announce(
        t('boardFace.calendar.rangeUpdated', {
          title: cardDisplayTitle(t, intent.entry.card.title),
        }),
      );
      refreshCalendar();
    },
    [board.id, refreshCalendar, t, updateCardDueDate, updateCardStartDate],
  );

  const applyTimelineDatePatch = useCallback(
    async (
      cardId: string,
      previous: { readonly startAt: string | null; readonly dueAt: string | null },
      patch: { readonly startAt: string | null; readonly dueAt: string | null },
      // BE-44.7: the board-face card's current flag — a timeline move shifts
      // the date, never the kind. `undefined` (card not on this face) omits
      // it and keeps instant semantics, the back-compatible default.
      dueAllDay: boolean | undefined,
    ) => {
      if (patch.startAt !== previous.startAt) {
        await updateCardStartDate.mutateAsync({
          cardId,
          boardId: board.id,
          startAt: patch.startAt,
        });
      }
      if (patch.dueAt !== previous.dueAt) {
        await updateCardDueDate.mutateAsync({
          cardId,
          boardId: board.id,
          dueAt: patch.dueAt,
          ...(dueAllDay === undefined ? {} : { dueAllDay }),
        });
      }
    },
    [board.id, updateCardDueDate, updateCardStartDate],
  );

  const findTimelineBoardCard = useCallback(
    (cardId: string) => {
      for (const list of offlineContents.lists) {
        const card = list.cards.find((candidate) => candidate.id === cardId);
        if (card !== undefined) return { list, card };
      }
      return null;
    },
    [offlineContents.lists],
  );

  const handleTimelineMoveToLane = useCallback(
    async (intent: BoardTimelineLaneIntent) => {
      const current = findTimelineBoardCard(intent.cardId);
      if (current === null) return;

      if (intent.lane.kind === 'list') {
        if (current.list.id === intent.lane.list.id) return;
        await moveCard.mutateAsync({
          cardId: intent.cardId,
          fromListId: current.list.id,
          toListId: intent.lane.list.id,
          previousCardId: null,
          nextCardId: null,
        });
        announce(
          t('boardFace.timeline.movedToLane', {
            title: cardDisplayTitle(t, intent.entry.card.title),
            lane: intent.lane.title,
          }),
        );
        refreshTimeline();
        return;
      }

      if (intent.lane.kind === 'member') {
        const laneMember = intent.lane.member;
        if (laneMember === null) {
          const sourceMember =
            intent.fromLane.kind === 'member' ? intent.fromLane.member : null;
          if (
            sourceMember !== null &&
            current.card.members.some((member) => member.id === sourceMember.id)
          ) {
            await removeCardMember.mutateAsync({
              cardId: intent.cardId,
              boardId: board.id,
              userId: sourceMember.id,
            });
          }
        } else if (!current.card.members.some((member) => member.id === laneMember.id)) {
          await addCardMember.mutateAsync({
            cardId: intent.cardId,
            boardId: board.id,
            userId: laneMember.id,
          });
        }
        refreshTimeline();
        return;
      }

      if (intent.lane.kind === 'label') {
        const laneLabel = intent.lane.label;
        if (laneLabel === null) {
          const sourceLabel =
            intent.fromLane.kind === 'label' ? intent.fromLane.label : null;
          if (
            sourceLabel !== null &&
            current.card.labels.some((label) => label.id === sourceLabel.id)
          ) {
            await removeCardLabel.mutateAsync({
              cardId: intent.cardId,
              boardId: board.id,
              labelId: sourceLabel.id,
            });
          }
        } else if (!current.card.labels.some((label) => label.id === laneLabel.id)) {
          await applyCardLabel.mutateAsync({
            cardId: intent.cardId,
            boardId: board.id,
            labelId: laneLabel.id,
          });
        }
        refreshTimeline();
      }
    },
    [
      addCardMember,
      applyCardLabel,
      board.id,
      findTimelineBoardCard,
      moveCard,
      refreshTimeline,
      removeCardLabel,
      removeCardMember,
    ],
  );

  const handleTimelineMoveBar = useCallback(
    async (intent: BoardTimelineMoveIntent) => {
      const patch = boardTimelineMovePatch(intent.entry, intent.toDateKey);
      if (patch === null) return;
      await applyTimelineDatePatch(
        intent.cardId,
        intent.entry,
        patch,
        findTimelineBoardCard(intent.cardId)?.card.dueAllDay,
      );
      announce(
        t('boardFace.timeline.moved', {
          title: cardDisplayTitle(t, intent.entry.card.title),
        }),
      );
      refreshTimeline();
    },
    [applyTimelineDatePatch, findTimelineBoardCard, refreshTimeline, t],
  );

  const handleTimelineResizeBar = useCallback(
    async (intent: BoardTimelineResizeIntent) => {
      const patch = boardTimelineResizePatch(intent.entry, intent.edge, intent.toDateKey);
      if (patch === null) {
        announce(t('boardFace.timeline.rangeRefused'));
        return;
      }
      await applyTimelineDatePatch(
        intent.cardId,
        intent.entry,
        patch,
        findTimelineBoardCard(intent.cardId)?.card.dueAllDay,
      );
      announce(
        t('boardFace.timeline.rangeUpdated', {
          title: cardDisplayTitle(t, intent.entry.card.title),
        }),
      );
      refreshTimeline();
    },
    [applyTimelineDatePatch, findTimelineBoardCard, refreshTimeline, t],
  );

  const handleTimelineScheduleCard = useCallback(
    async (intent: BoardTimelineScheduleIntent) => {
      const current = findTimelineBoardCard(intent.cardId);
      const patch = boardTimelineSchedulePatch(intent.dateKey, timelineZoom);
      const laneListId = timelineLaneListId(intent.lane);
      if (laneListId !== null && laneListId !== intent.fromListId) {
        await moveCard.mutateAsync({
          cardId: intent.cardId,
          fromListId: intent.fromListId,
          toListId: laneListId,
          previousCardId: null,
          nextCardId: null,
        });
      }
      await applyTimelineDatePatch(
        intent.cardId,
        { startAt: null, dueAt: null },
        patch,
        current?.card.dueAllDay,
      );
      if (current !== null && intent.lane.kind === 'member') {
        const laneMember = intent.lane.member;
        if (laneMember === null) {
          await Promise.all(
            current.card.members.map((member) =>
              removeCardMember.mutateAsync({
                cardId: intent.cardId,
                boardId: board.id,
                userId: member.id,
              }),
            ),
          );
        } else if (!current.card.members.some((member) => member.id === laneMember.id)) {
          await addCardMember.mutateAsync({
            cardId: intent.cardId,
            boardId: board.id,
            userId: laneMember.id,
          });
        }
      }
      if (current !== null && intent.lane.kind === 'label') {
        const laneLabel = intent.lane.label;
        if (laneLabel === null) {
          await Promise.all(
            current.card.labels.map((label) =>
              removeCardLabel.mutateAsync({
                cardId: intent.cardId,
                boardId: board.id,
                labelId: label.id,
              }),
            ),
          );
        } else if (!current.card.labels.some((label) => label.id === laneLabel.id)) {
          await applyCardLabel.mutateAsync({
            cardId: intent.cardId,
            boardId: board.id,
            labelId: laneLabel.id,
          });
        }
      }
      announce(t('boardFace.timeline.scheduled'));
      refreshTimeline();
    },
    [
      addCardMember,
      applyTimelineDatePatch,
      applyCardLabel,
      board.id,
      findTimelineBoardCard,
      moveCard,
      refreshTimeline,
      removeCardLabel,
      removeCardMember,
      timelineZoom,
    ],
  );

  const handleMoveCardFront = useCallback(
    (card: BoardFaceList['cards'][number], listId: string) => {
      setMoveResult(null);
      setMoveTarget({ mode: 'card', card, listId });
      setCopyListTarget(null);
      setCopyListResult(null);
      setQuickEditCardId(null);
      setOpenComposer(null);
    },
    [],
  );

  const handleCopyCardFront = useCallback(
    (card: BoardFaceList['cards'][number], listId: string) => {
      setCopyResult(null);
      setCopyTarget({ card, listId });
      setQuickEditCardId(null);
      setOpenComposer(null);
      setMoveTarget(null);
      setMoveResult(null);
      setCopyListTarget(null);
      setCopyListResult(null);
    },
    [],
  );

  const handleMoveListOpen = useCallback((list: BoardFaceList) => {
    setMoveResult(null);
    setMoveTarget({ mode: 'list', list });
    setCopyListTarget(null);
    setCopyListResult(null);
    setOpenComposer(null);
    setQuickEditCardId(null);
  }, []);

  const handleCopyListOpen = useCallback((list: BoardFaceList) => {
    setCopyListResult(null);
    setCopyListTarget(list);
    setCopyTarget(null);
    setCopyResult(null);
    setMoveTarget(null);
    setMoveResult(null);
    setOpenComposer(null);
    setQuickEditCardId(null);
  }, []);

  const handleListOperationsOpen = useCallback((list: BoardFaceList) => {
    setListOperationsTarget(list);
    setPasteCardsTarget(null);
    setCopyListTarget(null);
    setCopyListResult(null);
    setCopyTarget(null);
    setCopyResult(null);
    setMoveTarget(null);
    setMoveResult(null);
    setOpenComposer(null);
    setQuickEditCardId(null);
  }, []);

  const handlePasteMany = useCallback(
    (input: {
      text: string;
      listId: string;
      listTitle: string;
      previousCardId: string | null;
      nextCardId: string | null;
    }) => {
      setPasteCardsTarget({ ...input, error: null });
      setListOperationsTarget(null);
      setCopyListTarget(null);
      setCopyListResult(null);
      setCopyTarget(null);
      setCopyResult(null);
      setMoveTarget(null);
      setMoveResult(null);
      setQuickEditCardId(null);
    },
    [],
  );

  const handleMoveCancel = useCallback(() => {
    setMoveTarget(null);
    setMoveResult(null);
  }, []);

  const handleCopyCancel = useCallback(() => {
    setCopyTarget(null);
    setCopyResult(null);
  }, []);

  const handleCopyListCancel = useCallback(() => {
    setCopyListTarget(null);
    setCopyListResult(null);
  }, []);

  const handleListOperationsCancel = useCallback(() => {
    setListOperationsTarget(null);
  }, []);

  const handlePasteCardsCancel = useCallback(() => {
    setPasteCardsTarget(null);
  }, []);

  function handleListSort(mode: ListCardSortBody['mode']): void {
    if (listOperationsTarget === null) return;
    sortListCards.mutate(
      {
        listId: listOperationsTarget.id,
        listTitle: listOperationsTarget.title,
        mode,
      },
      {
        onSuccess: () => setListOperationsTarget(null),
      },
    );
  }

  function handleArchiveAllCards(): void {
    if (listOperationsTarget === null) return;
    archiveAllListCards.mutate(
      {
        listId: listOperationsTarget.id,
        listTitle: listOperationsTarget.title,
        count: listOperationsTarget.openCardCount,
      },
      {
        onSuccess: () => setListOperationsTarget(null),
      },
    );
  }

  function handleMoveAllCards(destinationListId: string): void {
    if (listOperationsTarget === null) return;
    const destination = lists.find((list) => list.id === destinationListId);
    if (destination === undefined) return;
    moveAllListCards.mutate(
      {
        sourceListId: listOperationsTarget.id,
        sourceTitle: listOperationsTarget.title,
        destinationListId,
        destinationTitle: destination.title,
        count: listOperationsTarget.openCardCount,
      },
      {
        onSuccess: () => setListOperationsTarget(null),
      },
    );
  }

  function recordPasteCardsError(error: unknown): void {
    setPasteCardsTarget((current) =>
      current === null
        ? current
        : { ...current, error: pastedCardsErrorMessage(t, error) },
    );
  }

  function handlePasteSingleCard(): void {
    if (pasteCardsTarget === null) return;
    createCard.mutate(
      {
        listId: pasteCardsTarget.listId,
        title: pasteCardsTarget.text,
        previousCardId: pasteCardsTarget.previousCardId,
        nextCardId: pasteCardsTarget.nextCardId,
      },
      {
        onSuccess: (card) => {
          announce(t('boardFace.card.added', { title: cardDisplayTitle(t, card.title) }));
          setPasteCardsTarget(null);
        },
        onError: recordPasteCardsError,
      },
    );
  }

  function handlePasteCardsPerLine(): void {
    if (pasteCardsTarget === null) return;
    createBulkCards.mutate(
      {
        listId: pasteCardsTarget.listId,
        listTitle: pasteCardsTarget.listTitle,
        text: pasteCardsTarget.text,
        previousCardId: pasteCardsTarget.previousCardId,
        nextCardId: pasteCardsTarget.nextCardId,
      },
      {
        onSuccess: () => setPasteCardsTarget(null),
        onError: recordPasteCardsError,
      },
    );
  }

  function announceDashboardMutationError(error: unknown): void {
    /* ⚠ The second conditional pair whose arms differ by a slot — same crash
     * shape as `boardFace.offline.syncFailed*`, same two-fixture rule. */
    announce(
      error instanceof Error
        ? t('boardFace.dashboard.updateFailedWithReason', { reason: error.message })
        : t('boardFace.dashboard.updateFailed'),
    );
  }

  function handleAddDashboardTile(
    input: Parameters<typeof createDashboardTile.mutate>[0],
  ) {
    createDashboardTile.mutate(input, {
      onSuccess: () => announce(t('boardFace.dashboard.tileAdded')),
      onError: announceDashboardMutationError,
    });
  }

  function handleChangeDashboardTileFacet(
    tileId: string,
    facet: Parameters<typeof updateDashboardTileFacet.mutate>[0]['facet'],
  ) {
    updateDashboardTileFacet.mutate(
      { tileId, facet },
      {
        onSuccess: () => announce(t('boardFace.dashboard.tileUpdated')),
        onError: announceDashboardMutationError,
      },
    );
  }

  function handleChangeDashboardTileChartType(
    tileId: string,
    chartType: Parameters<typeof updateDashboardTileChartType.mutate>[0]['chartType'],
  ) {
    updateDashboardTileChartType.mutate(
      { tileId, chartType },
      {
        onSuccess: () => announce(t('boardFace.dashboard.tileUpdated')),
        onError: announceDashboardMutationError,
      },
    );
  }

  function handleRemoveDashboardTile(tileId: string) {
    deleteDashboardTile.mutate(
      { tileId },
      {
        onSuccess: () => announce(t('boardFace.dashboard.tileRemoved')),
        onError: announceDashboardMutationError,
      },
    );
  }

  async function handleMapCreateCard(input: BoardMapCreateIntent): Promise<void> {
    await createCard.mutateAsync({
      listId: input.listId,
      title: input.title,
      previousCardId: null,
      nextCardId: null,
      location: input.location,
    });
    announce(t('boardFace.map.cardCreated', { title: input.title }));
    refreshMap();
  }

  function handleMapOpenCard(intent: BoardMapOpenCardIntent): void {
    handleCardOpen({
      cardId: intent.card.id,
      path: viewCardPath(intent.card),
      stripScrollLeft: 0,
      listScrollTop: 0,
    });
  }

  function handleMapRenameCard(cardId: string, title: string): void {
    const list = offlineContents.lists.find((candidate) =>
      candidate.cards.some((card) => card.id === cardId),
    );
    if (list === undefined) return;
    renameCard.mutate(
      { cardId, listId: list.id, title },
      {
        onSuccess: () => {
          announce(t('boardFace.card.renamed', { title }));
          refreshMap();
        },
      },
    );
  }

  function handleMoveCardSubmit(input: {
    boardId: string;
    listId: string;
    previousCardId: string | null;
    nextCardId: string | null;
    sameBoard: boolean;
  }) {
    if (moveTarget?.mode !== 'card') return;
    const title = cardDisplayTitle(t, moveTarget.card.title);
    if (input.sameBoard) {
      const variables: MoveCardVariables = {
        cardId: moveTarget.card.id,
        fromListId: moveTarget.listId,
        toListId: input.listId,
        previousCardId: input.previousCardId,
        nextCardId: input.nextCardId,
      };
      if (shouldQueueOfflineCardMove()) {
        queueOfflineCardMove(variables, title);
        setMoveTarget(null);
        setMoveResult(null);
        return;
      }

      moveCard.mutate(variables, {
        onSuccess: () => {
          announce(t('boardFace.card.moved', { title }));
          setMoveTarget(null);
          setMoveResult(null);
        },
      });
      return;
    }

    moveCardToBoard.mutate(
      {
        cardId: moveTarget.card.id,
        title,
        sourceBoardId: board.id,
        sourceListId: moveTarget.listId,
        destinationBoardId: input.boardId,
        destinationListId: input.listId,
        previousCardId: input.previousCardId,
        nextCardId: input.nextCardId,
      },
      {
        onSuccess: (result) => {
          announce(t('boardFace.card.moved', { title }));
          setMoveResult({ reconciliation: result.reconciliation });
        },
      },
    );
  }

  function handleMoveCardPreview(input: {
    boardId: string;
    listId: string;
    previousCardId: string | null;
    nextCardId: string | null;
  }): Promise<Reconciliation> {
    if (moveTarget?.mode !== 'card') {
      return Promise.reject(new Error('No card selected for move preview'));
    }
    return queryClient.fetchQuery({
      queryKey: queryKeys.cards.movePreview(
        moveTarget.card.id,
        input.boardId,
        input.listId,
      ),
      queryFn: () =>
        previewCardMoveToBoard(moveTarget.card.id, {
          boardId: input.boardId,
          listId: input.listId,
        }),
    });
  }

  function handleMoveListPreview(input: { boardId: string }): Promise<Reconciliation> {
    if (moveTarget?.mode !== 'list') {
      return Promise.reject(new Error('No list selected for move preview'));
    }
    return queryClient.fetchQuery({
      queryKey: queryKeys.lists.movePreview(moveTarget.list.id, input.boardId),
      queryFn: () =>
        previewListMoveToBoard(moveTarget.list.id, { boardId: input.boardId }),
    });
  }

  function handleMoveListSubmit(input: { boardId: string }) {
    if (moveTarget?.mode !== 'list') return;
    moveListToBoard.mutate(
      {
        listId: moveTarget.list.id,
        title: moveTarget.list.title,
        sourceBoardId: board.id,
        destinationBoardId: input.boardId,
      },
      {
        onSuccess: (result) => {
          announce(t('boardFace.list.moved', { title: moveTarget.list.title }));
          setMoveResult({
            reconciliation: result.reconciliation,
            cardsMoved: result.cardsMoved,
          });
        },
      },
    );
  }

  function handleCopyListSubmit(input: {
    boardId: string;
    previousListId: string | null;
    nextListId: string | null;
    title: string;
    titleEdited: boolean;
  }) {
    if (copyListTarget === null) return;
    const sourceTitle =
      copyListTarget.title.trim() === ''
        ? t('copyMove.untitledList')
        : copyListTarget.title;
    copyList.mutate(
      {
        sourceBoardId: board.id,
        sourceListId: copyListTarget.id,
        sourceTitle,
        destinationBoardId: input.boardId,
        previousListId: input.previousListId,
        nextListId: input.nextListId,
        title: input.titleEdited ? input.title : undefined,
      },
      {
        onSuccess: (result) => {
          announce(t('boardFace.list.copied', { title: result.list.title }));
          setCopyListResult({
            reconciliation: result.reconciliation,
            cardsCopied: result.cardsCopied,
          });
        },
      },
    );
  }

  function handleCopyCardSubmit(input: {
    boardId: string;
    listId: string;
    previousCardId: string | null;
    nextCardId: string | null;
    title: string;
    keepChecklists: boolean;
    keepLabels: boolean;
    keepMembers: boolean;
    keepAttachments: boolean;
    keepDates: boolean;
  }) {
    if (copyTarget === null) return;
    const sourceTitle = cardDisplayTitle(t, copyTarget.card.title);
    copyCard.mutate(
      {
        sourceBoardId: board.id,
        sourceCardId: copyTarget.card.id,
        sourceTitle,
        destinationBoardId: input.boardId,
        destinationListId: input.listId,
        previousCardId: input.previousCardId,
        nextCardId: input.nextCardId,
        title: input.title,
        keepChecklists: input.keepChecklists,
        keepLabels: input.keepLabels,
        keepMembers: input.keepMembers,
        keepAttachments: input.keepAttachments,
        keepDates: input.keepDates,
      },
      {
        onSuccess: (result) => {
          announce(t('boardFace.card.copied', { title: result.card.title }));
          setCopyResult(result.reconciliation);
          if (!hasReconciliationConsequences(result.reconciliation)) {
            setCopyTarget(null);
            setCopyResult(null);
          }
        },
      },
    );
  }

  /**
   * ⏭ **BLOCKED — the finding this chunk owes the parent, and the fifth
   * §12.4-shaped seam: each file is right on its own and the JOIN is owned by
   * nobody.**
   *
   * §6.5 put `openComposer` / `onOpenComposerChange` / `quickEditCardId` /
   * `onQuickEditChange` on `ListColumnProps`, and §6.7 put the state that
   * feeds them here. Between the two sits `SortableListColumn.tsx` — chunk
   * `C2`'s file, owned by **no C3 agent** — which declares none of the four
   * and forwards none of them. So `BoardFace` knows which composer is open and
   * cannot say so, `ListColumn` is ready to be told and never is, both files
   * compile, both files' tests pass, and **no card composer and no quick-edit
   * panel is reachable in the composed app.**
   *
   * The values are computed here, correctly, so that the parent's integration
   * is mechanical: add the four props to `SortableListColumnProps` (each
   * `| undefined`, for `exactOptionalPropertyTypes` — the same widening C2
   * needed for `dragHandle`), forward them to `ListColumn` as **plain props,
   * never a conditional JSX spread**, and pass these four into
   * `<SortableListColumn>` in the strip below.
   */
  const cardColumnPropsFor = (entry: BoardFaceList) => ({
    openComposer:
      openComposer?.kind === 'card' && openComposer.listId === entry.id
        ? openComposer.placement
        : null,
    onOpenComposerChange: (placement: 'bottom' | 'top' | null) => {
      handleCardComposerChange(entry.id, placement);
    },
    // Scoped to this column: `ListColumn` asks "is the card in quick edit one
    // of mine", and answering with a card in another column would make two
    // columns render a panel for the same card.
    quickEditCardId:
      quickEditCardId !== null && entry.cards.some((card) => card.id === quickEditCardId)
        ? quickEditCardId
        : null,
    onQuickEditChange: handleQuickEditChange,
  });

  const listIds = useMemo(() => lists.map((list) => list.id), [lists]);

  /**
   * The card the `DragOverlay` draws. Found in `lists` rather than in
   * `previewLists` because it is the same object in both — the preview moves
   * a card between arrays, it does not replace it.
   */
  const activeCard =
    activeCardId === null
      ? null
      : (lists.flatMap((entry) => entry.cards).find((card) => card.id === activeCardId) ??
        null);
  const hasChosenBackground = isChosenBoardBackground(board.background);
  const boardSurfaceStyle = boardBackgroundStyle(board.background, board.id);
  const currentBoardHref =
    typeof window === 'undefined'
      ? boardPath(board.shortLink, board.slug)
      : `${window.location.pathname}${window.location.search}${window.location.hash}`;
  const pastedCardLineCount =
    pasteCardsTarget === null ? 0 : parsePastedCardTitles(pasteCardsTarget.text).length;

  /**
   * The first list to the right of `index` the server has actually heard of.
   *
   * ⚠ Brief §5.2's rule, on the create route rather than the move route: a
   * placeholder id sent as `nextListId` is an id `POST /lists` has never seen.
   * `null` is the safe answer rather than a lossy one — with `previousListId`
   * given, §3.2 reads the pair as "immediately after it", which is where the
   * placeholder sits anyway.
   */
  function nextRealListId(index: number): string | null {
    return (
      lists.slice(index + 1).find((entry) => !isOptimisticListId(entry.id))?.id ?? null
    );
  }

  /**
   * `FR-BOARD-003`. `BoardTitle`'s `onRename` returns `void`, so `InlineEdit`
   * closes at once and the optimistic cache write is what the member sees —
   * the announcement waits for the server, because `lib/boards.ts` announces
   * the rollback itself if it never arrives and two contradictory sentences in
   * one live region is worse than a late one (§5.7).
   */
  function handleRename(title: string): void {
    rename.mutate(
      { title },
      {
        onSuccess: (updated) => {
          announce(t('boardFace.board.renamed', { title: updated.title }));
        },
      },
    );
  }

  function handleRunBoardAutomationButton(commandId: string): void {
    setBoardAutomationRunReceipt(null);
    runBoardAutomationButton.mutate(
      { boardId: board.id, commandId, workspaceId: board.workspaceId },
      {
        onSuccess: (receipt) => {
          setBoardAutomationRunReceipt(receipt);
        },
      },
    );
  }

  function closeTemplateDialog(): void {
    setTemplateDialogOpen(false);
    setTemplateCreateResult(null);
    createFromTemplate.reset();
  }

  function handleCreateFromTemplate(input: CreateFromTemplateDialogSubmit): void {
    if (templateBoard === null) return;
    createFromTemplate.mutate(
      {
        templateBoardId: templateBoard.id,
        templateTitle: templateBoard.title,
        workspaceId: input.workspaceId,
        visibility: input.visibility,
        title: input.title,
        keepCards: input.keepCards,
      },
      {
        onSuccess: (result) => {
          announce(
            t('boardFace.board.createdFromTemplate', { title: result.board.title }),
          );
          setTemplateCreateResult({
            cardsCopied: result.cardsCopied,
            path: boardPath(result.board.shortLink, result.board.slug),
          });
        },
      },
    );
  }

  function handleChangeTemplateVisibility(patch: TemplateVisibilityPatch): void {
    updateTemplateVisibility.mutate(patch, {
      onSuccess: (updated) => {
        /* ⚠ `VISIBILITY_LABELS`, not `.replace('_', ' ')`. This file had been
         * a FOURTH author of the visibility and category nouns, and its own
         * spelling disagreed with the control that had just been used — the
         * category branch below announced `project management` where
         * `TemplateVisibilityControls` says `Project management`. Both slots are
         * now filled from the tables that already own those words. Those tables
         * are still unrouted; they are the next vocabulary slice. */
        announce(
          t('boardFace.template.visibilitySet', {
            visibility: t(VISIBILITY_LABEL_KEYS[updated.visibility]),
          }),
        );
      },
    });
  }

  function handleChangeTemplateCategory(patch: TemplateCategoryPatch): void {
    updateTemplateCategory.mutate(patch, {
      onSuccess: (updated) => {
        announce(
          updated.templateCategory === null
            ? t('boardFace.template.categoryCleared')
            : t('boardFace.template.categorySet', {
                category: t(TEMPLATE_CATEGORY_LABEL_KEYS[updated.templateCategory]),
              }),
        );
      },
    });
  }

  /**
   * `FR-EXP-012` AC2. No `announce()`: `BoardClosedNotice` is `role="status"`
   * and its **disappearing** is this event's one channel (§5.7).
   *
   * ⚠ The focus move is not decoration. This button unmounts with the notice
   * it lives in, so without it focus falls to `<body>` and a keyboard member
   * loses their place on the board they just reopened — a hole this control
   * creates and therefore has to close. The menu trigger is the nearest
   * permanent control in the same header, and it always exists wherever this
   * button does (both are gated on `signedIn`).
   */
  function handleReopen(): void {
    reopen.mutate(undefined, {
      onSuccess: () => {
        menuTriggerRef.current?.focus();
      },
    });
  }

  function handlePowerUpHeaderAction(entry: PowerUpSurfaceRuntimeEntry): void {
    if (isCalendarPowerUpHeaderEntry(entry)) {
      setCalendarPowerUpOpen(true);
      announce(t('boardFace.powerUps.calendarOpened'));
      return;
    }
    announce(
      t('boardFace.powerUps.notInteractive', {
        powerUp: entry.powerUpName,
        label: entry.label,
      }),
    );
  }

  const face = (
    /*
      ⚠ A **ROW**, and the panel is its second child — the layout contract
      `BoardMenuPanel`'s docstring states and no test of either file can hold
      (see this file's header). `min-w-0` on the column below is the other half.
    */
    <BoardShortcutPreferencesProvider value={shortcutPreferenceState}>
      <div
        data-board-text={hasChosenBackground ? board.background.textTreatment : undefined}
        data-board-background={hasChosenBackground ? '' : undefined}
        data-board-background-type={
          hasChosenBackground ? board.background.type : undefined
        }
        data-reduced-motion={reducedMotion ? 'true' : undefined}
        data-board-shortcut-scope=""
        className={cn(
          'flex min-h-0 flex-1 text-on-board',
          !hasChosenBackground && 'bg-board',
        )}
        style={boardSurfaceStyle}
      >
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          {/*
        `relative` so the skip link's `focus:absolute` anchors here rather than
        to the shell's own positioned ancestor, where it would land on top of
        the shell's "Skip to content" link.
      */}
          <header className="relative flex shrink-0 flex-col gap-3 px-3 py-3">
            {/*
          `FR-EXP-022`'s edge case: "skip links must be provided to jump past
          the board header straight to the lists". This is NOT the shell's link
          — that one targets `#main`, which is this header. Visible on focus
          only, and the class string is `__root.tsx`'s proven one.
        */}
            <a
              href={
                effectiveActiveView === 'board' ? '#board-lists' : '#board-view-content'
              }
              className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded focus:bg-accent focus:px-3 focus:py-2 focus:text-accent-ink"
            >
              {effectiveActiveView === 'board'
                ? t('boardFace.skipLists')
                : t('boardFace.skipViewContent')}
            </a>

            {/*
          The title row. Desktop keeps the one-row density budget; phone widths
          let the title take a full row so board names do not get squeezed into
          one-word columns beside the controls.
        */}
            <div className="flex flex-wrap items-center gap-2">
              {/*
            The page's only `<h1>` (§7.3), now `BoardTitle` — renameable in
            place, `FR-BOARD-003`. The shell's `Header` renders no heading, so
            nothing competes with it; a list name is an `<h2>` and a card title
            is a link, not a heading.

            `min-w-0` because the `<h1>` is a flex child of this row now: a long
            board title would otherwise refuse to shrink and push the trigger
            off the right edge.

            ⚠ `canEdit` — `lib/board-face.ts`'s, meaning "the board is not
            closed" and nothing about roles (`D3-BRIEF.md` §5.5). Widening it
            to know about permissions is `FE-09.2`'s, chunk `D4`, and would
            silently change every C1/C2/C3 affordance that already reads it.

            ⚠ No `error` prop — `InlineEdit`'s error line is a `role="alert"`
            and `lib/boards.ts` already announces the rollback. One channel per
            event; see the header.
          */}
              <div className="min-w-0 flex-1 basis-full sm:basis-auto">
                <BoardTitle
                  title={board.title}
                  canEdit={workingCanEdit}
                  onRename={handleRename}
                />
              </div>

              {signedIn && (
                <Button
                  type="button"
                  size="sm"
                  variant={focusModeEnabled ? 'primary' : 'quiet'}
                  className={cn(!focusModeEnabled && 'text-on-board')}
                  aria-pressed={focusModeEnabled}
                  aria-label={
                    focusModeEnabled
                      ? t('boardFace.focus.exitLabel')
                      : t('boardFace.focus.enterLabel')
                  }
                  onClick={() => setFocusModeForCurrentMember(!focusModeEnabled)}
                  iconStart={
                    focusModeEnabled ? (
                      <Minimize2 aria-hidden="true" className="size-3.5" />
                    ) : (
                      <Maximize2 aria-hidden="true" className="size-3.5" />
                    )
                  }
                >
                  {focusModeEnabled
                    ? t('boardFace.focus.exit')
                    : t('boardFace.focus.enter')}
                </Button>
              )}

              {signedIn && showSecondaryBoardChrome && (
                <BoardViewSwitcher
                  activeView={effectiveActiveView}
                  accounting={viewAccounting}
                  busy={viewPreferenceBusy}
                  mobile={mobileBoardViewport}
                  pinnedViews={pinnedViews}
                  onViewChange={onViewChange ?? (() => undefined)}
                  onPinnedViewsChange={setPinnedViewsForCurrentMember}
                />
              )}

              {signedIn && showSecondaryBoardChrome && shownPinnedViews.length > 0 && (
                <div
                  aria-label={t('boardFace.pinned.region')}
                  className="flex min-w-0 items-center gap-1 overflow-x-auto"
                >
                  {shownPinnedViews.map((view, index) => (
                    <div
                      key={view}
                      draggable
                      aria-label={t('boardFace.pinned.shortcut', {
                        view: boardViewText(view),
                      })}
                      className={cn(
                        'flex shrink-0 cursor-grab items-center gap-0.5 rounded-card active:cursor-grabbing',
                        draggingPinnedView === view && 'opacity-60',
                      )}
                      onDragStart={(event) => handlePinnedViewDragStart(event, view)}
                      onDragOver={handlePinnedViewDragOver}
                      onDrop={(event) => handlePinnedViewDrop(event, view)}
                      onDragEnd={() => {
                        draggingPinnedViewRef.current = null;
                        setDraggingPinnedView(null);
                      }}
                    >
                      <Button
                        type="button"
                        size="sm"
                        variant={view === effectiveActiveView ? 'primary' : 'secondary'}
                        {...(view === effectiveActiveView
                          ? {}
                          : { className: 'bg-surface-raised/90' })}
                        onClick={() => onViewChange?.(view)}
                      >
                        {boardViewText(view)}
                      </Button>
                      <IconButton
                        label={t('boardFace.pinned.moveLeft', {
                          view: boardViewText(view),
                        })}
                        size="sm"
                        className="text-on-board"
                        disabled={index === 0}
                        onClick={() => movePinnedView(view, -1)}
                      >
                        <ArrowLeft aria-hidden="true" className="size-3.5" />
                      </IconButton>
                      <IconButton
                        label={t('boardFace.pinned.moveRight', {
                          view: boardViewText(view),
                        })}
                        size="sm"
                        className="text-on-board"
                        disabled={index === shownPinnedViews.length - 1}
                        onClick={() => movePinnedView(view, 1)}
                      >
                        <ArrowRight aria-hidden="true" className="size-3.5" />
                      </IconButton>
                    </div>
                  ))}
                </div>
              )}

              {signedIn && showSecondaryBoardChrome && !starState.isError && (
                <BoardStarButton
                  boardTitle={board.title}
                  starred={starState.data?.starred ?? false}
                  busy={starState.isPending || toggleBoardStar.isPending}
                  className="text-on-board hover:bg-surface-raised hover:text-ink"
                  onToggle={(starred) => {
                    toggleBoardStar.mutate({ board, starred });
                  }}
                />
              )}

              {signedIn && showSecondaryBoardChrome && workingCanEdit && (
                <div className="min-w-0 max-w-full flex-1 basis-full sm:basis-auto">
                  <AutomationButtonStrip
                    label={t('boardFace.automation.stripLabel')}
                    buttons={boardAutomationButtons.data?.buttons ?? []}
                    maxVisibleButtons={3}
                    loading={boardAutomationButtons.isPending}
                    disabled={!workingCanEdit}
                    disabledReason={t('boardFace.automation.disabledReason')}
                    runBusyId={
                      runBoardAutomationButton.isPending
                        ? (runBoardAutomationButton.variables?.commandId ?? null)
                        : null
                    }
                    receipt={boardAutomationRunReceipt}
                    error={
                      boardAutomationButtons.isError
                        ? boardAutomationButtons.error.message
                        : runBoardAutomationButton.error?.message
                    }
                    onRun={handleRunBoardAutomationButton}
                  />
                </div>
              )}

              {signedIn && showSecondaryBoardChrome && calendarPowerUpEntry !== null && (
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  className="text-on-board"
                  aria-label={t('boardFace.powerUps.openCalendar')}
                  iconStart={<CalendarDays aria-hidden="true" className="size-4" />}
                  onClick={() => handlePowerUpHeaderAction(calendarPowerUpEntry)}
                >
                  {t('boardFace.powerUps.calendar')}
                </Button>
              )}

              {signedIn &&
                showSecondaryBoardChrome &&
                genericBoardHeaderPowerUpButtons.map((entry) => (
                  <Button
                    key={entry.id}
                    type="button"
                    size="sm"
                    variant="quiet"
                    className="text-on-board"
                    aria-label={t('boardFace.powerUps.headerAction', {
                      powerUp: entry.powerUpName,
                      label: entry.label,
                    })}
                    onClick={() => handlePowerUpHeaderAction(entry)}
                  >
                    {entry.label}
                  </Button>
                ))}

              {showSecondaryBoardChrome && (
                <Button
                  size="sm"
                  variant="quiet"
                  className="text-on-board"
                  aria-pressed={showLabelNames}
                  onClick={() => setShowLabelNames((current) => !current)}
                >
                  {showLabelNames
                    ? t('boardFace.labels.hide')
                    : t('boardFace.labels.show')}
                </Button>
              )}

              {showSecondaryBoardChrome && (
                <BoardFilterPanel
                  boardTitle={board.title}
                  filter={filter}
                  members={members.data ?? []}
                  labels={offlineContents.labels}
                  customFields={customFieldDefinitions}
                  signedIn={signedIn}
                  open={filterOpen}
                  onOpenChange={setFilterOpen}
                  onFilterChange={onFilterChange ?? (() => undefined)}
                />
              )}

              {/*
            `FE-08.2`'s trigger, and the one permanent tab stop this chunk
            budgeted. In `BoardFace`'s own header deliberately: not in a column
            (three pixels of chrome there costs four cards a column) and not in
            the shell's `Header`, whose slots are `search`, `notifications`,
            `account` and `workspaceSwitcher` — a board control there would
            render on `/profile`.

            ⚠ Signed-in only (§2.5): the panel's members probe 401s for an
            anonymous caller even on a public board, and `main.tsx` redirects
            on any non-session 401.

            `text-on-board` overrides `quiet`'s `text-ink-muted`, which is a
            theme-surface ink with no measured contrast against
            `--color-board`; `--color-on-board` is the token measured against
            it in both themes (`styles.tokens.test.ts`).

            ⚠ It overrides only the **base** state: `tailwind-merge` groups by
            variant prefix, so `hover:text-ink` is a different group from
            `text-on-board` and both survive the merge — on-board ink at rest,
            `quiet`'s `bg-surface-raised` + `text-ink` pair on hover. The test
            below asserts the emitted class string rather than trusting that,
            because a `text-*` class dropped by a conflict that does not exist
            is this codebase's most expensive shipped defect.
          */}
              {signedIn && showSecondaryBoardChrome && (
                <div className="flex items-center gap-1">
                  <IconButton
                    label={t('boardFace.undo')}
                    size="sm"
                    className="text-on-board"
                    disabled={!undoRedo.canUndo}
                    busy={undoRedo.undoBusy}
                    onClick={undoRedo.undo}
                  >
                    <Undo2 aria-hidden="true" className="size-3.5" />
                  </IconButton>
                  <IconButton
                    label={t('boardFace.redo')}
                    size="sm"
                    className="text-on-board"
                    disabled={!undoRedo.canRedo}
                    busy={undoRedo.redoBusy}
                    onClick={undoRedo.redo}
                  >
                    <Redo2 aria-hidden="true" className="size-3.5" />
                  </IconButton>
                  {boardWatch.data?.watching === true ? (
                    <span
                      aria-label={t('boardFace.watching', { board: board.title })}
                      className="inline-flex size-7 items-center justify-center rounded-card text-on-board"
                    >
                      <Eye aria-hidden="true" className="size-3.5" />
                    </span>
                  ) : null}
                  <IconButton
                    ref={menuTriggerRef}
                    label={t('boardFace.menu')}
                    size="sm"
                    className="text-on-board"
                    onClick={() => {
                      setMenuOpen(true);
                    }}
                  >
                    <BoardMenuGlyph />
                  </IconButton>
                </div>
              )}
            </div>

            {showSecondaryBoardChrome && filterChips.length > 0 && (
              <div
                aria-label={t('boardFace.filters.activeRegion')}
                className="flex min-w-0 items-center gap-2 overflow-hidden rounded-card bg-panel/85 px-2 py-1.5 text-ink shadow-card"
              >
                <span className="shrink-0 text-xs font-semibold uppercase text-ink-muted">
                  {t('boardFace.filters.filtered')}
                </span>
                <span className="shrink-0 rounded-full bg-surface-raised px-2 py-0.5 text-xs font-medium text-ink">
                  {t(
                    'boardFace.filters.activeCount',
                    { count: filterChips.length },
                    filterChips.length,
                  )}
                </span>
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  className="shrink-0"
                  aria-label={t('boardFace.filters.clearAllLabel')}
                  onClick={() => onFilterChange?.(EMPTY_BOARD_FILTER)}
                >
                  {t('boardFace.filters.clearAll')}
                </Button>
                <div className="flex min-w-0 items-center gap-1.5 overflow-x-auto">
                  {filterChips.map((chip) => (
                    <FilterChip
                      key={chip.key}
                      facet={chip.facet}
                      value={
                        <span className="inline-block max-w-36 truncate">
                          {chip.value}
                        </span>
                      }
                      removeLabel={chip.removeLabel}
                      className="shrink-0 py-0.5 pl-2 text-xs"
                      onRemove={() => {
                        onFilterChange?.(removeBoardFilterChip(filter, chip));
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {showSecondaryBoardChrome && liveMessage !== null ? (
              <p
                role="status"
                className="w-fit rounded-card bg-panel/90 px-2 py-1 text-xs font-medium text-ink shadow-card"
              >
                {liveMessage}
              </p>
            ) : null}

            {showSecondaryBoardChrome && selectedCards.length > 0 ? (
              <div
                role="status"
                aria-label={t('boardFace.selection.region')}
                className="flex w-fit max-w-full flex-wrap items-center gap-2 rounded-card border border-border-strong bg-surface/95 px-2 py-1.5 text-sm text-ink shadow-card"
              >
                <span className="font-medium">
                  {t(
                    'boardFace.selection.count',
                    { count: selectedCards.length },
                    selectedCards.length,
                  )}
                </span>
                {selectionNotice !== null ? (
                  <span className="text-xs text-ink-muted">{selectionNotice}</span>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  onClick={() =>
                    handleOpenBulkCardActions(selectedCards[0]!, selectedCards[0]!.listId)
                  }
                >
                  {t('boardFace.selection.archive')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  onClick={() =>
                    handleOpenBulkCardActions(
                      selectedCards[0]!,
                      selectedCards[0]!.listId,
                      'move',
                    )
                  }
                >
                  {t('boardFace.selection.move')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  onClick={() =>
                    handleOpenBulkCardActions(
                      selectedCards[0]!,
                      selectedCards[0]!.listId,
                      'copy',
                    )
                  }
                >
                  {t('boardFace.selection.copy')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  disabled={selectedCards.length !== 2}
                  disabledReason={t('boardFace.selection.mergeDisabled')}
                  onClick={() =>
                    handleOpenBulkCardActions(
                      selectedCards[0]!,
                      selectedCards[0]!.listId,
                      'merge',
                    )
                  }
                >
                  {t('boardFace.selection.merge')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="quiet"
                  onClick={clearCardSelection}
                >
                  {t('boardFace.selection.clear')}
                </Button>
              </div>
            ) : null}

            {/*
              ⚠ NOT gated on `showSecondaryBoardChrome`, unlike every sibling
              in this stack, and the asymmetry is the point.

              It was gated, and the gate silenced `FR-EXP-020` AC4 outright:
              `showSecondaryBoardChrome` is `!focusModeEnabled`, so with focus
              mode on there was no `role="status"` node — and `recordSource`
              deliberately makes no `announce()` call (pinned in
              `undo.test.ts`, under `announce.ts`'s D11 one-event-one-channel
              rule) — so a member who archived in focus mode was told the card
              was archived and never told an Undo existed, in ANY region.
              Fan-out 14 photographed it:
              `docs/design/fe14-undo-journeys/focus-mode-no-undo-announcement-1440x900.png`.

              `FR-VIEW-011` authorises hiding "the workspace sidebar and
              secondary board chrome". The filter bar, the pinned views, the
              template banner and the board menu are chrome and keep their
              gate. A transient confirmation of an action the member took one
              second ago is not — `FR-EXP-013`'s Behaviour states it with no
              mode qualifier: "Destructive-but-recoverable actions ALSO surface
              a transient 'Undo' confirmation in a toast."

              So this is a gate removal, not a design: AC4 is discharged by the
              same mechanism already accepted on the non-focus path — `Notice`
              resolving `role = tone === 'bad' ? 'alert' : 'status'`, with the
              sentence and the `Undo` button inside one polite update.
            */}
            {undoRedo.toast !== null ? (
              <Notice
                tone="info"
                body={undoRedo.toast.message}
                className="max-w-sm"
                action={
                  <div className="flex gap-2">
                    <Button size="sm" variant="primary" onClick={undoRedo.undo}>
                      {undoRedo.toast.actionLabel}
                    </Button>
                    <Button size="sm" variant="quiet" onClick={undoRedo.dismissToast}>
                      {t('boardFace.undo.dismiss')}
                    </Button>
                  </div>
                }
              />
            ) : null}

            {showSecondaryBoardChrome && templateBoard !== null ? (
              <BoardTemplateBanner
                template={templateBoard}
                onCreate={() => {
                  setTemplateCreateResult(null);
                  setTemplateDialogOpen(true);
                }}
                canManageVisibility={canManageTemplate}
                visibilityDisabledReason={templateManagementDisabledReason}
                visibilityBusy={updateTemplateVisibility.isPending}
                categoryBusy={updateTemplateCategory.isPending}
                visibilityError={updateTemplateVisibility.error?.message}
                categoryError={updateTemplateCategory.error?.message}
                onChangeVisibility={signedIn ? handleChangeTemplateVisibility : undefined}
                onChangeCategory={signedIn ? handleChangeTemplateCategory : undefined}
              />
            ) : showSecondaryBoardChrome && templateLoading === true ? (
              <p
                role="status"
                className="w-fit rounded-card bg-panel/90 px-2 py-1 text-xs font-medium text-ink shadow-card"
              >
                {t('boardFace.template.checkingStatus')}
              </p>
            ) : null}

            {/*
              SA-9. NOT gated on `showSecondaryBoardChrome`: whose board this
              is, and on what authority the caller is reading it, is not
              chrome that focus mode may hide.
            */}
            {showPlatformAdminNotice && (
              <PlatformAdminBoardNotice
                onDismiss={() => setPlatformAdminNoticeDismissedFor(board.id)}
              />
            )}
            {/*
          `FR-EXP-012` AC2's read-only half, and — from `D3` — its Reopen.
          Reading `board.closed` here is NOT the pattern brief §3 forbids: that
          prohibition is on gating an *affordance* on `closed` instead of on
          `canEdit` — every affordance below is gated on `canEdit`, which is
          `lib/board-face.ts`'s single answer. This is a statement of fact about
          the board, and the fact is on the payload.

          ⚠ **An anonymous viewer gets the notice and no Reopen**, the same
          §2.5 reasoning as the trigger: `POST /boards/:id/reopen` requires a
          session, the members probe that would gate the control is disabled
          for them, and a control locked forever on "Checking what you can do
          on this board…" is worse than an absent one. `onReopen` undefined is
          exactly the notice C1 shipped.
        */}
            {board.closed && (
              <BoardClosedNotice
                boardTitle={board.title}
                onReopen={signedIn ? handleReopen : undefined}
                reopenBusy={reopen.isPending}
                reopenDisabledReason={signedIn ? reopenDisabledReason : undefined}
              />
            )}
            {offlineCachedAt !== undefined && (
              <OfflineBoardNotice mode="cached" cachedAt={offlineCachedAt} />
            )}
            {activeOfflineMoves.length + activeOfflineDescriptions.length > 0 && (
              <OfflineBoardNotice
                mode="pending-sync"
                pendingCount={
                  activeOfflineMoves.length + activeOfflineDescriptions.length
                }
                onRetry={() => void syncOfflineDrafts()}
              />
            )}
            {rejectedOfflineDescriptions.map((draft) => (
              <Notice
                key={draft.clientOperationId}
                tone="warn"
                title={t('boardFace.offline.rejectedTitle', {
                  title: draft.cardTitle,
                })}
                body={
                  <span>
                    {t('boardFace.offline.rejectedBody')}{' '}
                    <span className="block max-h-24 overflow-auto whitespace-pre-wrap break-words font-medium text-ink">
                      {draft.description}
                    </span>
                  </span>
                }
                action={
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      setRejectedOfflineDescriptions((current) =>
                        current.filter(
                          (entry) => entry.clientOperationId !== draft.clientOperationId,
                        ),
                      );
                    }}
                  >
                    {t('boardFace.offline.dismiss')}
                  </Button>
                }
              />
            ))}
          </header>

          {/*
        `FR-EXP-033` AC2 — the one-time first-run tip, and the confirmation
        that spends it.

        ⚠ **A sibling of the header and the strip, not a child of either**, and
        `shrink-0` so it takes its own height out of the flex column rather
        than compressing the strip's scroller.

        Its cost against `docs/notes/density-budget.md` is **zero cards**, and
        that is measured rather than argued: it renders on exactly one board in
        the product — the caller's own welcome board — and
        `onboarding/welcome-content.ts` seeds that board with 3 lists and 5
        cards against a budget of 24 at 1440×900. It cannot push a card off a
        screen that is holding a fifth of what it can hold, and on every other
        board it renders nothing at all.

        Nor does it compete with `B13`'s empty states, which `FR-EXP-033`
        forbids ("never two competing prompts"): `BoardEmpty` needs a board
        with no lists and `ListEmpty` needs a list with no cards, and the
        welcome board's seed content has neither. `Notice` already carries
        `role="status"`, so the confirmation is announced without a second live
        region on a surface `lib/announce.ts` says has exactly one.
      */}
          {showWelcomeTip && (
            <div className="shrink-0 px-3 pb-3">
              <Notice
                tone="info"
                title={
                  welcomeTipConfirmed
                    ? t('boardFace.welcomeTip.done')
                    : t('boardFace.welcomeTip.title')
                }
                body={welcomeTipConfirmed ? undefined : t('boardFace.welcomeTip.body')}
              />
            </div>
          )}

          {/*
        `DndContext` wraps the strip and nothing else — the empty board has no
        sortable on it. It renders no element of its own, so its accessibility
        nodes land here beside the `<ol>` rather than inside it (an `<ol>` may
        contain only `<li>`).

        ⚠ **ONE context, two kinds of drag** (§7.6) — a card leaving list A for
        list B has to see both, and a second context would also put a second
        `role="status" aria-live="assertive"` region on a surface
        `lib/announce.ts` says has exactly one.

        `collisionDetection` and `autoScroll` are `card-drag.ts`'s, passed ONCE
        and never swapped on drag-type state: `boardCollisionDetection` branches
        internally on `active.data.current.type`, and §12.8 verified against
        dnd-kit's source that a single `BOARD_AUTO_SCROLL` serves the list
        drag's X axis and the card drag's Y axis both.
      */}
          {showingMobileParityNotice ? (
            <section
              id="board-view-content"
              aria-label={t('boardFace.mobileLimitation.label', {
                view: boardViewText(effectiveActiveView),
              })}
              className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
            >
              <MobileParityNotice
                kind="view"
                title={t('boardFace.mobileLimitation.title', {
                  view: boardViewText(effectiveActiveView),
                })}
                body={t('boardFace.mobileLimitation.body', {
                  view: boardViewText(effectiveActiveView),
                })}
                href={currentBoardHref}
              />
            </section>
          ) : effectiveActiveView === 'board' ? (
            <DndContext
              sensors={sensors}
              collisionDetection={boardCollisionDetection}
              autoScroll={BOARD_AUTO_SCROLL}
              accessibility={{
                screenReaderInstructions: dragInstructions(t),
                announcements,
              }}
              onDragStart={handleDragStart}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              {/*
          ⚠ `items` is EVERY rendered list id in payload order, placeholders
          included. A placeholder is kept out of a drag by
          `useSortable({ disabled: true })`, never by being filtered out here:
          filtering it while still rendering it makes the strategy's rects
          disagree with the DOM, which is an off-by-one nothing turns red for.
        */}
              <SortableContext items={listIds} strategy={horizontalListSortingStrategy}>
                {/*
            The strip. `<ol>` because the order is the board's order and is
            meaningful (`FR-VIEW-007`), `aria-label` because a bare list of
            columns announces nothing.

            `tabIndex={-1}` for the same reason `__root.tsx` puts it on
            `<main>`: following a skip link moves the scroll position but
            leaves focus where it was, so the next Tab re-enters the header the
            member just asked to skip. An element is not focusable merely by
            being a fragment target.

            ⚠ Rendered even when there are no lists, so `#board-lists` always
            resolves; `shrink-0` then keeps the empty strip out of the layout
            while `BoardEmpty` below takes the space. `hidden` would remove the
            skip target from the accessibility tree and `sr-only` would
            absolutely position a scroll container, so neither is usable here.
          */}
                <ol
                  ref={stripRef}
                  id="board-lists"
                  aria-label={t('boardFace.lists')}
                  tabIndex={-1}
                  onFocus={handleFocusIn}
                  onClick={(event) => {
                    if (selectedCardIds.size === 0) return;
                    const target =
                      event.target instanceof HTMLElement ? event.target : null;
                    if (target?.closest('[data-card-id]') !== null) return;
                    if (
                      target?.closest(
                        'a,button,input,textarea,select,[role="button"]',
                      ) !== null
                    ) {
                      return;
                    }
                    clearCardSelection();
                  }}
                  className={cn(
                    /*
                     * `scroll-px-3` matches `px-3`, and it is what stops a column
                     * scrolled into view from sitting flush against the viewport
                     * edge with its own padding cut off — `revealFocusedElement`
                     * scrolls the focused CONTROL into view, so without scroll
                     * padding the open composer's field lands exactly on the edge
                     * and the panel around it is clipped. Found in the 1440×900
                     * screenshot pass; no role or name query can see it.
                     */
                    'flex items-start gap-3 px-3 pb-3 scroll-px-3',
                    'snap-x snap-mandatory md:snap-none',
                    hasLists
                      ? 'min-h-0 flex-1 overflow-x-auto overflow-y-hidden'
                      : 'shrink-0 overflow-hidden',
                  )}
                >
                  {/*
              ⚠ `previewLists`, not `lists` — this is the half of §12.9 that
              RENDERS. `previewLists` is `contents.lists` with at most one card
              spliced across two arrays while a cross-list drag is in flight;
              it is identical to `lists` (same reference) at every other moment.
              `handleCardDrop` reads `lists`, never this.
            */}
                  {previewLists.map((list, index) => {
                    // One call, four props — `cardColumnPropsFor` builds fresh
                    // closures, so calling it per prop would allocate four objects
                    // and hand `SortableListColumn` four unrelated identities.
                    const cardProps = cardColumnPropsFor(list);
                    return (
                      <Fragment key={list.id}>
                        {/*
                  `w-list`, `max-h-full min-h-0` and the sortable node itself
                  are all `SortableListColumn`'s now — the `<li>` moved there
                  because `useSortable` needs `setNodeRef` on it, and calling
                  the hook once per list from inside a `.map` here would be a
                  hook in a loop.
                */}
                        <SortableListColumn
                          list={list}
                          cardAgingMode={board.cardAgingMode}
                          /*
                           * `FR-CARD-025` AC5 / `FE-25.2` — the render half of
                           * the covers setting, and the reason it drew nothing
                           * for three fan-outs. `CardTile` and
                           * `SortableCardTile` have declared and honoured
                           * `coversEnabled` since the Card-Front Projection
                           * chunk; nothing ever SUPPLIED it, so `?? true`
                           * applied and a covers-off board drew every cover.
                           *
                           * ⚠ It reaches `CardTile` through THREE files, not
                           * one: `SortableListColumn` → `ListColumn` →
                           * `SortableCardTile`. `FE-25.2`'s own bullet says
                           * the fix is two lines and names only the two
                           * endpoints — the shape `SortableListColumn`'s
                           * docstring records C3 shipping, where both ends
                           * compile, both suites pass, and the prop arrives
                           * nowhere.
                           *
                           * `board.coversEnabled` is `z.boolean()` on
                           * `boardContentsBoardSchema` — required and
                           * non-nullable — so there is nothing to default here.
                           */
                          coversEnabled={board.coversEnabled}
                          showCardRef={board.showCardNumbersOnFront}
                          dangerousComments={board.dangerousComments}
                          canEdit={workingCanEdit}
                          canWatch={signedIn}
                          onInsertListAfter={
                            workingCanEdit ? handleInsertListAfter : undefined
                          }
                          // The gutter right of the LAST list is not a gap between two
                          // lists — the trailing composer is already there. Its menu
                          // still offers `Add a list to the right`, which is why this
                          // is a separate signal from the callback above.
                          hasGapAfter={index < lists.length - 1}
                          /*
                           * ⚠ Plain props, never a conditional spread — the C2 defect
                           * that JSX spread attributes bypass excess-property
                           * checking, so a spread compiles against props that were
                           * never declared. That is precisely how these four went
                           * missing: `SortableListColumn` declared none of them until
                           * the parent added them at integration, and until then no
                           * card composer and no quick-edit panel was reachable in
                           * the composed application, with both files' suites green.
                           */
                          openComposer={cardProps.openComposer}
                          onOpenComposerChange={cardProps.onOpenComposerChange}
                          quickEditCardId={cardProps.quickEditCardId}
                          onQuickEditChange={cardProps.onQuickEditChange}
                          showLabelNames={showLabelNames}
                          onOpenCard={
                            onCardOpen === undefined ? undefined : handleCardOpen
                          }
                          onOpenCardDates={
                            workingCanEdit && onCardOpen !== undefined
                              ? handleCardOpen
                              : undefined
                          }
                          onArchiveCard={handleArchiveCardFront}
                          onToggleCardCompleted={handleToggleCardCompleted}
                          onMoveCard={handleMoveCardFront}
                          onCopyCard={handleCopyCardFront}
                          onMoveList={handleMoveListOpen}
                          onCopyList={handleCopyListOpen}
                          onOpenListOperations={handleListOperationsOpen}
                          recordUndoSource={undoRedo.recordSource}
                          onPasteMany={handlePasteMany}
                          collapsed={collapsedListIds?.has(list.id) ?? false}
                          onCollapseChange={
                            onListCollapseChange === undefined
                              ? undefined
                              : handleListCollapseChange
                          }
                          highlightedCardId={highlightedCardId}
                          filterActive={filterActive}
                          selectedCardIds={selectedCardIds}
                          selectionActive={selectedCardIds.size > 0}
                          onSelectCardGesture={handleCardSelectionGesture}
                          onOpenBulkCardActions={(card, listId) =>
                            handleOpenBulkCardActions(card, listId)
                          }
                          onRemoveMirror={handleRemoveMirror}
                          onConvertLinkCard={handleConvertLinkCard}
                          pendingSyncCardIds={offlinePendingCardIds}
                          powerUpSurfaces={powerUpSurfaces}
                        />

                        {/*
                  `FR-BOARD-009` AC3: the composer opens *in the gap*, and the
                  list it creates "takes that position, shifting later lists
                  right". Mounted only while it is the open one, so the single
                  `openComposer` value is the whole of the mutual exclusion —
                  a second instance cannot exist to be open.
                */}
                        {openComposer?.kind === 'gap' &&
                          openComposer.afterListId === list.id && (
                            <li className="flex w-list shrink-0">
                              <ListComposer
                                boardId={board.id}
                                previousListId={list.id}
                                nextListId={nextRealListId(index)}
                                open
                                onOpenChange={handleGapOpenChange}
                                trigger="none"
                                className="w-full"
                              />
                            </li>
                          )}
                      </Fragment>
                    );
                  })}

                  {/*
              The add-a-list affordance, at the end of the strip so it is the
              last Tab stop rather than the first (§7.3's focus order: header,
              then each list left to right).

              ⚠ Gated on `lists.length > 0` as well as on `canEdit`:
              `BoardEmpty` takes `canEdit` and renders the empty-board
              affordance itself, so rendering one here too would put two
              buttons named `Add a list` on an empty editable board and every
              role query would find both.

              Both neighbours null — brief §3.2: on `POST /lists` that means
              "append at the far right", which is exactly this composer's job.
              (On the *position* route the same pair is a 422; the two routes
              read it differently and that asymmetry is the trap.)
            */}
                  {workingCanEdit && hasLists && (
                    /*
                     * ⚠ `w-list` ONLY WHILE OPEN — a defect the parent found in the
                     * 1440×900 screenshot pass, after the suite was green.
                     *
                     * A permanent 292px column here makes the reference four-list
                     * board 1532px wide against a 1440px viewport, so the closed
                     * `Add a list` trigger renders **clipped by the right edge** —
                     * a button sliced in half, which reads as a broken layout rather
                     * than as an affordance. Every role and accessible-name query
                     * passed throughout: the button was present, focusable and
                     * correctly named, and none of those can see that a third of it
                     * is off screen.
                     *
                     * Closed, it is `w-auto` and takes only the width it needs.
                     * Open, it becomes the full column the new list will occupy, so
                     * the composer is the size of the thing being composed.
                     */
                    <li
                      className={cn(
                        'flex shrink-0 snap-start',
                        openComposer?.kind === 'end' ? 'w-list' : 'w-auto',
                      )}
                    >
                      <ListComposer
                        boardId={board.id}
                        previousListId={null}
                        nextListId={null}
                        open={openComposer?.kind === 'end'}
                        onOpenChange={handleEndOpenChange}
                        trigger="button"
                        className="w-full"
                      />
                    </li>
                  )}
                </ol>
              </SortableContext>

              {/*
          The card under the pointer, for the whole of the drag — §6.7 item 8.

          ⚠ **`CardTile` itself, not an approximation of it.** The overlay is
          what a member looks at for the entire gesture, and a hand-built
          lookalike is a visual defect no role or name query can see. dnd-kit
          measures the dragged node and sizes this wrapper to it, so the tile
          needs no width of its own here.

          ⚠ **`aria-hidden` and `inert`, and both are load-bearing.** The
          original `<li>` stays mounted in the strip throughout, so without
          this the board would carry TWO elements with `role="link"` and the
          same accessible name for the duration of every drag — which breaks
          `getByRole('link', { name })` in seven spec files, in Playwright's
          strict mode and in RTL alike (both resolve roles through the
          accessibility tree, which `aria-hidden` removes this from). `inert`
          is the other half: an `<a href>` is focusable, and a focusable
          element inside an `aria-hidden` subtree is exactly the shape WCAG
          4.1.2 forbids. Nothing is lost by hiding it — the spoken half of the
          drag is dnd-kit's own live region (§7.6), which is why this file
          neither renders an `aria-live` node of its own nor calls
          `announce()` for drag progress.

          ⏭ FINDING for the parent, and it needs a one-line edit in a file no
          C3 agent owns — see the return. `SortableCardTile` renders the
          dragged card with no `isDragging` treatment at all. With a
          `DragOverlay` mounted, `@dnd-kit/sortable` sets
          `shouldDisplaceDragSource = false` and still hands the source item a
          strategy transform, so the card is drawn TWICE: once following the
          pointer here, once sitting fully opaque in the slot it would land in.
          `isDragging && 'opacity-0'` on that `<li>` is what turns the second
          one into the empty placeholder gap `FR-BOARD-025`'s behaviour
          sentence actually asks for.
        */}
              <DragOverlay>
                {activeCard === null ? null : (
                  /*
                   * ⚠ **TWO divs, and the outer one must carry no transform.** The
                   * tilt used to sit on this element and it broke every cross-list
                   * keyboard drag, silently — found at integration, traced out of
                   * dnd-kit's source rather than guessed:
                   *
                   *   1. `useDragOverlayMeasuring` measures `getMeasurableNode(node)`
                   *      (`core.esm.js`), which, when dnd-kit's own positioned
                   *      wrapper has exactly ONE element child, returns THAT CHILD —
                   *      i.e. whatever we render here, not dnd-kit's wrapper.
                   *   2. That measurement uses `getClientRect` **transform-aware**,
                   *      while every droppable is measured with
                   *      `getTransformAgnosticClientRect`. So a decorative transform
                   *      here puts the dragged card's rect in a different coordinate
                   *      system from every rect it is compared against. `rotate-2`
                   *      inflated a 276×35.56 card to 277.07×45.17 and moved its
                   *      `left` from 20 to 19.46 and its `top` up by 4.8.
                   *   3. `sortableKeyboardCoordinates` filters ArrowRight candidates
                   *      with `collisionRect.left < rect.left`. At 19.46 the card's
                   *      own SIBLING in the same column (left 20) passed that test
                   *      and won `closestCorners` against a column 300px away — so
                   *      ArrowRight moved DOWN inside list A and a card could never
                   *      be dragged to another list by keyboard at all. Nothing red.
                   *
                   * `ignoreTransform` is not a way out: `parseTransform` reads only
                   * `a` and `d` out of `matrix(a,b,c,d,tx,ty)` as scaleX/scaleY and
                   * drops `b`/`c`, so a pure rotation is not invertible by it.
                   *
                   * Nesting is: a transform on a DESCENDANT does not affect an
                   * ancestor's `getBoundingClientRect`, because transforms do not
                   * affect layout. The outer div measures as the card's true box;
                   * the inner one keeps the tilt.
                   */
                  <div aria-hidden="true" inert className="cursor-grabbing">
                    <div className="rotate-2">
                      <CardTile
                        card={activeCard}
                        cardAgingMode={board.cardAgingMode}
                        /*
                         * ⚠ THE FOURTH LINE `FE-25.2`'S TWO-LINE DIAGNOSIS
                         * MISSES, and the only one of the four that no unit
                         * test could have caught by accident: this `CardTile`
                         * is not reached through the column chain above, so
                         * threading the strip leaves it behind. Without it a
                         * card picked up on a covers-OFF board would GROW a
                         * cover the instant it left its column and lose it
                         * again on drop — visible for the duration of a
                         * gesture and at no other moment.
                         *
                         * It is pinned by `BoardFace.test.tsx`'s two overlay
                         * cases, which render the recorded `children` on their
                         * own — the only way this branch draws anything in
                         * jsdom.
                         */
                        coversEnabled={board.coversEnabled}
                        showCardRef={board.showCardNumbersOnFront}
                        dangerousComments={board.dangerousComments}
                        showLabelNames={showLabelNames}
                        powerUpSurfaces={powerUpSurfaces}
                      />
                    </div>
                  </div>
                )}
              </DragOverlay>
            </DndContext>
          ) : effectiveActiveView === 'table' ? (
            boardTable.isError ? (
              <section
                id="board-view-content"
                aria-label={t('boardFace.viewRegion', { view: boardViewText('table') })}
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
              >
                <Notice
                  tone="bad"
                  title={t('boardFace.viewError', { view: boardViewText('table') })}
                  body={boardTable.error.message}
                  action={
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => void boardTable.refetch()}
                    >
                      {t('boardFace.retry')}
                    </Button>
                  }
                />
              </section>
            ) : boardTable.data === undefined ? (
              <section
                id="board-view-content"
                aria-label={t('boardFace.viewRegion', { view: boardViewText('table') })}
                aria-busy="true"
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
              >
                <div className="rounded-card border border-border-strong bg-panel/90 p-4 text-card text-ink">
                  {t('boardFace.viewLoading', { view: boardViewText('table') })}
                </div>
              </section>
            ) : (
              <BoardTableView
                table={boardTable.data}
                allLists={offlineContents.lists}
                allLabels={offlineContents.labels}
                allMembers={members.data ?? []}
                customFields={boardTableCustomFields.data ?? []}
                readonly={!workingCanEdit}
                filterActive={filterActive}
                busy={
                  createCard.isPending ||
                  renameCard.isPending ||
                  moveCard.isPending ||
                  updateCardDueDate.isPending ||
                  applyCardLabel.isPending ||
                  removeCardLabel.isPending ||
                  addCardMember.isPending ||
                  removeCardMember.isPending
                }
                onOpenCard={(row) => {
                  handleCardOpen({
                    cardId: row.card.id,
                    path: viewCardPath(row.card),
                    stripScrollLeft: 0,
                    listScrollTop: 0,
                  });
                }}
                onRenameCard={handleTableRenameCard}
                onMoveCard={handleTableMoveCard}
                onSetDueDate={handleTableSetDueDate}
                onAddLabel={handleTableAddLabel}
                onRemoveLabel={handleTableRemoveLabel}
                onAddMember={handleTableAddMember}
                onRemoveMember={handleTableRemoveMember}
                onCreateCard={(intent) => void handleTableCreateCard(intent)}
              />
            )
          ) : effectiveActiveView === 'calendar' ? (
            boardCalendar.isError ? (
              <section
                id="board-view-content"
                aria-label={t('boardFace.viewRegion', {
                  view: boardViewText('calendar'),
                })}
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
              >
                <Notice
                  tone="bad"
                  title={t('boardFace.viewError', { view: boardViewText('calendar') })}
                  body={boardCalendar.error.message}
                  action={
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => void boardCalendar.refetch()}
                    >
                      {t('boardFace.retry')}
                    </Button>
                  }
                />
              </section>
            ) : boardCalendar.data === undefined ? (
              <section
                id="board-view-content"
                aria-label={t('boardFace.viewRegion', {
                  view: boardViewText('calendar'),
                })}
                aria-busy="true"
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
              >
                <div className="rounded-card border border-border-strong bg-panel/90 p-4 text-card text-ink">
                  {t('boardFace.viewLoading', { view: boardViewText('calendar') })}
                </div>
              </section>
            ) : (
              <BoardCalendarView
                calendar={boardCalendar.data}
                mode={calendarMode}
                anchorDate={calendarAnchor}
                weekStartsOn={calendarWeekStartsOn}
                readonly={!workingCanEdit}
                onModeChange={setCalendarMode}
                onCreateCard={({ dateKey }) => setCalendarCreateDate(dateKey)}
                onOpenCard={({ entry }) => {
                  handleCardOpen({
                    cardId: entry.card.id,
                    path: viewCardPath(entry.card),
                    stripScrollLeft: 0,
                    listScrollTop: 0,
                  });
                }}
                onReschedule={(intent) => void handleCalendarReschedule(intent)}
                onResize={(intent) => void handleCalendarResize(intent)}
              />
            )
          ) : effectiveActiveView === 'timeline' ? (
            boardTimeline.isError ? (
              <section
                id="board-view-content"
                aria-label={t('boardFace.viewRegion', {
                  view: boardViewText('timeline'),
                })}
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
              >
                <Notice
                  tone="bad"
                  title={t('boardFace.viewError', { view: boardViewText('timeline') })}
                  body={boardTimeline.error.message}
                  action={
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => void boardTimeline.refetch()}
                    >
                      {t('boardFace.retry')}
                    </Button>
                  }
                />
              </section>
            ) : boardTimeline.data === undefined ? (
              <section
                id="board-view-content"
                aria-label={t('boardFace.viewRegion', {
                  view: boardViewText('timeline'),
                })}
                aria-busy="true"
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
              >
                <div className="rounded-card border border-border-strong bg-panel/90 p-4 text-card text-ink">
                  {t('boardFace.viewLoading', { view: boardViewText('timeline') })}
                </div>
              </section>
            ) : (
              <BoardTimelineView
                timeline={boardTimeline.data}
                zoom={timelineZoom}
                anchorDate={timelineAnchor}
                readonly={!workingCanEdit}
                busy={
                  moveCard.isPending ||
                  updateCardStartDate.isPending ||
                  updateCardDueDate.isPending ||
                  applyCardLabel.isPending ||
                  removeCardLabel.isPending ||
                  addCardMember.isPending ||
                  removeCardMember.isPending
                }
                unscheduledCards={timelineUnscheduledCards}
                onZoomChange={setTimelineZoom}
                onAnchorDateChange={setTimelineAnchor}
                onGroupByChange={setTimelineGroupBy}
                onOpenCard={({ entry }) => {
                  handleCardOpen({
                    cardId: entry.card.id,
                    path: viewCardPath(entry.card),
                    stripScrollLeft: 0,
                    listScrollTop: 0,
                  });
                }}
                onMoveBar={(intent) => void handleTimelineMoveBar(intent)}
                onResizeBar={(intent) => void handleTimelineResizeBar(intent)}
                onMoveToLane={(intent) => void handleTimelineMoveToLane(intent)}
                onScheduleCard={(intent) => void handleTimelineScheduleCard(intent)}
              />
            )
          ) : effectiveActiveView === 'dashboard' ? (
            boardDashboard.isError ? (
              <section
                id="board-view-content"
                aria-label={t('boardFace.viewRegion', {
                  view: boardViewText('dashboard'),
                })}
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
              >
                <Notice
                  tone="bad"
                  title={t('boardFace.viewError', { view: boardViewText('dashboard') })}
                  body={boardDashboard.error.message}
                  action={
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => void boardDashboard.refetch()}
                    >
                      {t('boardFace.retry')}
                    </Button>
                  }
                />
              </section>
            ) : boardDashboard.data === undefined ? (
              <section
                id="board-view-content"
                aria-label={t('boardFace.viewRegion', {
                  view: boardViewText('dashboard'),
                })}
                aria-busy="true"
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
              >
                <div className="rounded-card border border-border-strong bg-panel/90 p-4 text-card text-ink">
                  {t('boardFace.viewLoading', { view: boardViewText('dashboard') })}
                </div>
              </section>
            ) : (
              <BoardDashboardView
                dashboard={boardDashboard.data}
                readonly={!canConfigureDashboard}
                busy={
                  createDashboardTile.isPending ||
                  updateDashboardTileFacet.isPending ||
                  updateDashboardTileChartType.isPending ||
                  deleteDashboardTile.isPending
                }
                onAddTile={handleAddDashboardTile}
                onChangeTileFacet={handleChangeDashboardTileFacet}
                onChangeTileChartType={handleChangeDashboardTileChartType}
                onRemoveTile={handleRemoveDashboardTile}
              />
            )
          ) : effectiveActiveView === 'map' ? (
            boardMap.isError ? (
              <section
                id="board-view-content"
                aria-label={t('boardFace.viewRegion', { view: boardViewText('map') })}
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
              >
                <Notice
                  tone="bad"
                  title={t('boardFace.viewError', { view: boardViewText('map') })}
                  body={boardMap.error.message}
                  action={
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      onClick={() => void boardMap.refetch()}
                    >
                      {t('boardFace.retry')}
                    </Button>
                  }
                />
              </section>
            ) : boardMap.data === undefined ? (
              <section
                id="board-view-content"
                aria-label={t('boardFace.viewRegion', { view: boardViewText('map') })}
                aria-busy="true"
                className="min-h-0 flex-1 overflow-y-auto px-3 pb-3"
              >
                <div className="rounded-card border border-border-strong bg-panel/90 p-4 text-card text-ink">
                  {t('boardFace.viewLoading', { view: boardViewText('map') })}
                </div>
              </section>
            ) : (
              <BoardMapView
                map={boardMap.data}
                allLists={offlineContents.lists}
                readonly={!workingCanEdit}
                busy={createCard.isPending || renameCard.isPending}
                onCreateCard={(intent) => void handleMapCreateCard(intent)}
                onOpenCard={handleMapOpenCard}
                onQuickEditCard={() => undefined}
                onRenameCard={handleMapRenameCard}
              />
            )
          ) : (
            <BoardViewShell
              view={effectiveActiveView}
              boardTitle={board.title}
              contents={offlineContents}
              onOpenCard={onCardOpen === undefined ? undefined : handleCardOpen}
              onReturnToBoard={() => onViewChange?.('board')}
            />
          )}

          {/*
        `FR-VIEW-001`'s edge case and `FR-EXP-034` AC1 — a board with no lists
        still renders the face. A sibling of the strip and not a child of it,
        because an `<ol>` may contain only `<li>`.

        `BoardEmpty` gates the action on `canEdit` itself, so the composer is
        handed over unconditionally and the gate stays in one place.
      */}
          {effectiveActiveView === 'board' && !hasLists && (
            <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-3">
              <BoardEmpty
                boardTitle={board.title}
                canEdit={workingCanEdit}
                action={
                  <ListComposer
                    boardId={board.id}
                    previousListId={null}
                    nextListId={null}
                    open={openComposer?.kind === 'end'}
                    onOpenChange={handleEndOpenChange}
                    trigger="button"
                  />
                }
              />
            </div>
          )}
        </div>

        {/*
        `FE-08.2`. **Beside the board, not under it** — the row-flex sibling of
        the column above, which is the first of the two mountings the panel's
        own layout contract permits and the one `FR-BOARD-005` AC1 describes
        literally ("the panel opens beside the board and the board face remains
        scrollable"). It carries its own `w-80`, `h-full`, `shrink-0`, scroll
        and ink and takes no `className`.

        Rendered unconditionally rather than behind `menuOpen`: it returns
        `null` while closed, and its own hooks (the members query, gated on
        `open`) must run on every render regardless. `menuOpen` cannot become
        true without the trigger, so an anonymous viewer never mounts anything
        of it.

        ⚠ `onDeleted` is the host's, and the host that matters is the route —
        the board this face is drawn from no longer exists once it fires. The
        optional call keeps this component renderable without a router; see the
        prop's docstring for why that is a reported seam and not a default.
      */}
        <BoardMenuPanel
          board={board}
          lists={offlineContents.lists}
          open={menuOpen}
          onOpenChange={setMenuOpen}
          mobile={mobileBoardViewport}
          returnFocusRef={menuTriggerRef}
          onDeleted={() => onDeleted?.()}
          onLeft={() => onLeft?.()}
          template={template}
          templateLoading={templateLoading}
          onChangeTemplateVisibility={handleChangeTemplateVisibility}
          onChangeTemplateCategory={handleChangeTemplateCategory}
          templateVisibilityBusy={updateTemplateVisibility.isPending}
          templateCategoryBusy={updateTemplateCategory.isPending}
          templateVisibilityError={updateTemplateVisibility.error?.message}
          templateCategoryError={updateTemplateCategory.error?.message}
          onOpenCard={handleCardOpen}
          initialSection={initialBoardMenuSection}
          onOpenShortcuts={handleOpenShortcuts}
          onCollapseAllLists={signedIn ? collapseAllLists : undefined}
          onExpandAllLists={signedIn ? expandAllLists : undefined}
          onPermanentListDelete={undoRedo.clearSource}
        />
        <CalendarCreateDialog
          open={calendarCreateDate !== null}
          dateKey={calendarCreateDate}
          lists={offlineContents.lists}
          busy={createCard.isPending || updateCardDueDate.isPending}
          onOpenChange={(open) => {
            if (!open) setCalendarCreateDate(null);
          }}
          onSubmit={handleCalendarCreate}
        />
        <Dialog
          open={calendarPowerUpOpen && calendarPowerUpEntry !== null}
          onOpenChange={setCalendarPowerUpOpen}
          size="lg"
          title={t('boardFace.powerUps.calendar')}
          description={t('boardFace.powerUps.calendarDescription')}
          closeControl={
            <IconButton
              label={t('boardFace.powerUps.closeCalendar')}
              size="sm"
              variant="quiet"
            >
              <X aria-hidden="true" className="size-4" />
            </IconButton>
          }
        >
          {calendarPowerUpCalendar.isError ? (
            <Notice
              tone="bad"
              title={t('boardFace.powerUps.calendarLoadError')}
              body={calendarPowerUpCalendar.error.message}
              action={
                <Button
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => void calendarPowerUpCalendar.refetch()}
                >
                  {t('boardFace.retry')}
                </Button>
              }
            />
          ) : calendarPowerUpCalendar.data === undefined ? (
            <section
              aria-label={t('boardFace.powerUps.calendar')}
              aria-busy="true"
              className="rounded-card border border-border-strong bg-panel p-4 text-card text-ink"
            >
              {t('boardFace.powerUps.calendarLoading')}
            </section>
          ) : (
            <BoardCalendarView
              calendar={calendarPowerUpCalendar.data}
              label={t('boardFace.powerUps.calendar')}
              className="h-[min(70vh,42rem)] rounded-card border border-border-strong px-0 pb-0"
              mode="month"
              anchorDate={calendarAnchor}
              weekStartsOn={calendarWeekStartsOn}
              readonly
              showModeSwitcher={false}
              onOpenCard={({ entry }) => {
                setCalendarPowerUpOpen(false);
                handleCardOpen({
                  cardId: entry.card.id,
                  path: viewCardPath(entry.card),
                  stripScrollLeft: 0,
                  listScrollTop: 0,
                });
              }}
            />
          )}
        </Dialog>
        <CreateFromTemplateDialog
          open={templateDialogOpen}
          templateTitle={templateBoard?.title ?? board.title}
          workspaces={templateWorkspaces.data ?? []}
          defaultWorkspaceId={board.workspaceId}
          loading={templateWorkspaces.isPending}
          busy={createFromTemplate.isPending}
          cardsCopied={templateCreateResult?.cardsCopied}
          createdBoardPath={templateCreateResult?.path}
          onCancel={closeTemplateDialog}
          onCreate={handleCreateFromTemplate}
        />
        {bulkCardActionTarget !== null ? (
          <BulkCardActionsDialog
            open
            initialMode={bulkCardActionTarget.mode}
            selectedCount={orderedSelectedCardIds.length}
            selectedCards={selectedCards.map((card) => ({
              id: card.id,
              title: card.title,
            }))}
            currentBoardId={board.id}
            boards={moveDestinations.boards}
            listsByBoardId={moveDestinations.listsByBoardId}
            cardsByListId={moveDestinations.cardsByListId}
            loading={moveDestinations.loading}
            busy={
              bulkArchiveCards.isPending ||
              bulkMoveCards.isPending ||
              bulkCopyCards.isPending ||
              mergeCards.isPending
            }
            error={
              bulkArchiveCards.error instanceof Error
                ? bulkArchiveCards.error.message
                : bulkMoveCards.error instanceof Error
                  ? bulkMoveCards.error.message
                  : bulkCopyCards.error instanceof Error
                    ? bulkCopyCards.error.message
                    : mergeCards.error instanceof Error
                      ? mergeCards.error.message
                      : null
            }
            onCancel={() => setBulkCardActionTarget(null)}
            onSubmit={handleBulkCardActionSubmit}
          />
        ) : null}
        {moveTarget !== null ? (
          <CrossBoardMoveDialog
            open
            mode={moveTarget.mode}
            subjectTitle={
              moveTarget.mode === 'card'
                ? cardDisplayTitle(t, moveTarget.card.title)
                : moveTarget.list.title
            }
            currentBoardId={board.id}
            currentListId={moveTarget.mode === 'card' ? moveTarget.listId : undefined}
            currentListTitle={
              moveTarget.mode === 'card'
                ? lists.find((list) => list.id === moveTarget.listId)?.title
                : undefined
            }
            currentCardId={moveTarget.mode === 'card' ? moveTarget.card.id : undefined}
            currentCards={
              moveTarget.mode === 'card'
                ? lists.find((list) => list.id === moveTarget.listId)?.cards
                : undefined
            }
            boards={moveDestinations.boards}
            listsByBoardId={moveDestinations.listsByBoardId}
            cardsByListId={moveDestinations.cardsByListId}
            loading={moveDestinations.loading}
            busy={
              moveTarget.mode === 'card'
                ? moveCard.isPending || moveCardToBoard.isPending
                : moveListToBoard.isPending
            }
            result={moveResult?.reconciliation}
            cardsMoved={moveResult?.cardsMoved}
            onCancel={handleMoveCancel}
            onPreviewCard={handleMoveCardPreview}
            onPreviewList={handleMoveListPreview}
            onMoveCard={handleMoveCardSubmit}
            onMoveList={handleMoveListSubmit}
          />
        ) : null}
        {copyTarget !== null ? (
          <CardCopyDialog
            open
            subjectTitle={cardDisplayTitle(t, copyTarget.card.title)}
            currentBoardId={board.id}
            currentListId={copyTarget.listId}
            currentCardId={copyTarget.card.id}
            boards={moveDestinations.boards}
            listsByBoardId={moveDestinations.listsByBoardId}
            cardsByListId={moveDestinations.cardsByListId}
            loading={moveDestinations.loading}
            busy={copyCard.isPending}
            result={copyResult}
            onCancel={handleCopyCancel}
            onCopy={handleCopyCardSubmit}
          />
        ) : null}
        {copyListTarget !== null ? (
          <ListCopyDialog
            open
            subjectTitle={copyListTarget.title}
            currentBoardId={board.id}
            currentListId={copyListTarget.id}
            boards={moveDestinations.boards}
            listsByBoardId={moveDestinations.listsByBoardId}
            loading={moveDestinations.loading}
            busy={copyList.isPending}
            result={copyListResult?.reconciliation}
            cardsCopied={copyListResult?.cardsCopied}
            onCancel={handleCopyListCancel}
            onCopy={handleCopyListSubmit}
          />
        ) : null}
        {listOperationsTarget !== null ? (
          <ListOperationsDialog
            open
            list={listOperationsTarget}
            lists={lists}
            busy={
              sortListCards.isPending ||
              archiveAllListCards.isPending ||
              moveAllListCards.isPending
            }
            onCancel={handleListOperationsCancel}
            onSort={handleListSort}
            onArchiveAll={handleArchiveAllCards}
            onMoveAll={handleMoveAllCards}
          />
        ) : null}
        {pasteCardsTarget !== null ? (
          <PasteCardsDialog
            open
            listTitle={pasteCardsTarget.listTitle}
            text={pasteCardsTarget.text}
            lineCount={pastedCardLineCount}
            busy={createCard.isPending || createBulkCards.isPending}
            error={pasteCardsTarget.error}
            onSingleCard={handlePasteSingleCard}
            onCardsPerLine={handlePasteCardsPerLine}
            onCancel={handlePasteCardsCancel}
          />
        ) : null}
      </div>
    </BoardShortcutPreferencesProvider>
  );

  /*
    Member initials (`docs/notes/member-initials.md`): ONE Radix tooltip provider
    for the whole face, so the card fronts' avatar tooltips share it instead of
    mounting one each, and moving along a row of circles opens the next name at
    once (`skipDelayDuration`). It renders no element, so the row layout
    contract above is untouched. Wrapped here rather than inside `face` so the
    face's JSX keeps its indentation.
  */
  return <TooltipProvider>{face}</TooltipProvider>;
}
